import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import './App.css';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './components/ui/select';
import { Textarea } from './components/ui/textarea';
import { Badge } from './components/ui/badge';
import { Calendar } from './components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './components/ui/popover';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './components/ui/dialog';
import { Checkbox } from './components/ui/checkbox';
import { CalendarIcon, Clock, Users, Building2, FileText, Plus, Edit, Trash2, LogOut, Eye, RotateCcw, Download, Receipt, ChevronUp, ChevronDown, CheckCircle2, AlertTriangle, Bell, Timer, GripVertical, Check, X, Play, Pause } from 'lucide-react';
import { format } from 'date-fns';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const API_BASE = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

function App() {
  // Comprehensive ResizeObserver error suppression
  useEffect(() => {
    // Override ResizeObserver to catch errors at source
    const OriginalResizeObserver = window.ResizeObserver;
    window.ResizeObserver = class ResizeObserver extends OriginalResizeObserver {
      constructor(callback) {
        const wrappedCallback = (entries, observer) => {
          try {
            callback(entries, observer);
          } catch (err) {
            if (!err.message.includes('ResizeObserver loop completed with undelivered notifications')) {
              throw err;
            }
          }
        };
        super(wrappedCallback);
      }
    };

    const originalError = console.error;
    const originalWarn = console.warn;
    
    console.error = (...args) => {
      if (typeof args[0] === 'string' && args[0].includes('ResizeObserver')) {
        return;
      }
      originalError.apply(console, args);
    };
    
    console.warn = (...args) => {
      if (typeof args[0] === 'string' && args[0].includes('ResizeObserver')) {
        return;
      }
      originalWarn.apply(console, args);
    };

    const handleError = (event) => {
      if (event.message?.includes('ResizeObserver') || 
          event.error?.message?.includes('ResizeObserver')) {
        event.stopImmediatePropagation();
        event.preventDefault();
        return false;
      }
    };

    const handleUnhandledRejection = (event) => {
      if (event.reason?.message?.includes('ResizeObserver')) {
        event.preventDefault();
        return false;
      }
    };

    window.addEventListener('error', handleError, true);
    window.addEventListener('unhandledrejection', handleUnhandledRejection, true);
    
    return () => {
      window.ResizeObserver = OriginalResizeObserver;
      console.error = originalError;
      console.warn = originalWarn;
      window.removeEventListener('error', handleError, true);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection, true);
    };
  }, []);

  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');

  // Data states
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [positions, setPositions] = useState([]);
  const [users, setUsers] = useState([]);
  const [timeEntries, setTimeEntries] = useState([]);
  const [lawFirmSettings, setLawFirmSettings] = useState(null);

  // Form states
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [timeEntryForm, setTimeEntryForm] = useState({
    client_id: '',
    project_id: '',
    date: new Date(),
    description: '',
    hours: 0,
    minutes: 0,
    is_chargeable: true
  });

  // Admin time entry form (for creating entries for other users)
  const [adminTimeEntryForm, setAdminTimeEntryForm] = useState({
    user_id: '',
    client_id: '',
    project_id: '',
    date: new Date(),
    description: '',
    hours: 0,
    minutes: 0,
    is_chargeable: true
  });

  const [filters, setFilters] = useState({
    client_id: 'all',
    user_id: 'all',
    project_id: 'all',
    chargeable: 'all',
    invoiced: 'all',
    start_date: '',
    end_date: ''
  });

  // Edit states
  const [editingClient, setEditingClient] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [editingPosition, setEditingPosition] = useState(null);
  const [editingTimeEntry, setEditingTimeEntry] = useState(null);
  const [editingProject, setEditingProject] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  // View states
  const [viewingClient, setViewingClient] = useState(null);
  const [viewingUser, setViewingUser] = useState(null);
  const [viewingPosition, setViewingPosition] = useState(null);
  const [viewingProject, setViewingProject] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);

  // Project management states
  const [projectForm, setProjectForm] = useState({
    client_id: '',
    name: '',
    settlement_method: 'hourly_rate',
    hourly_rate: '',
    hourly_rate_currency: 'USD',
    fixed_rate: '',
    fixed_rate_currency: 'USD',
    cap_amount: '',
    cap_currency: 'USD',
    cap_period: ''
  });
  const [showProjectForm, setShowProjectForm] = useState(false);
  const [selectedClientForProjects, setSelectedClientForProjects] = useState(null);

  // Delete confirmation states
  const [deletingItem, setDeletingItem] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  // Sorting and table states
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });
  const [dashboardSortConfig, setDashboardSortConfig] = useState({ key: 'date', direction: 'desc' });
  const [externalCostsSortConfig, setExternalCostsSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  const [invoicesSortConfig, setInvoicesSortConfig] = useState({ key: 'created_at', direction: 'desc' });

  // Report generation states
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [reportSource, setReportSource] = useState(''); // 'dashboard' or 'timesheet'

  // Invoice management states
  const [selectedEntries, setSelectedEntries] = useState(new Set());
  const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState(false);
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoices, setInvoices] = useState([]);
  const [selectedInvoiceDetails, setSelectedInvoiceDetails] = useState(null);
  const [isInvoiceDetailsModalOpen, setIsInvoiceDetailsModalOpen] = useState(false);

  // External Cost states
  const [externalCosts, setExternalCosts] = useState([]);
  const [externalCostForm, setExternalCostForm] = useState({
    client_id: '',
    project_id: 'none',
    description: '',
    amount: '',
    currency: 'USD'
  });
  const [editingExternalCost, setEditingExternalCost] = useState(null);
  const [selectedExternalCosts, setSelectedExternalCosts] = useState(new Set());
  const [isExternalCostModalOpen, setIsExternalCostModalOpen] = useState(false);

  // Currencies
  const currencies = ['USD', 'EUR', 'PLN'];

  // Credentials change state
  const [credentialsForm, setCredentialsForm] = useState({
    current_password: '',
    new_email: '',
    new_password: '',
    confirm_password: ''
  });
  const [isCredentialsModalOpen, setIsCredentialsModalOpen] = useState(false);

  // Task Management states (Lawyer only)
  const [tasks, setTasks] = useState([]);
  const [taskForm, setTaskForm] = useState({
    client_id: 'no-client',
    client_name: '',
    project_id: 'no-project',
    project_name: '',
    deadline: '',
    description: '',
    comments: ''
  });
  const [editingTask, setEditingTask] = useState(null);
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [isCompletionModalOpen, setIsCompletionModalOpen] = useState(false);
  const [isSuspendModalOpen, setIsSuspendModalOpen] = useState(false);
  const [completingTask, setCompletingTask] = useState(null);
  const [suspendingTask, setSuspendingTask] = useState(null);
  const [completionComments, setCompletionComments] = useState('');
  const [suspendComments, setSuspendComments] = useState('');
  const [suspendDueDate, setSuspendDueDate] = useState('');
  const [taskFilters, setTaskFilters] = useState({
    client_id: 'all',
    project_id: 'all',
    deadline: 'all',
    is_completed: 'all',
    start_date: '',
    end_date: '',
    sort_by: 'priority',
    sort_order: 'desc'
  });
  const [showCompletedTasks, setShowCompletedTasks] = useState(false);
  const [suspendedReminders, setSuspendedReminders] = useState([]);
  const [showReminders, setShowReminders] = useState(false);
  const [taskSortConfig, setTaskSortConfig] = useState({ key: 'priority_order', direction: 'desc' });
  const [draggedTask, setDraggedTask] = useState(null);

  const [clientForm, setClientForm] = useState({
    business_name: '',
    address: {
      street: '',
      building: '',
      apartment: '',
      postal_code: '',
      city: '',
      country: ''
    },
    vat_number: '',
    cooperation_type: 'permanent',
    settlement_method: 'hourly_rate',
    hourly_rate: '',
    hourly_rate_currency: 'USD',
    fixed_rate: '',
    fixed_rate_currency: 'USD',
    cap_amount: '',
    cap_currency: 'USD',
    cap_period: ''
  });

  const [userForm, setUserForm] = useState({
    email: '',
    password: '',
    name: '',
    surname: '',
    role: 'lawyer',
    position_id: '',
    address: {
      street: '',
      building: '',
      apartment: '',
      postal_code: '',
      city: '',
      country: ''
    }
  });

  const [positionForm, setPositionForm] = useState({
    name: '',
    description: '',
    hourly_rate: '',
    currency: 'USD'
  });

  const [lawFirmForm, setLawFirmForm] = useState({
    business_name: '',
    vat_number: '',
    address: {
      street: '',
      building: '',
      apartment: '',
      postal_code: '',
      city: '',
      country: ''
    }
  });

  // Setup axios defaults
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUserInfo();
    }
  }, [token]);

  // Fetch data when user is loaded
  useEffect(() => {
    if (user) {
      fetchInitialData();
    }
  }, [user]);

  // Fetch tasks when lawyer switches to tasks tab
  useEffect(() => {
    if (user?.role === 'lawyer' && activeTab === 'tasks') {
      fetchTasks();
    }
  }, [activeTab, user]);

  // Initialize law firm form with existing settings
  useEffect(() => {
    if (lawFirmSettings) {
      setLawFirmForm({
        business_name: lawFirmSettings.business_name || '',
        vat_number: lawFirmSettings.vat_number || '',
        address: {
          street: lawFirmSettings.address?.street || '',
          building: lawFirmSettings.address?.building || '',
          apartment: lawFirmSettings.address?.apartment || '',
          postal_code: lawFirmSettings.address?.postal_code || '',
          city: lawFirmSettings.address?.city || '',
          country: lawFirmSettings.address?.country || ''
        }
      });
    }
  }, [lawFirmSettings]);

  // Debounced handlers to prevent ResizeObserver issues
  const debouncedSetExternalCostForm = useCallback((newForm) => {
    setTimeout(() => {
      setExternalCostForm(newForm);
    }, 0);
  }, []);

  // Memoized options to prevent ResizeObserver errors
  const clientOptions = useMemo(() => 
    clients
      .filter(client => client.is_active !== false)
      .map(client => ({ id: client.id, name: client.business_name })),
    [clients]
  );

  const projectOptionsForClient = useCallback((clientId) => 
    projects.filter(project => project.client_id === clientId && project.is_active !== false),
    [projects]
  );

  const filteredProjectOptions = useMemo(() => {
    // Only show projects when a specific client is selected, not when "All Clients" is selected
    if (filters.client_id === 'all' || !filters.client_id) {
      return [];
    }
    
    return projects.filter(project => 
      project.client_id === filters.client_id && project.is_active !== false
    );
  }, [projects, filters.client_id]);

  const fetchUserInfo = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user info:', error);
      handleLogout();
    }
  };

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const [clientsRes, projectsRes, positionsRes, usersRes, timeEntriesRes, settingsRes, externalCostsRes, tasksRes] = await Promise.allSettled([
        axios.get(`${API_BASE}/api/clients`),
        axios.get(`${API_BASE}/api/projects`),
        axios.get(`${API_BASE}/api/positions`),
        user?.role !== 'lawyer' ? axios.get(`${API_BASE}/api/users`) : Promise.resolve({ data: [] }),
        axios.get(`${API_BASE}/api/time-entries`),
        axios.get(`${API_BASE}/api/law-firm-settings`),
        user?.role !== 'lawyer' ? axios.get(`${API_BASE}/api/external-costs`) : Promise.resolve({ data: [] }),
        user?.role === 'lawyer' ? axios.get(`${API_BASE}/api/tasks`) : Promise.resolve({ data: [] })
      ]);

      if (clientsRes.status === 'fulfilled') setClients(clientsRes.value.data);
      if (projectsRes.status === 'fulfilled') setProjects(projectsRes.value.data);
      if (positionsRes.status === 'fulfilled') setPositions(positionsRes.value.data);
      if (usersRes.status === 'fulfilled') setUsers(usersRes.value.data);
      if (timeEntriesRes.status === 'fulfilled') setTimeEntries(timeEntriesRes.value.data);
      if (settingsRes.status === 'fulfilled') setLawFirmSettings(settingsRes.value.data);
      if (externalCostsRes.status === 'fulfilled') setExternalCosts(externalCostsRes.value.data);
      if (tasksRes.status === 'fulfilled') setTasks(tasksRes.value.data);
      
      // Fetch role-specific data
      if (user?.role !== 'lawyer') {
        fetchInvoices();
      } else if (user?.role === 'lawyer') {
        await fetchSuspendedReminders();
        
        // Check if user wants reminders from previous session
        const shouldShowReminders = localStorage.getItem('showRemindersOnNextLogin');
        if (shouldShowReminders === 'true') {
          localStorage.removeItem('showRemindersOnNextLogin');
          // Wait a bit to ensure reminders are loaded
          setTimeout(() => {
            if (suspendedReminders.length > 0) {
              setShowReminders(true);
            }
          }, 1000);
        }
      }
    } catch (error) {
      console.error('Failed to fetch initial data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const response = await axios.post(`${API_BASE}/api/auth/login`, loginForm);
      const { access_token, user: userData } = response.data;
      
      setToken(access_token);
      setUser(userData);
      localStorage.setItem('token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    } catch (error) {
      alert('Login failed: ' + (error.response?.data?.detail || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setActiveTab('dashboard');
  };

  const handleTimeEntrySubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await axios.post(`${API_BASE}/api/time-entries`, timeEntryForm);
      
      // Reset form
      setTimeEntryForm({
        client_id: '',
        project_id: '',
        date: new Date(),
        description: '',
        hours: 0,
        minutes: 0,
        is_chargeable: true
      });
      
      // Refresh time entries
      const response = await axios.get(`${API_BASE}/api/time-entries`);
      setTimeEntries(response.data);
      
      alert('Time entry added successfully!');
    } catch (error) {
      alert('Failed to add time entry: ' + (error.response?.data?.detail || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleAdminTimeEntrySubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      
      // Extract user_id from form and create entry data
      const { user_id, ...entryData } = adminTimeEntryForm;
      await axios.post(`${API_BASE}/api/time-entries/admin?user_id=${user_id}`, entryData);
      
      // Reset form
      setAdminTimeEntryForm({
        user_id: '',
        client_id: '',
        project_id: '',
        date: new Date(),
        description: '',
        hours: 0,
        minutes: 0,
        is_chargeable: true
      });
      
      // Refresh time entries
      const response = await axios.get(`${API_BASE}/api/time-entries`);
      setTimeEntries(response.data);
      
      alert('Time entry added successfully for the selected user!');
    } catch (error) {
      alert('Failed to add time entry: ' + (error.response?.data?.detail || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleClientSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      
      // Clean form data - convert empty strings to null for numeric fields
      const cleanedClientForm = {
        ...clientForm,
        hourly_rate: clientForm.hourly_rate ? parseFloat(clientForm.hourly_rate) : null,
        fixed_rate: clientForm.fixed_rate ? parseFloat(clientForm.fixed_rate) : null,
        cap_amount: clientForm.cap_amount ? parseFloat(clientForm.cap_amount) : null,
        cap_period: clientForm.cap_period || null
      };
      
      await axios.post(`${API_BASE}/api/clients`, cleanedClientForm);
      
      // Reset form
      setClientForm({
        business_name: '',
        address: {
          street: '',
          building: '',
          apartment: '',
          postal_code: '',
          city: '',
          country: ''
        },
        vat_number: '',
        cooperation_type: 'permanent',
        settlement_method: 'hourly_rate',
        hourly_rate: '',
        hourly_rate_currency: 'USD',
        fixed_rate: '',
        fixed_rate_currency: 'USD',
        cap_amount: '',
        cap_currency: 'USD',
        cap_period: ''
      });
      
      // Refresh clients
      const response = await axios.get(`${API_BASE}/api/clients`);
      setClients(response.data);
      
      alert('Client added successfully!');
    } catch (error) {
      console.error('Client creation error:', error);
      let errorMessage = 'Unknown error';
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      alert('Failed to add client: ' + errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleUserSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await axios.post(`${API_BASE}/api/users`, userForm);
      
      // Reset form
      setUserForm({
        email: '',
        password: '',
        name: '',
        surname: '',
        role: 'lawyer',
        position_id: '',
        address: {
          street: '',
          building: '',
          apartment: '',
          postal_code: '',
          city: '',
          country: ''
        }
      });
      
      // Refresh users
      const response = await axios.get(`${API_BASE}/api/users`);
      setUsers(response.data);
      
      alert('User added successfully!');
    } catch (error) {
      alert('Failed to add user: ' + (error.response?.data?.detail || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handlePositionSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await axios.post(`${API_BASE}/api/positions`, positionForm);
      
      // Reset form
      setPositionForm({
        name: '',
        description: '',
        hourly_rate: '',
        currency: 'USD'
      });
      
      // Refresh positions
      const response = await axios.get(`${API_BASE}/api/positions`);
      setPositions(response.data);
      
      alert('Position added successfully!');
    } catch (error) {
      alert('Failed to add position: ' + (error.response?.data?.detail || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleLawFirmSettingsSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const response = await axios.post(`${API_BASE}/api/law-firm-settings`, lawFirmForm);
      setLawFirmSettings(response.data);
      alert('Law firm settings saved successfully!');
    } catch (error) {
      console.error('Law firm settings error:', error);
      let errorMessage = 'Unknown error';
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      alert('Failed to save settings: ' + errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCredentialsSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      
      // Validate required fields
      if (!credentialsForm.current_password) {
        alert('Current password is required');
        return;
      }
      
      // Check if at least one field is being updated
      if (!credentialsForm.new_email && !credentialsForm.new_password) {
        alert('Please provide either a new email or new password');
        return;
      }
      
      // Validate password confirmation if new password is provided
      if (credentialsForm.new_password && credentialsForm.new_password !== credentialsForm.confirm_password) {
        alert('New password and confirmation password do not match');
        return;
      }
      
      // Prepare the request data
      const requestData = {
        current_password: credentialsForm.current_password
      };
      
      if (credentialsForm.new_email) {
        requestData.new_email = credentialsForm.new_email;
      }
      
      if (credentialsForm.new_password) {
        requestData.new_password = credentialsForm.new_password;
      }
      
      const response = await axios.put(`${API_BASE}/api/auth/change-credentials`, requestData);
      
      // Update user state with new data
      setUser(response.data);
      
      // Reset form
      setCredentialsForm({
        current_password: '',
        new_email: '',
        new_password: '',
        confirm_password: ''
      });
      
      alert('Credentials updated successfully!');
    } catch (error) {
      console.error('Credentials update error:', error);
      let errorMessage = 'Unknown error';
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      alert('Failed to update credentials: ' + errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getRoleDisplayName = (role) => {
    const roles = {
      'super_admin': 'Super Administrator',
      'admin_full': 'Full Administrator',
      'admin_modify': 'Modify Administrator',
      'admin_passive': 'Passive Administrator',
      'lawyer': 'Lawyer'
    };
    return roles[role] || role;
  };

  const getClientProjects = (clientId) => {
    return projects.filter(p => p.client_id === clientId && p.is_active !== false);
  };

  const formatTime = (hours, minutes) => {
    return `${hours}h ${minutes}m`;
  };

  const getTotalHours = (entries) => {
    const total = entries.reduce((sum, entry) => {
      return sum + entry.hours + (entry.minutes / 60);
    }, 0);
    return total.toFixed(2);
  };

  // Task Management Functions
  const fetchTasks = async () => {
    if (user?.role !== 'lawyer') return;
    
    try {
      const params = new URLSearchParams();
      
      if (taskFilters.client_id && taskFilters.client_id !== 'all') {
        params.append('client_id', taskFilters.client_id);
      }
      
      if (taskFilters.project_id && taskFilters.project_id !== 'all') {
        params.append('project_id', taskFilters.project_id);
      }
      
      if (taskFilters.start_date) {
        params.append('deadline_start', taskFilters.start_date);
      }
      
      if (taskFilters.end_date) {
        params.append('deadline_end', taskFilters.end_date);
      }
      
      if (taskFilters.is_completed && taskFilters.is_completed !== 'all') {
        if (taskFilters.is_completed === 'suspended') {
          params.append('is_suspended', 'true');
        } else {
          params.append('is_completed', taskFilters.is_completed === 'true');
        }
      }
      
      if (taskFilters.sort_by && taskFilters.sort_by !== 'priority') {
        params.append('sort_by', taskFilters.sort_by);
      }
      
      if (taskFilters.sort_order && taskFilters.sort_order !== 'desc') {
        params.append('sort_order', taskFilters.sort_order);
      }
      
      const response = await axios.get(`${API_BASE}/api/tasks?${params.toString()}`);
      setTasks(response.data);
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
    }
  };

  const handleTaskSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      
      const taskData = {
        client_id: taskForm.client_id === 'no-client' ? null : taskForm.client_id,
        client_name: taskForm.client_name || (taskForm.client_id !== 'no-client' ? 
          clients.find(c => c.id === taskForm.client_id)?.business_name : ''),
        project_id: taskForm.project_id === 'no-project' ? null : taskForm.project_id,
        project_name: taskForm.project_name || (taskForm.project_id !== 'no-project' ? 
          projects.find(p => p.id === taskForm.project_id)?.name : ''),
        deadline: taskForm.deadline,
        description: taskForm.description,
        comments: taskForm.comments
      };
      
      if (editingTask) {
        await axios.put(`${API_BASE}/api/tasks/${editingTask.id}`, taskData);
        setEditingTask(null);
        alert('Task updated successfully!');
      } else {
        await axios.post(`${API_BASE}/api/tasks`, taskData);
        alert('Task added successfully!');
      }
      
      // Reset form
      setTaskForm({
        client_id: 'no-client',
        client_name: '',
        project_id: 'no-project',
        project_name: '',
        deadline: '',
        description: '',
        comments: ''
      });
      setIsTaskModalOpen(false);
      await fetchTasks();
    } catch (error) {
      console.error('Task submission error:', error);
      alert('Failed to save task: ' + (error.response?.data?.detail || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleEditTask = (task) => {
    setEditingTask(task);
    setTaskForm({
      client_id: task.client_id || 'no-client',
      client_name: task.client_name,
      project_id: task.project_id || 'no-project',
      project_name: task.project_name || '',
      deadline: new Date(task.deadline).toISOString().split('T')[0],
      description: task.description,
      comments: task.comments || ''
    });
    setIsTaskModalOpen(true);
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Are you sure you want to delete this task?')) return;
    
    try {
      setLoading(true);
      await axios.delete(`${API_BASE}/api/tasks/${taskId}`);
      await fetchTasks();
      alert('Task deleted successfully!');
    } catch (error) {
      console.error('Delete task error:', error);
      alert('Failed to delete task: ' + (error.response?.data?.detail || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteTask = (task) => {
    setCompletingTask(task);
    setCompletionComments('');
    setIsCompletionModalOpen(true);
  };

  const handleSuspendTask = (task) => {
    setSuspendingTask(task);
    setSuspendComments('');
    setSuspendDueDate('');
    setIsSuspendModalOpen(true);
  };

  const handleSuspendSubmit = async () => {
    try {
      setLoading(true);
      
      const suspendData = {
        suspend_comments: suspendComments
      };
      
      if (suspendDueDate) {
        suspendData.suspend_due_date = new Date(suspendDueDate).toISOString();
      }
      
      await axios.put(`${API_BASE}/api/tasks/${suspendingTask.id}/suspend`, suspendData);
      setIsSuspendModalOpen(false);
      setSuspendingTask(null);
      setSuspendComments('');
      setSuspendDueDate('');
      await fetchTasks();
      alert('Task suspended successfully!');
    } catch (error) {
      console.error('Suspend task error:', error);
      alert('Failed to suspend task: ' + (error.response?.data?.detail || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleResumeTask = async (taskId) => {
    try {
      setLoading(true);
      await axios.put(`${API_BASE}/api/tasks/${taskId}/resume`);
      await fetchTasks();
      alert('Task resumed successfully!');
    } catch (error) {
      console.error('Resume task error:', error);
      alert('Failed to resume task: ' + (error.response?.data?.detail || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const fetchSuspendedReminders = async () => {
    if (user?.role !== 'lawyer') return;
    
    try {
      const response = await axios.get(`${API_BASE}/api/tasks/suspended-reminders`);
      setSuspendedReminders(response.data);
      
      // Show reminders if there are any
      if (response.data.length > 0) {
        setShowReminders(true);
      }
    } catch (error) {
      console.error('Failed to fetch suspended reminders:', error);
    }
  };

  const dismissReminders = () => {
    setShowReminders(false);
  };

  const formatDateWithDay = (dateString) => {
    try {
      const date = new Date(dateString);
      const options = { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric',
        weekday: 'long'
      };
      const formatted = date.toLocaleDateString('en-US', options);
      // Rearrange to show format like "Aug 20 2025 - Wednesday"
      const parts = formatted.split(', ');
      return `${parts[1]} - ${parts[0]}`;
    } catch (error) {
      return dateString;
    }
  };

  const remindLater = () => {
    setShowReminders(false);
    // Set a flag to show reminders again on next login
    localStorage.setItem('showRemindersOnNextLogin', 'true');
  };

  const handleTaskCompletion = async () => {
    try {
      setLoading(true);
      await axios.put(`${API_BASE}/api/tasks/${completingTask.id}/complete`, {
        completion_comments: completionComments
      });
      
      setIsCompletionModalOpen(false);
      setCompletingTask(null);
      setCompletionComments('');
      await fetchTasks();
      alert('Task marked as completed!');
    } catch (error) {
      console.error('Complete task error:', error);
      alert('Failed to complete task: ' + (error.response?.data?.detail || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleUncompleteTask = async (taskId) => {
    try {
      setLoading(true);
      await axios.put(`${API_BASE}/api/tasks/${taskId}/uncomplete`);
      await fetchTasks();
      alert('Task marked as incomplete!');
    } catch (error) {
      console.error('Uncomplete task error:', error);
      alert('Failed to uncomplete task: ' + (error.response?.data?.detail || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleTaskReorder = async (newOrder, draggedTask, targetTask) => {
    try {
      // Check if dropping before a task with earlier due date
      if (targetTask) {
        const draggedDeadline = new Date(draggedTask.deadline);
        const targetDeadline = new Date(targetTask.deadline);
        
        if (draggedDeadline > targetDeadline) {
          const shouldChangeDueDate = window.confirm(
            `You're moving a task (due ${formatDateWithDay(draggedTask.deadline)}) before a task due earlier (${formatDateWithDay(targetTask.deadline)}). Would you like to change the due date of the moved task?`
          );
          
          if (shouldChangeDueDate) {
            const newDueDate = prompt(
              `Enter new due date for "${draggedTask.description}" (current: ${formatDateWithDay(draggedTask.deadline)}):`,
              targetTask.deadline.split('T')[0]
            );
            
            if (newDueDate) {
              try {
                // Update the task with new due date
                await axios.put(`${API_BASE}/api/tasks/${draggedTask.id}`, {
                  deadline: new Date(newDueDate).toISOString()
                });
              } catch (error) {
                console.error('Failed to update task due date:', error);
                alert('Failed to update due date, but task order will still be changed.');
              }
            }
          }
        }
      }
      
      const taskIds = newOrder.map(task => task.id);
      await axios.post(`${API_BASE}/api/tasks/reorder`, { task_ids: taskIds });
      await fetchTasks();
    } catch (error) {
      console.error('Failed to reorder tasks:', error);
      alert('Failed to reorder tasks: ' + (error.response?.data?.detail || 'Unknown error'));
    }
  };

  const getTaskIcon = (task) => {
    if (task.is_completed) {
      return <CheckCircle2 className="h-4 w-4 text-green-600" />;
    }
    
    const today = new Date();
    const deadline = new Date(task.deadline);
    const diffTime = deadline.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return <AlertTriangle className="h-4 w-4 text-red-600" />;
    } else if (diffDays === 0) {
      return <Clock className="h-4 w-4 text-orange-600" />;
    } else if (diffDays <= 2) {
      return <Timer className="h-4 w-4 text-yellow-600" />;
    }
    
    return <Bell className="h-4 w-4 text-blue-600" />;
  };

  const getTaskStatusText = (task) => {
    if (task.is_completed) {
      return 'Completed';
    }
    
    const today = new Date();
    const deadline = new Date(task.deadline);
    const diffTime = deadline.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return 'Overdue';
    } else if (diffDays === 0) {
      return 'Due Today';
    } else if (diffDays <= 2) {
      return 'Due Soon';
    }
    
    return 'Upcoming';
  };

  const getFilteredTasks = () => {
    let filteredTasks = [...tasks];
    
    // Filter by completion status
    if (!showCompletedTasks) {
      filteredTasks = filteredTasks.filter(task => !task.is_completed);
    }
    
    // Apply additional filters
    if (taskFilters.client_id && taskFilters.client_id !== 'all') {
      filteredTasks = filteredTasks.filter(task => task.client_id === taskFilters.client_id);
    }
    
    if (taskFilters.project_id && taskFilters.project_id !== 'all') {
      filteredTasks = filteredTasks.filter(task => task.project_id === taskFilters.project_id);
    }
    
    if (taskFilters.is_completed && taskFilters.is_completed !== 'all') {
      if (taskFilters.is_completed === 'suspended') {
        filteredTasks = filteredTasks.filter(task => task.is_suspended === true);
      } else {
        const isCompleted = taskFilters.is_completed === 'true';
        filteredTasks = filteredTasks.filter(task => task.is_completed === isCompleted);
      }
    }
    
    // Sort tasks by current sort settings or default priority
    if (taskFilters.sort_by !== 'priority') {
      // Custom sorting - backend handles this, but we can also sort locally for immediate feedback
      return filteredTasks; // Return as-is since backend should handle sorting
    } else {
      // Default sorting by priority and deadline
      filteredTasks.sort((a, b) => {
        // Completed tasks always go to bottom
        if (a.is_completed && !b.is_completed) return 1;
        if (!a.is_completed && b.is_completed) return -1;
        
        // Suspended tasks go after active tasks but before completed
        if (a.is_suspended && !b.is_suspended && !b.is_completed) return 1;
        if (!a.is_suspended && b.is_suspended && !a.is_completed) return -1;
        
        // Within the same status group, sort by priority order (descending)
        if (a.priority_order !== b.priority_order) {
          return b.priority_order - a.priority_order;
        }
        
        // If same priority, sort by deadline
        return new Date(a.deadline) - new Date(b.deadline);
      });
    }
    
    return filteredTasks;
  };

  const generateTasksReport = () => {
    const filteredTasks = getFilteredTasks();
    
    // Prepare data for Excel
    const reportData = filteredTasks.map(task => ({
      'Client Name': task.client_name,
      'Project Name': task.project_name || 'No Project',
      'Description': task.description,
      'Deadline': formatDateWithDay(task.deadline),
      'Status': task.is_completed ? 'Completed' : task.is_suspended ? 'Suspended' : getTaskStatusText(task),
      'Comments': task.comments || '',
      'Completion Comments': task.completion_comments || '',
      'Suspend Comments': task.suspend_comments || '',
      'Created': format(new Date(task.created_at), 'MMM dd, yyyy'),
      'Completed': task.completed_at ? format(new Date(task.completed_at), 'MMM dd, yyyy') : '',
      'Suspended': task.suspended_at ? format(new Date(task.suspended_at), 'MMM dd, yyyy') : '',
      'Resume By': task.suspend_due_date ? formatDateWithDay(task.suspend_due_date) : ''
    }));
    
    // Create workbook
    const ws = XLSX.utils.json_to_sheet(reportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Tasks Report');
    
    // Generate filename
    const today = format(new Date(), 'yyyy-MM-dd');
    const filename = `Tasks_Report_${today}.xlsx`;
    
    // Save file
    XLSX.writeFile(wb, filename);
  };

  // Project Management Functions
  const handleProjectSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      
      // Prepare project data with proper null handling
      const projectData = {
        ...projectForm,
        hourly_rate: projectForm.hourly_rate ? parseFloat(projectForm.hourly_rate) : null,
        fixed_rate: projectForm.fixed_rate ? parseFloat(projectForm.fixed_rate) : null,
        cap_amount: projectForm.cap_amount ? parseFloat(projectForm.cap_amount) : null,
        cap_period: projectForm.cap_period || null
      };
      
      console.log('Sending project data:', projectData);
      await axios.post(`${API_BASE}/api/projects`, projectData);
      
      // Refresh projects
      const response = await axios.get(`${API_BASE}/api/projects`);
      setProjects(response.data);
      
      // Reset form
      setProjectForm({
        client_id: '',
        name: '',
        settlement_method: 'hourly_rate',
        hourly_rate: '',
        hourly_rate_currency: 'USD',
        fixed_rate: '',
        fixed_rate_currency: 'USD',
        cap_amount: '',
        cap_currency: 'USD',
        cap_period: ''
      });
      setShowProjectForm(false);
      alert('Project created successfully!');
    } catch (error) {
      console.error('Project creation error:', error);
      console.error('Error response:', error.response);
      let errorMessage = 'Unknown error';
      if (error.response?.data?.detail) {
        if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        } else if (Array.isArray(error.response.data.detail)) {
          errorMessage = error.response.data.detail.map(err => typeof err === 'string' ? err : err.msg || JSON.stringify(err)).join(', ');
        } else {
          errorMessage = JSON.stringify(error.response.data.detail);
        }
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      alert('Error creating project: ' + errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleEditProject = (project) => {
    setEditingProject({
      ...project,
      hourly_rate: project.hourly_rate || '',
      fixed_rate: project.fixed_rate || '',
      cap_amount: project.cap_amount || ''
    });
    setIsEditModalOpen(true);
  };

  const handleUpdateProject = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      
      // Prepare project data with proper null handling
      const projectData = {
        ...editingProject,
        hourly_rate: editingProject.hourly_rate ? parseFloat(editingProject.hourly_rate) : null,
        fixed_rate: editingProject.fixed_rate ? parseFloat(editingProject.fixed_rate) : null,
        cap_amount: editingProject.cap_amount ? parseFloat(editingProject.cap_amount) : null,
        cap_period: editingProject.cap_period || null
      };
      
      await axios.put(`${API_BASE}/api/projects/${editingProject.id}`, projectData);
      
      // Refresh projects
      const response = await axios.get(`${API_BASE}/api/projects`);
      setProjects(response.data);
      
      setIsEditModalOpen(false);
      setEditingProject(null);
      alert('Project updated successfully!');
    } catch (error) {
      console.error('Project update error:', error);
      let errorMessage = 'Unknown error';
      if (error.response?.data?.detail) {
        if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        } else if (Array.isArray(error.response.data.detail)) {
          errorMessage = error.response.data.detail.map(err => typeof err === 'string' ? err : err.msg || JSON.stringify(err)).join(', ');
        } else {
          errorMessage = JSON.stringify(error.response.data.detail);
        }
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      alert('Error updating project: ' + errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleViewProject = (project) => {
    setViewingProject(project);
    setIsViewModalOpen(true);
  };

  const handleAddProjectToClient = (client) => {
    setProjectForm({ ...projectForm, client_id: client.id });
    setSelectedClientForProjects(client);
    setShowProjectForm(true);
  };

  const fetchFilteredTimeEntries = async () => {
    try {
      const params = {};
      if (filters.client_id && filters.client_id !== 'all') params.client_id = filters.client_id;
      if (filters.user_id && filters.user_id !== 'all' && user?.role !== 'lawyer') params.user_id = filters.user_id;
      if (filters.project_id && filters.project_id !== 'all') params.project_id = filters.project_id;
      if (filters.chargeable && filters.chargeable !== 'all') params.is_chargeable = filters.chargeable === 'true';
      if (filters.invoiced && filters.invoiced !== 'all') params.is_invoiced = filters.invoiced === 'true';
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;
      
      const response = await axios.get(`${API_BASE}/api/time-entries`, { params });
      setTimeEntries(response.data);
    } catch (error) {
      console.error('Failed to filter time entries:', error);
    }
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      client_id: 'all',
      user_id: 'all',
      project_id: 'all',
      chargeable: 'all',
      invoiced: 'all',
      start_date: '',
      end_date: ''
    });
    setTimeEntries([]);
  };

  // Sorting functions
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const handleDashboardSort = (key) => {
    let direction = 'asc';
    if (dashboardSortConfig.key === key && dashboardSortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setDashboardSortConfig({ key, direction });
  };

  // Cost calculation function
  const calculateEntryCost = (entry) => {
    const totalHours = entry.hours + (entry.minutes / 60);
    
    // First check if there's a specific project with rates
    const project = projects.find(p => p.id === entry.project_id);
    if (project) {
      if (project.hourly_rate) {
        return totalHours * project.hourly_rate;
      }
      if (project.fixed_rate) {
        return project.fixed_rate; // Fixed rate per project
      }
    }
    
    // Fall back to client rates
    const client = clients.find(c => c.id === entry.client_id);
    if (client && client.hourly_rate) {
      return totalHours * client.hourly_rate;
    }
    
    return 0;
  };

  // Get sorted and filtered time entries
  const getSortedTimeEntries = (entries, config) => {
    let filteredEntries = [...entries];
    
    // Apply project filter
    if (filters.project_id && filters.project_id !== 'all') {
      filteredEntries = filteredEntries.filter(entry => entry.project_id === filters.project_id);
    }
    
    // Apply chargeable filter
    if (filters.chargeable && filters.chargeable !== 'all') {
      const isChargeable = filters.chargeable === 'true';
      filteredEntries = filteredEntries.filter(entry => entry.is_chargeable === isChargeable);
    }
    
    // Sort entries
    filteredEntries.sort((a, b) => {
      let aValue, bValue;
      
      switch (config.key) {
        case 'date':
          aValue = new Date(a.date);
          bValue = new Date(b.date);
          break;
        case 'client':
          const aClient = clients.find(c => c.id === a.client_id);
          const bClient = clients.find(c => c.id === b.client_id);
          aValue = aClient?.business_name || '';
          bValue = bClient?.business_name || '';
          break;
        case 'project':
          const aProject = projects.find(p => p.id === a.project_id);
          const bProject = projects.find(p => p.id === b.project_id);
          aValue = aProject?.name || 'No Project';
          bValue = bProject?.name || 'No Project';
          break;
        case 'description':
          aValue = a.description;
          bValue = b.description;
          break;
        case 'time':
          aValue = a.hours + (a.minutes / 60);
          bValue = b.hours + (b.minutes / 60);
          break;
        case 'chargeable':
          aValue = a.is_chargeable ? 1 : 0;
          bValue = b.is_chargeable ? 1 : 0;
          break;
        case 'cost':
          aValue = calculateEntryCost(a);
          bValue = calculateEntryCost(b);
          break;
        default:
          return 0;
      }
      
      if (aValue < bValue) return config.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return config.direction === 'asc' ? 1 : -1;
      return 0;
    });
    
    return filteredEntries;
  };

  // Get sorted external costs
  const getSortedExternalCosts = (costs, config) => {
    let sortedCosts = [...costs];
    
    sortedCosts.sort((a, b) => {
      let aValue, bValue;
      
      switch (config.key) {
        case 'created_at':
          aValue = new Date(a.created_at);
          bValue = new Date(b.created_at);
          break;
        case 'client':
          const aClient = clients.find(c => c.id === a.client_id);
          const bClient = clients.find(c => c.id === b.client_id);
          aValue = aClient?.business_name || '';
          bValue = bClient?.business_name || '';
          break;
        case 'project':
          const aProject = a.project_id ? projects.find(p => p.id === a.project_id) : null;
          const bProject = b.project_id ? projects.find(p => p.id === b.project_id) : null;
          aValue = aProject?.name || 'No Project';
          bValue = bProject?.name || 'No Project';
          break;
        case 'description':
          aValue = a.description;
          bValue = b.description;
          break;
        case 'amount':
          aValue = a.amount;
          bValue = b.amount;
          break;
        case 'currency':
          aValue = a.currency;
          bValue = b.currency;
          break;
        case 'status':
          aValue = a.invoice_number ? 'Invoiced' : 'Not Invoiced';
          bValue = b.invoice_number ? 'Invoiced' : 'Not Invoiced';
          break;
        default:
          return 0;
      }
      
      if (aValue < bValue) return config.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return config.direction === 'asc' ? 1 : -1;
      return 0;
    });
    
    return sortedCosts;
  };

  // Get sorted invoices
  const getSortedInvoices = (invoiceList, config) => {
    let sortedInvoices = [...invoiceList];
    
    sortedInvoices.sort((a, b) => {
      let aValue, bValue;
      
      switch (config.key) {
        case 'invoice_number':
          aValue = a.invoice_number;
          bValue = b.invoice_number;
          break;
        case 'created_at':
          aValue = new Date(a.created_at);
          bValue = new Date(b.created_at);
          break;
        case 'total_entries':
          aValue = a.total_entries || 0;
          bValue = b.total_entries || 0;
          break;
        case 'total_hours':
          aValue = a.total_hours || 0;
          bValue = b.total_hours || 0;
          break;
        case 'total_costs':
          aValue = a.total_costs || 0;
          bValue = b.total_costs || 0;
          break;
        case 'total_amount':
          aValue = a.total_amount || 0;
          bValue = b.total_amount || 0;
          break;
        default:
          return 0;
      }
      
      if (aValue < bValue) return config.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return config.direction === 'asc' ? 1 : -1;
      return 0;
    });
    
    return sortedInvoices;
  };

  // Handle sorting for external costs and invoices
  const handleExternalCostsSort = (key) => {
    const direction = externalCostsSortConfig.key === key && externalCostsSortConfig.direction === 'desc' ? 'asc' : 'desc';
    setExternalCostsSortConfig({ key, direction });
  };

  const handleInvoicesSort = (key) => {
    const direction = invoicesSortConfig.key === key && invoicesSortConfig.direction === 'desc' ? 'asc' : 'desc';
    setInvoicesSortConfig({ key, direction });
  };

  // Invoice management functions
  const handleEntrySelection = (entryId, isSelected) => {
    const newSelected = new Set(selectedEntries);
    if (isSelected) {
      newSelected.add(entryId);
    } else {
      newSelected.delete(entryId);
    }
    setSelectedEntries(newSelected);
  };

  const handleSelectAll = (entries, isSelected) => {
    if (isSelected) {
      const unInvoicedEntries = entries.filter(entry => !entry.invoice_number);
      const newSelected = new Set([...selectedEntries, ...unInvoicedEntries.map(entry => entry.id)]);
      setSelectedEntries(newSelected);
    } else {
      setSelectedEntries(new Set());
    }
  };

  const handleMarkAsInvoiced = () => {
    if (selectedEntries.size === 0) {
      alert('Please select entries to mark as invoiced');
      return;
    }
    setInvoiceNumber('');
    setIsInvoiceModalOpen(true);
  };

  const handleInvoiceSubmit = async () => {
    const normalizedInvoiceNumber = normalizeInvoiceNumber(invoiceNumber);
    
    if (!isValidInvoiceNumber(normalizedInvoiceNumber)) {
      alert('Please enter a valid invoice number');
      return;
    }

    if (selectedEntries.size === 0) {
      alert('Please select time entries to mark as invoiced');
      return;
    }

    try {
      const response = await axios.post(`${API_BASE}/api/time-entries/mark-invoiced`, {
        time_entry_ids: Array.from(selectedEntries),
        invoice_number: normalizedInvoiceNumber
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      // Clear selection
      setSelectedEntries(new Set());
      setInvoiceNumber('');
      
      // Refresh time entries
      await fetchFilteredTimeEntries();
      
      // Refresh invoices
      await fetchInvoices();

      alert(`Successfully marked ${response.data.entries_count} time entries as invoiced with invoice number: ${response.data.invoice_number}`);
      setIsInvoiceModalOpen(false);
    } catch (error) {
      console.error('Failed to mark time entries as invoiced:', error);
      alert(error.response?.data?.detail || 'Failed to mark time entries as invoiced');
    }
  };

  const fetchInvoices = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/invoices`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setInvoices(response.data);
    } catch (error) {
      console.error('Failed to fetch invoices:', error);
    }
  };

  const handleInvoiceDetails = async (invoiceNumber) => {
    try {
      let response;
      
      // First try the path parameter approach
      try {
        const encodedInvoiceNumber = encodeURIComponent(invoiceNumber);
        response = await axios.get(`${API_BASE}/api/invoices/${encodedInvoiceNumber}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
      } catch (pathError) {
        // If path parameter approach fails, try query parameter approach
        console.log('Path parameter approach failed for invoice details, trying query parameter approach...');
        response = await axios.get(`${API_BASE}/api/invoices/details?invoice_number=${encodeURIComponent(invoiceNumber)}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
      }
      
      console.log('Invoice details response:', response.data);
      setSelectedInvoiceDetails(response.data);
      setIsInvoiceDetailsModalOpen(true);
    } catch (error) {
      console.error('Failed to fetch invoice details:', error);
      console.error('Error response:', error.response?.data);
      alert(`Failed to fetch invoice details: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleGenerateInvoiceReport = async (invoiceNumber) => {
    try {
      let response;
      
      // First try the path parameter approach
      try {
        const encodedInvoiceNumber = encodeURIComponent(invoiceNumber);
        response = await axios.get(`${API_BASE}/api/invoices/${encodedInvoiceNumber}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
      } catch (pathError) {
        // If path parameter approach fails, try query parameter approach
        console.log('Path parameter approach failed for invoice report, trying query parameter approach...');
        response = await axios.get(`${API_BASE}/api/invoices/details?invoice_number=${encodeURIComponent(invoiceNumber)}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
      }
      
      const invoiceData = response.data;
      console.log('Invoice report data:', invoiceData);
      
      // Temporarily store invoice data for report generation
      setSelectedInvoiceDetails(invoiceData);
      setReportSource(`invoice-${invoiceNumber}`);
      setIsReportModalOpen(true);
    } catch (error) {
      console.error('Failed to generate invoice report:', error);
      console.error('Error response:', error.response?.data);
      alert(`Failed to generate invoice report: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleRedoInvoice = async (invoiceNumber) => {
    if (!confirm(`Are you sure you want to redo invoice ${invoiceNumber}? This will remove the invoice information from all associated time entries and external costs and make them available for invoicing again.`)) {
      return;
    }

    try {
      let response;
      
      // First try the path parameter approach (works for most invoice numbers)
      try {
        const encodedInvoiceNumber = encodeURIComponent(invoiceNumber);
        response = await axios.delete(`${API_BASE}/api/invoices/${encodedInvoiceNumber}/redo`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
      } catch (pathError) {
        // If path parameter approach fails (likely due to special characters), 
        // try the query parameter approach as fallback
        console.log('Path parameter approach failed, trying query parameter approach...');
        response = await axios.delete(`${API_BASE}/api/invoices/redo?invoice_number=${encodeURIComponent(invoiceNumber)}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
      }
      
      const { entries_count, costs_count, total_count } = response.data;
      let message = `Successfully redone invoice ${invoiceNumber}.`;
      if (entries_count > 0 && costs_count > 0) {
        message += ` ${entries_count} time entries and ${costs_count} external costs are now available for invoicing again.`;
      } else if (entries_count > 0) {
        message += ` ${entries_count} time entries are now available for invoicing again.`;
      } else if (costs_count > 0) {
        message += ` ${costs_count} external costs are now available for invoicing again.`;
      }
      
      alert(message);
      
      // Refresh data
      await fetchInvoices();
      await fetchFilteredTimeEntries();
      if (user?.role !== 'lawyer') {
        await fetchFilteredExternalCosts();
      }
    } catch (error) {
      console.error('Failed to redo invoice:', error);
      console.error('Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: error.config?.url
      });
      alert(`Failed to redo invoice: ${error.response?.data?.detail || error.message}`);
    }
  };

  // External Cost Management Functions
  const handleExternalCostSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      
      const costData = {
        ...externalCostForm,
        project_id: externalCostForm.project_id === 'none' ? '' : externalCostForm.project_id,
        amount: parseFloat(externalCostForm.amount)
      };
      
      if (editingExternalCost) {
        await axios.put(`${API_BASE}/api/external-costs/${editingExternalCost.id}`, costData);
        alert('External cost updated successfully!');
      } else {
        await axios.post(`${API_BASE}/api/external-costs`, costData);
        alert('External cost added successfully!');
      }
      
      // Reset form
      setExternalCostForm({
        client_id: '',
        project_id: 'none',
        description: '',
        amount: '',
        currency: 'USD'
      });
      setEditingExternalCost(null);
      
      // Refresh external costs
      const response = await axios.get(`${API_BASE}/api/external-costs`);
      setExternalCosts(response.data);
      
    } catch (error) {
      alert('Failed to save external cost: ' + (error.response?.data?.detail || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  // Helper function to validate and normalize invoice numbers
  const normalizeInvoiceNumber = (invoiceNumber) => {
    // Invoice numbers support: letters, numbers, slashes, dashes, dots, spaces, and other common symbols
    // No restrictions - all characters are allowed for maximum flexibility
    return invoiceNumber?.trim() || '';
  };

  const isValidInvoiceNumber = (invoiceNumber) => {
    const normalized = normalizeInvoiceNumber(invoiceNumber);
    return normalized.length > 0; // Only require non-empty after trimming
  };

  const handleEditExternalCost = (cost) => {
    setEditingExternalCost(cost);
    setExternalCostForm({
      client_id: cost.client_id,
      project_id: cost.project_id || 'none',
      description: cost.description,
      amount: cost.amount.toString(),
      currency: cost.currency
    });
  };

  const handleDeleteExternalCost = async (costId) => {
    if (!confirm('Are you sure you want to delete this external cost?')) return;
    
    try {
      await axios.delete(`${API_BASE}/api/external-costs/${costId}`);
      const response = await axios.get(`${API_BASE}/api/external-costs`);
      setExternalCosts(response.data);
      alert('External cost deleted successfully!');
    } catch (error) {
      alert('Failed to delete external cost: ' + (error.response?.data?.detail || 'Unknown error'));
    }
  };

  const handleSelectExternalCost = (costId, checked) => {
    setSelectedExternalCosts(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(costId);
      } else {
        newSet.delete(costId);
      }
      return newSet;
    });
  };

  const handleMarkExternalCostsInvoiced = async (invoiceNum) => {
    const normalizedInvoiceNumber = normalizeInvoiceNumber(invoiceNum);
    
    if (!isValidInvoiceNumber(normalizedInvoiceNumber)) {
      alert('Please enter a valid invoice number');
      return;
    }

    if (selectedExternalCosts.size === 0) {
      alert('Please select external costs to mark as invoiced');
      return;
    }

    try {
      const response = await axios.post(`${API_BASE}/api/external-costs/mark-invoiced`, {
        external_cost_ids: Array.from(selectedExternalCosts),
        invoice_number: normalizedInvoiceNumber
      });

      // Clear selection
      setSelectedExternalCosts(new Set());
      
      // Refresh external costs
      const costsResponse = await axios.get(`${API_BASE}/api/external-costs`);
      setExternalCosts(costsResponse.data);
      
      // Refresh invoices
      await fetchInvoices();

      alert(`Successfully marked ${response.data.entries_count} external costs as invoiced with invoice number: ${response.data.invoice_number}`);
    } catch (error) {
      console.error('Failed to mark external costs as invoiced:', error);
      alert(error.response?.data?.detail || 'Failed to mark external costs as invoiced');
    }
  };

  const fetchFilteredExternalCosts = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.client_id !== 'all') params.append('client_id', filters.client_id);
      if (filters.project_id !== 'all') params.append('project_id', filters.project_id);
      if (filters.invoiced === 'invoiced') params.append('is_invoiced', 'true');
      if (filters.invoiced === 'not_invoiced') params.append('is_invoiced', 'false');

      const response = await axios.get(`${API_BASE}/api/external-costs?${params.toString()}`);
      setExternalCosts(response.data);
    } catch (error) {
      console.error('Failed to fetch filtered external costs:', error);
    }
  };

  // Law firm configuration from settings
  const getLawFirmInfo = () => {
    // Use lawFirmSettings if available (from database), then lawFirmForm (current form), then fallback
    const settings = lawFirmSettings || lawFirmForm;
    
    if (settings && settings.business_name) {
      return {
        business_name: settings.business_name || "Your Law Firm Name",
        address: {
          street: settings.address?.street || "123 Legal Street",
          building: settings.address?.building || "Suite 100", 
          city: settings.address?.city || "Legal City",
          postal_code: settings.address?.postal_code || "12345",
          country: settings.address?.country || "Country"
        },
        vat_number: settings.vat_number || "VAT123456789"
      };
    }
    
    // Default fallback
    return {
      business_name: "Your Law Firm Name",
      address: {
        street: "123 Legal Street",
        building: "Suite 100", 
        city: "Legal City",
        postal_code: "12345",
        country: "Country"
      },
      vat_number: "VAT123456789"
    };
  };

  // Helper function to handle Polish characters in PDF (improved version)
  const formatPolishText = (text) => {
    if (!text) return '';
    return text
      .replace(/ą/g, 'a').replace(/Ą/g, 'A')
      .replace(/ć/g, 'c').replace(/Ć/g, 'C')
      .replace(/ę/g, 'e').replace(/Ę/g, 'E')
      .replace(/ł/g, 'l').replace(/Ł/g, 'L')
      .replace(/ń/g, 'n').replace(/Ń/g, 'N')
      .replace(/ó/g, 'o').replace(/Ó/g, 'O')
      .replace(/ś/g, 's').replace(/Ś/g, 'S')
      .replace(/ź/g, 'z').replace(/Ź/g, 'Z')
      .replace(/ż/g, 'z').replace(/Ż/g, 'Z');
  };

  // Report generation functions
  const handleGenerateReport = (source) => {
    setReportSource(source);
    setIsReportModalOpen(true);
  };

  const generateExcelReport = () => {
    try {
      const reportData = prepareReportData();
      const lawFirmInfo = getLawFirmInfo(); // Get current law firm info
      const wb = XLSX.utils.book_new();

      // Group entries by project
      const { projectEntries, nonProjectEntries } = groupEntriesByProject(reportData.entries);

      let allData = [];
      let merges = [];
      let currentRow = 0;

      // Helper function to add styled header
      const addStyledHeader = (text, startRow, colspan = 6) => {
        const headerRow = new Array(colspan).fill('');
        headerRow[0] = text;
        allData.push(headerRow);
        
        // Add merge for header if colspan > 1
        if (colspan > 1) {
          merges.push({
            s: { r: startRow, c: 0 },
            e: { r: startRow, c: colspan - 1 }
          });
        }
        return startRow + 1;
      };

      // Law firm information
      currentRow = addStyledHeader('INFORMACJE O KANCELARII / LAW FIRM INFORMATION', currentRow, 6);
      allData.push([lawFirmInfo.business_name, '', '', '', '', '']);
      allData.push([`${lawFirmInfo.address.street} ${lawFirmInfo.address.building}`, '', '', '', '', '']);
      allData.push([`${lawFirmInfo.address.city}, ${lawFirmInfo.address.postal_code}`, '', '', '', '', '']);
      allData.push([lawFirmInfo.address.country, '', '', '', '', '']);
      allData.push([`NIP/VAT: ${lawFirmInfo.vat_number}`, '', '', '', '', '']);
      allData.push(['', '', '', '', '', '']); // Empty row
      currentRow += 6;

      // Client information  
      currentRow = addStyledHeader('INFORMACJE O KLIENCIE / CLIENT INFORMATION', currentRow, 6);
      allData.push([reportData.client.business_name, '', '', '', '', '']);
      allData.push([`${reportData.client.address.street} ${reportData.client.address.building}`, '', '', '', '', '']);
      allData.push([`${reportData.client.address.city}, ${reportData.client.address.postal_code}`, '', '', '', '', '']);
      allData.push([reportData.client.address.country, '', '', '', '', '']);
      allData.push([`NIP/VAT: ${reportData.client.vat_number || 'Brak/N/A'}`, '', '', '', '', '']);
      allData.push(['', '', '', '', '', '']); // Empty row
      currentRow += 6;

      // Report date (with invoice info if applicable)
      if (reportData.isInvoiceReport) {
        const invoiceNumber = reportData.invoiceNumber || 'N/A';
        const invoiceDate = reportData.invoiceDate || new Date();
        allData.push([`ZAŁĄCZNIK DO FAKTURY / TIMESHEET TO INVOICE: ${invoiceNumber}`, '', '', '', '', '']);
        
        // Safely format invoice date
        try {
          const formattedInvoiceDate = format(new Date(invoiceDate), 'dd.MM.yyyy HH:mm');
          allData.push([`DATA FAKTURY / INVOICE DATE: ${formattedInvoiceDate}`, '', '', '', '', '']);
        } catch (error) {
          console.error('Error formatting invoice date in Excel:', error);
          allData.push([`DATA FAKTURY / INVOICE DATE: ${new Date().toLocaleDateString()}`, '', '', '', '', '']);
        }
        
        allData.push([`RAPORT WYGENEROWANY / REPORT GENERATED: ${format(new Date(), 'dd.MM.yyyy')}`, '', '', '', '', '']);
        currentRow += 3;
      } else {
        allData.push([`RAPORT WYGENEROWANY / REPORT GENERATED: ${format(new Date(), 'dd.MM.yyyy')}`, '', '', '', '', '']);
        currentRow += 1;
      }
      allData.push(['', '', '', '', '', '']); // Empty row
      currentRow += 1;

      // Process non-project entries first
      if (nonProjectEntries.length > 0) {
        currentRow = addStyledHeader('PRACE POZAPROJEKTOWE / NON-PROJECT ENTRIES', currentRow, 6);
        
        // Table headers
        allData.push(['Data/Date', 'Opis/Description', 'Czas/Time', 'Płatne/Chargeable', 'Stawka/Rate', 'Koszt/Cost']);
        currentRow += 1;

        let totalHours = 0;
        let totalCost = 0;

        nonProjectEntries.forEach(entry => {
          const hours = entry.hours + (entry.minutes / 60);
          const cost = calculateEntryCost(entry);
          const currency = entry.client?.hourly_rate_currency || 'USD';
          
          totalHours += hours;
          totalCost += cost;

          allData.push([
            format(new Date(entry.date), 'dd.MM.yyyy'),
            entry.description,
            formatTime(entry.hours, entry.minutes),
            entry.is_chargeable ? 'Tak/Yes' : 'Nie/No',
            entry.client?.hourly_rate ? `${entry.client.hourly_rate} ${currency}/h` : '-',
            cost > 0 ? `${cost.toFixed(2)} ${currency}` : '-'
          ]);
          currentRow++;
        });

        // Add totals for non-project entries
        allData.push(['', '', `${totalHours.toFixed(2)}h`, '', 'RAZEM/TOTAL:', `${totalCost.toFixed(2)} ${reportData.client.hourly_rate_currency || 'USD'}`]);
        allData.push(['', '', '', '', '', '']); // Empty row
        currentRow += 2;
      }

      // Process project entries
      Object.entries(projectEntries).forEach(([projectId, entries]) => {
        const project = projects.find(p => p.id === projectId);
        if (!project) return;

        currentRow = addStyledHeader(`PROJEKT / PROJECT: ${project.name.toUpperCase()}`, currentRow, 6);
        
        // Project table headers (now including Rate and Cost columns)
        allData.push(['Data/Date', 'Opis/Description', 'Czas/Time', 'Płatne/Chargeable', 'Stawka/Rate', 'Koszt/Cost']);
        currentRow += 1;

        let totalHours = 0;

        entries.forEach(entry => {
          const hours = entry.hours + (entry.minutes / 60);
          totalHours += hours;

          // For project entries, use project rate information
          let hourlyRate = '';
          let entryCost = '';
          const currency = project.hourly_rate_currency || project.fixed_rate_currency || 'USD';

          if (project.settlement_method === 'hourly_rate' && project.hourly_rate) {
            hourlyRate = `${project.hourly_rate} ${currency}/h`;
            entryCost = entry.is_chargeable ? `${(hours * project.hourly_rate).toFixed(2)} ${currency}` : '-';
          } else if (project.settlement_method === 'fixed_rate') {
            hourlyRate = `Fixed Rate Project`;
            entryCost = '-'; // Individual entries don't show cost for fixed rate projects
          } else {
            hourlyRate = '-';
            entryCost = '-';
          }

          allData.push([
            format(new Date(entry.date), 'dd.MM.yyyy'),
            entry.description,
            formatTime(entry.hours, entry.minutes),
            entry.is_chargeable ? 'Tak/Yes' : 'Nie/No',
            hourlyRate,
            entryCost
          ]);
          currentRow++;
        });

        // Calculate project fee
        let projectFee = project.fixed_rate || 0;
        
        // Apply cap if exists
        if (project.cap_amount && projectFee > project.cap_amount) {
          projectFee = project.cap_amount;
        }

        const currency = project.fixed_rate_currency || project.hourly_rate_currency || 'USD';
        
        // Add totals for project
        allData.push(['', '', `${totalHours.toFixed(2)}h`, '', 'OPŁATA ZA PROJEKT/PROJECT FEE:', `${projectFee.toFixed(2)} ${currency}`]);
        allData.push(['', '', '', '', '', '']); // Empty row
        currentRow += 2;
      });

      // Process external costs
      if (reportData.externalCosts && reportData.externalCosts.length > 0) {
        currentRow = addStyledHeader('KOSZTY ZEWNĘTRZNE / EXTERNAL COSTS', currentRow, 6);
        
        // External costs table headers
        allData.push(['Data/Date', 'Opis/Description', 'Projekt/Project', 'Kwota/Amount', 'Waluta/Currency', 'Status']);
        currentRow += 1;

        let totalAmount = 0;
        const costsByCurrency = {};

        reportData.externalCosts.forEach(cost => {
          const project = cost.project_id ? projects.find(p => p.id === cost.project_id) : null;
          
          if (!costsByCurrency[cost.currency]) {
            costsByCurrency[cost.currency] = 0;
          }
          costsByCurrency[cost.currency] += cost.amount;

          allData.push([
            format(new Date(cost.created_at), 'dd.MM.yyyy'),
            cost.description || 'N/A',
            project?.name || 'Brak/None',
            cost.amount.toFixed(2),
            cost.currency,
            cost.invoice_number ? 'Zafakturowane/Invoiced' : 'Niezafakturowane/Not Invoiced'
          ]);
          currentRow++;
        });

        // Add totals for external costs by currency
        Object.entries(costsByCurrency).forEach(([currency, amount]) => {
          allData.push(['', '', '', '', `RAZEM ${currency}/TOTAL ${currency}:`, `${amount.toFixed(2)} ${currency}`]);
          currentRow++;
        });
        
        allData.push(['', '', '', '', '', '']); // Empty row
        currentRow += 1;
      }

      // Create worksheet
      const ws = XLSX.utils.aoa_to_sheet(allData);

      // Apply styling and formatting
      const range = XLSX.utils.decode_range(ws['!ref']);
      
      // Set column widths with proper alignment
      ws['!cols'] = [
        { wch: 12 }, // Date
        { wch: 45 }, // Description  
        { wch: 8 },  // Time
        { wch: 15 }, // Chargeable
        { wch: 18 }, // Rate
        { wch: 18 }  // Cost
      ];

      // Apply merges
      if (merges.length > 0) {
        ws['!merges'] = merges;
      }

      // Style cells with proper alignment
      for (let R = range.s.r; R <= range.e.r; ++R) {
        for (let C = range.s.c; C <= range.e.c; ++C) {
          const cell_address = XLSX.utils.encode_cell({ c: C, r: R });
          if (!ws[cell_address]) continue;
          
          // Initialize cell style if it doesn't exist
          if (!ws[cell_address].s) ws[cell_address].s = {};
          
          // Header rows styling (law firm info, client info, etc.)
          if (allData[R] && allData[R][0] && 
              (allData[R][0].includes('INFORMATION') || 
               allData[R][0].includes('INFORMACJE') ||
               allData[R][0].includes('PROJEKT') ||
               allData[R][0].includes('PROJECT:') ||
               allData[R][0].includes('PRACE'))) {
            ws[cell_address].s = {
              font: { bold: true, color: { rgb: "FFFFFF" } },
              fill: { fgColor: { rgb: "366092" } },
              alignment: { horizontal: "left", vertical: "center" },
              border: {
                top: { style: "thin" },
                bottom: { style: "thin" },
                left: { style: "thin" },
                right: { style: "thin" }
              }
            };
          }
          // Table headers
          else if (allData[R] && 
                   (allData[R].includes('Data/Date') || allData[R].includes('Opis/Description'))) {
            ws[cell_address].s = {
              font: { bold: true },
              fill: { fgColor: { rgb: "D9E2F3" } },
              alignment: { horizontal: "center", vertical: "center" },
              border: {
                top: { style: "thin" },
                bottom: { style: "thin" },
                left: { style: "thin" },
                right: { style: "thin" }
              }
            };
          }
          // Total rows
          else if (allData[R] && (allData[R].join('').includes('RAZEM') || allData[R].join('').includes('TOTAL') || allData[R].join('').includes('OPŁATA'))) {
            ws[cell_address].s = {
              font: { bold: true },
              fill: { fgColor: { rgb: "F2F2F2" } },
              alignment: { 
                horizontal: C >= 4 ? "right" : "left", // Right align currency columns
                vertical: "center" 
              },
              border: {
                top: { style: "medium" },
                bottom: { style: "thin" },
                left: { style: "thin" },
                right: { style: "thin" }
              }
            };
          }
          // Currency and numeric columns (Rate and Cost)
          else if (C >= 4) {
            ws[cell_address].s = {
              border: {
                top: { style: "hair" },
                bottom: { style: "hair" },
                left: { style: "hair" },
                right: { style: "hair" }
              },
              alignment: { 
                horizontal: "right", // Right align currency values
                vertical: "center" 
              }
            };
          }
          // Regular data cells
          else {
            ws[cell_address].s = {
              border: {
                top: { style: "hair" },
                bottom: { style: "hair" },
                left: { style: "hair" },
                right: { style: "hair" }
              },
              alignment: { vertical: "center", wrapText: true }
            };
          }
        }
      }

      XLSX.utils.book_append_sheet(wb, ws, "Raport Czasu - Time Report"); // Changed / to - for Excel compatibility
      
      const fileName = `raport_czasu_${format(new Date(), 'yyyy-MM-dd')}.xlsx`;
      XLSX.writeFile(wb, fileName);
      
      setIsReportModalOpen(false);
    } catch (error) {
      console.error('Error generating Excel report:', error);
      alert('Błąd generowania raportu Excel / Error generating Excel report: ' + error.message);
    }
  };

  const generatePDFReport = () => {
    try {
      const reportData = prepareReportData();
      const lawFirmInfo = getLawFirmInfo(); // Get current law firm info
      const doc = new jsPDF();
      
      // Group entries by project
      const { projectEntries, nonProjectEntries } = groupEntriesByProject(reportData.entries);

      let yPosition = 20;

      // Add law firm info with consistent styling
      doc.setFontSize(14);
      doc.setFont("helvetica", 'bold');
      doc.setFillColor(54, 96, 146); // Blue background
      doc.setTextColor(255, 255, 255); // White text
      doc.rect(20, yPosition - 5, 170, 8, 'F'); // Background rectangle
      doc.text(formatPolishText('INFORMACJE O KANCELARII / LAW FIRM INFORMATION'), 22, yPosition);
      yPosition += 15;
      
      // Law firm details
      doc.setTextColor(0, 0, 0); // Black text
      doc.setFontSize(12);
      doc.setFont("helvetica", 'bold');
      doc.text(formatPolishText(lawFirmInfo.business_name), 20, yPosition);
      yPosition += 8;
      
      doc.setFontSize(10);
      doc.setFont("helvetica", 'normal');
      doc.text(formatPolishText(`${lawFirmInfo.address.street} ${lawFirmInfo.address.building}`), 20, yPosition);
      yPosition += 5;
      doc.text(formatPolishText(`${lawFirmInfo.address.city}, ${lawFirmInfo.address.postal_code}`), 20, yPosition);
      yPosition += 5;
      doc.text(formatPolishText(lawFirmInfo.address.country), 20, yPosition);
      yPosition += 5;
      doc.text(formatPolishText(`NIP/VAT: ${lawFirmInfo.vat_number}`), 20, yPosition);
      yPosition += 20;

      // Add client info with SAME styling as law firm
      doc.setFontSize(14);
      doc.setFont("helvetica", 'bold');
      doc.setFillColor(54, 96, 146); // Blue background
      doc.setTextColor(255, 255, 255); // White text
      doc.rect(20, yPosition - 5, 170, 8, 'F'); // Background rectangle
      doc.text(formatPolishText('INFORMACJE O KLIENCIE / CLIENT INFORMATION'), 22, yPosition);
      yPosition += 15;
      
      // Client details with same formatting as law firm
      doc.setTextColor(0, 0, 0); // Black text
      doc.setFontSize(12);
      doc.setFont("helvetica", 'bold');
      doc.text(formatPolishText(reportData.client.business_name || 'Unknown Client'), 20, yPosition);
      yPosition += 8;
      
      doc.setFontSize(10);
      doc.setFont("helvetica", 'normal');
      const clientStreet = reportData.client.address?.street || '';
      const clientBuilding = reportData.client.address?.building || '';
      const clientCity = reportData.client.address?.city || '';
      const clientPostal = reportData.client.address?.postal_code || '';
      const clientCountry = reportData.client.address?.country || '';
      const clientVat = reportData.client.vat_number || 'Brak/N/A';
      
      if (clientStreet || clientBuilding) {
        doc.text(formatPolishText(`${clientStreet} ${clientBuilding}`.trim()), 20, yPosition);
        yPosition += 5;
      }
      if (clientCity || clientPostal) {
        doc.text(formatPolishText(`${clientCity}${clientPostal ? ', ' + clientPostal : ''}`.trim()), 20, yPosition);
        yPosition += 5;
      }
      if (clientCountry) {
        doc.text(formatPolishText(clientCountry), 20, yPosition);
        yPosition += 5;
      }
      doc.text(formatPolishText(`NIP/VAT: ${clientVat}`), 20, yPosition);
      yPosition += 20;

      // Report date section with styling (with invoice info if applicable)
      doc.setFontSize(11);
      doc.setFont("helvetica", 'bold');
      doc.setFillColor(240, 248, 255); // Light blue background
      doc.setTextColor(0, 0, 0); // Black text for better contrast on light blue background
      doc.rect(20, yPosition - 5, 170, 8, 'F');
      if (reportData.isInvoiceReport) {
        const invoiceNumber = reportData.invoiceNumber || 'N/A';
        const invoiceDate = reportData.invoiceDate || new Date();
        
        doc.text(formatPolishText(`Załącznik do faktury / Timesheet to invoice: ${invoiceNumber}`), 22, yPosition);
        yPosition += 8;
        doc.rect(20, yPosition - 5, 170, 8, 'F');
        
        // Safely format invoice date
        try {
          const formattedInvoiceDate = format(new Date(invoiceDate), 'dd.MM.yyyy HH:mm');
          doc.text(formatPolishText(`Data faktury / Invoice Date: ${formattedInvoiceDate}`), 22, yPosition);
        } catch (error) {
          console.error('Error formatting invoice date:', error);
          doc.text(formatPolishText(`Data faktury / Invoice Date: ${new Date().toLocaleDateString()}`), 22, yPosition);
        }
        
        yPosition += 8;
        doc.rect(20, yPosition - 5, 170, 8, 'F');
        doc.text(formatPolishText(`Data raportu / Report Date: ${format(new Date(), 'dd.MM.yyyy')}`), 22, yPosition);
      } else {
        doc.text(formatPolishText(`Data raportu / Report Date: ${format(new Date(), 'dd.MM.yyyy')}`), 22, yPosition);
      }
      yPosition += 20;
      
      doc.setTextColor(0, 0, 0); // Reset to black

      // Process non-project entries
      if (nonProjectEntries.length > 0) {
        doc.setFontSize(10); // Reduced from 12 to 10 for better visual balance
        doc.setFont("helvetica", 'bold');
        doc.text(formatPolishText('PRACE POZAPROJEKTOWE / NON-PROJECT ENTRIES'), 20, yPosition);
        yPosition += 10;

        const nonProjectTableData = nonProjectEntries.map(entry => {
          const cost = calculateEntryCost(entry);
          const currency = entry.client?.hourly_rate_currency || 'USD';
          
          return [
            format(new Date(entry.date), 'dd.MM.yyyy'),
            formatPolishText(entry.description || 'No description'),
            formatTime(entry.hours, entry.minutes),
            formatPolishText(entry.is_chargeable ? 'Tak/Yes' : 'Nie/No'),
            entry.client?.hourly_rate ? `${entry.client.hourly_rate} ${currency}/h` : '-',
            cost > 0 ? `${cost.toFixed(2)} ${currency}` : '-'
          ];
        });

        const totalHours = nonProjectEntries.reduce((sum, entry) => sum + entry.hours + (entry.minutes / 60), 0);
        const totalCost = nonProjectEntries.reduce((sum, entry) => sum + calculateEntryCost(entry), 0);
        const currency = reportData.client.hourly_rate_currency || 'USD';

        autoTable(doc, {
          startY: yPosition,
          head: [[
            formatPolishText('Data/Date'), 
            formatPolishText('Opis/Description'), 
            formatPolishText('Czas/Time'), 
            formatPolishText('Platne/Chargeable'), 
            formatPolishText('Stawka/Rate'), 
            formatPolishText('Koszt/Cost')
          ]],
          body: nonProjectTableData,
          foot: [['', '', `${totalHours.toFixed(2)}h`, '', formatPolishText('RAZEM/TOTAL:'), `${totalCost.toFixed(2)} ${currency}`]],
          theme: 'grid',
          styles: { 
            fontSize: 9,
            font: 'helvetica',
            cellPadding: 3
          },
          headStyles: { 
            fillColor: [54, 96, 146], 
            textColor: 255,
            fontStyle: 'bold',
            halign: 'center'
          },
          footStyles: { 
            fillColor: [220, 220, 220], 
            textColor: [0, 0, 0], // Black text for better contrast
            fontStyle: 'bold',
            halign: 'right'
          },
          columnStyles: {
            0: { halign: 'center', cellWidth: 25 }, // Date
            1: { halign: 'left', cellWidth: 'auto' }, // Description
            2: { halign: 'center', cellWidth: 20 }, // Time
            3: { halign: 'center', cellWidth: 25 }, // Chargeable
            4: { halign: 'right', cellWidth: 30 }, // Rate
            5: { halign: 'right', cellWidth: 30 }  // Cost
          }
        });

        yPosition = doc.lastAutoTable.finalY + 20;
      }

      // Process project entries
      Object.entries(projectEntries).forEach(([projectId, entries]) => {
        const project = projects.find(p => p.id === projectId);
        if (!project) return;

        // Check if we need a new page
        if (yPosition > 250) {
          doc.addPage();
          yPosition = 20;
        }

        doc.setFontSize(10); // Reduced from 12 to 10 for better visual balance
        doc.setFont("helvetica", 'bold');
        const projectName = project?.name || 'Unnamed Project';
        doc.text(formatPolishText(`PROJEKT / PROJECT: ${projectName.toUpperCase()}`), 20, yPosition);
        yPosition += 10;

        const projectTableData = entries.map(entry => [
          format(new Date(entry.date), 'dd.MM.yyyy'),
          formatPolishText(entry.description || 'No description'),
          formatTime(entry.hours, entry.minutes),
          formatPolishText(entry.is_chargeable ? 'Tak/Yes' : 'Nie/No')
        ]);

        const totalHours = entries.reduce((sum, entry) => sum + entry.hours + (entry.minutes / 60), 0);
        
        // Calculate project fee
        let projectFee = project.fixed_rate || 0;
        
        // Apply cap if exists
        if (project.cap_amount && projectFee > project.cap_amount) {
          projectFee = project.cap_amount;
        }

        const currency = project.fixed_rate_currency || project.hourly_rate_currency || 'USD';

        autoTable(doc, {
          startY: yPosition,
          head: [[
            formatPolishText('Data/Date'), 
            formatPolishText('Opis/Description'), 
            formatPolishText('Czas/Time'), 
            formatPolishText('Platne/Chargeable')
          ]],
          body: projectTableData,
          foot: [['', '', `${totalHours.toFixed(2)}h`, formatPolishText(`OPLATA/FEE: ${projectFee.toFixed(2)} ${currency}`)]],
          theme: 'grid',
          styles: { 
            fontSize: 9,
            font: 'helvetica',
            cellPadding: 3
          },
          headStyles: { 
            fillColor: [54, 96, 146], 
            textColor: 255,
            fontStyle: 'bold',
            halign: 'center'
          },
          footStyles: { 
            fillColor: [240, 248, 255], 
            textColor: [0, 0, 0], // Black text for better contrast on light blue background
            fontStyle: 'bold',
            halign: 'right'
          },
          columnStyles: {
            0: { halign: 'center', cellWidth: 25 }, // Date
            1: { halign: 'left', cellWidth: 'auto' }, // Description
            2: { halign: 'center', cellWidth: 20 }, // Time
            3: { halign: 'center', cellWidth: 30 }  // Chargeable
          }
        });

        yPosition = doc.lastAutoTable.finalY + 20;
      });

      // Process external costs
      if (reportData.externalCosts && reportData.externalCosts.length > 0) {
        // Check for page break
        if (yPosition > 250) {
          doc.addPage();
          yPosition = 30;
        }
        
        // External costs header
        doc.setFontSize(10); // Reduced font size for section headers
        doc.setFont("helvetica", 'bold');
        doc.setFillColor(54, 96, 146);
        doc.setTextColor(255, 255, 255);
        doc.rect(20, yPosition - 5, 170, 8, 'F');
        doc.text(formatPolishText('KOSZTY ZEWNETRZNE / EXTERNAL COSTS'), 22, yPosition);
        yPosition += 20;

        const externalCostsTableData = reportData.externalCosts.map(cost => {
          const project = cost.project_id ? projects.find(p => p.id === cost.project_id) : null;
          return [
            format(new Date(cost.created_at), 'dd.MM.yyyy'),
            formatPolishText(cost.description || 'N/A'),
            formatPolishText(project?.name || 'Brak/None'),
            `${cost.amount.toFixed(2)} ${cost.currency}`,
            cost.invoice_number ? formatPolishText('Zafakturowane/Invoiced') : formatPolishText('Niezafakturowane/Not Invoiced')
          ];
        });

        // Calculate totals by currency
        const costsByCurrency = {};
        reportData.externalCosts.forEach(cost => {
          if (!costsByCurrency[cost.currency]) {
            costsByCurrency[cost.currency] = 0;
          }
          costsByCurrency[cost.currency] += cost.amount;
        });

        const totalString = Object.entries(costsByCurrency)
          .map(([currency, amount]) => `${amount.toFixed(2)} ${currency}`)
          .join(', ');

        autoTable(doc, {
          startY: yPosition,
          head: [[
            formatPolishText('Data/Date'), 
            formatPolishText('Opis/Description'), 
            formatPolishText('Projekt/Project'),
            formatPolishText('Kwota/Amount'),
            formatPolishText('Status')
          ]],
          body: externalCostsTableData,
          foot: [['', '', '', formatPolishText(`RAZEM/TOTAL: ${totalString}`), '']],
          theme: 'grid',
          styles: { 
            fontSize: 9,
            font: 'helvetica',
            cellPadding: 3
          },
          headStyles: { 
            fillColor: [54, 96, 146], 
            textColor: 255,
            fontStyle: 'bold',
            halign: 'center'
          },
          footStyles: { 
            fillColor: [240, 248, 255], 
            textColor: [0, 0, 0],
            fontStyle: 'bold',
            halign: 'right'
          },
          columnStyles: {
            0: { halign: 'center', cellWidth: 25 }, // Date
            1: { halign: 'left', cellWidth: 'auto' }, // Description
            2: { halign: 'left', cellWidth: 35 }, // Project
            3: { halign: 'right', cellWidth: 30 }, // Amount
            4: { halign: 'center', cellWidth: 35 }  // Status
          }
        });

        yPosition = doc.lastAutoTable.finalY + 20;
      }

      const fileName = `raport_czasu_${format(new Date(), 'yyyy-MM-dd')}.pdf`;
      doc.save(fileName);
      
      setIsReportModalOpen(false);
    } catch (error) {
      console.error('Error generating PDF report:', error);
      alert(formatPolishText('Blad generowania raportu PDF / Error generating PDF report: ') + error.message);
    }
  };

  const prepareReportData = () => {
    // Handle invoice reports
    if (reportSource.startsWith('invoice-') && selectedInvoiceDetails) {
      // For invoice reports, use both time entries and external costs
      const sortedEntries = [...(selectedInvoiceDetails.entries || [])].sort((a, b) => new Date(a.date) - new Date(b.date));
      const sortedCosts = [...(selectedInvoiceDetails.external_costs || [])].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      
      // Get main client for invoice (use the most common client in the time entries, fallback to external costs)
      const clientCounts = {};
      selectedInvoiceDetails.entries?.forEach(entry => {
        clientCounts[entry.client_id] = (clientCounts[entry.client_id] || 0) + 1;
      });
      selectedInvoiceDetails.external_costs?.forEach(cost => {
        clientCounts[cost.client_id] = (clientCounts[cost.client_id] || 0) + 1;
      });
      const mainClientId = Object.keys(clientCounts).reduce((a, b) => clientCounts[a] > clientCounts[b] ? a : b);
      const mainClient = clients.find(c => c.id === mainClientId);
      
      return {
        entries: sortedEntries,
        externalCosts: sortedCosts,
        client: mainClient || { business_name: 'Multiple Clients', address: {}, vat_number: '' },
        isInvoiceReport: true,
        invoiceNumber: selectedInvoiceDetails.invoice_number,
        invoiceDate: selectedInvoiceDetails.created_at
      };
    }

    // Handle external costs reports
    if (reportSource === 'external-costs') {
      // Get current filtered external costs
      const sortedCosts = [...externalCosts].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      
      // Get main client
      const mainClient = clients.find(c => c.id === filters.client_id) || clients[0];
      
      return {
        entries: [],
        externalCosts: sortedCosts,
        client: mainClient || { business_name: 'All Clients', address: {}, vat_number: '' },
        isExternalCostsReport: true
      };
    }
    
    // Regular dashboard/timesheet reports
    // Get current filtered entries based on source
    const currentEntries = getSortedTimeEntries(timeEntries, reportSource === 'dashboard' ? dashboardSortConfig : sortConfig);
    
    // Sort by date as required
    const sortedEntries = [...currentEntries].sort((a, b) => new Date(a.date) - new Date(b.date));
    
    // Get main client (assuming reports are per client)
    const mainClient = clients.find(c => c.id === filters.client_id) || clients[0];
    
    return {
      entries: sortedEntries,
      externalCosts: [],
      client: mainClient || { business_name: 'Unknown Client', address: {}, vat_number: '' }
    };
  };

  const groupEntriesByProject = (entries) => {
    const projectEntries = {};
    const nonProjectEntries = [];
    
    entries.forEach(entry => {
      if (entry.project_id && entry.project_id !== 'none') {
        if (!projectEntries[entry.project_id]) {
          projectEntries[entry.project_id] = [];
        }
        projectEntries[entry.project_id].push(entry);
      } else {
        nonProjectEntries.push(entry);
      }
    });
    
    return { projectEntries, nonProjectEntries };
  };

  // Edit functions
  const handleEditClient = (client) => {
    setEditingClient({
      ...client,
      hourly_rate: client.hourly_rate || '',
      hourly_rate_currency: client.hourly_rate_currency || 'USD',
      fixed_rate: client.fixed_rate || '',
      fixed_rate_currency: client.fixed_rate_currency || 'USD',
      cap_amount: client.cap_amount || '',
      cap_currency: client.cap_currency || 'USD',
      cap_period: client.cap_period || ''
    });
    setIsEditModalOpen(true);
  };

  const handleEditUser = (user) => {
    setEditingUser({
      ...user,
      password: '', // Don't pre-fill password
      position_id: user.position_id || ''
    });
    setIsEditModalOpen(true);
  };

  const handleEditPosition = (position) => {
    setEditingPosition({
      ...position,
      hourly_rate: position.hourly_rate || '',
      currency: position.currency || 'USD'
    });
    setIsEditModalOpen(true);
  };

  const handleEditTimeEntry = (entry) => {
    setEditingTimeEntry({
      ...entry,
      date: new Date(entry.date)
    });
    setIsEditModalOpen(true);
  };

  // View functions
  const handleViewClient = async (client) => {
    setViewingClient(client);
    setIsViewModalOpen(true);
  };

  const handleViewUser = async (user) => {
    setViewingUser(user);
    setIsViewModalOpen(true);
  };

  const handleViewPosition = async (position) => {
    setViewingPosition(position);
    setIsViewModalOpen(true);
  };

  // Delete functions
  const handleDeleteItem = (item, type) => {
    setDeletingItem({ ...item, type });
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async (permanent = false) => {
    try {
      setLoading(true);
      const { id, type } = deletingItem;
      
      await axios.delete(`${API_BASE}/api/${type}s/${id}?permanent=${permanent}`);
      
      // Refresh the appropriate list
      if (type === 'client') {
        const response = await axios.get(`${API_BASE}/api/clients`);
        setClients(response.data);
      } else if (type === 'user') {
        const response = await axios.get(`${API_BASE}/api/users`);
        setUsers(response.data);
      } else if (type === 'position') {
        const response = await axios.get(`${API_BASE}/api/positions`);
        setPositions(response.data);
      }
      
      setIsDeleteModalOpen(false);
      setDeletingItem(null);
      alert(`${type.charAt(0).toUpperCase() + type.slice(1)} ${permanent ? 'permanently deleted' : 'suspended'} successfully!`);
    } catch (error) {
      console.error('Delete error:', error);
      let errorMessage = 'Unknown error';
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      }
      alert(`Failed to delete ${deletingItem?.type}: ` + errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleRestoreItem = async (item, type) => {
    try {
      setLoading(true);
      await axios.put(`${API_BASE}/api/${type}s/${item.id}/restore`);
      
      // Refresh the appropriate list
      if (type === 'client') {
        const response = await axios.get(`${API_BASE}/api/clients`);
        setClients(response.data);
      } else if (type === 'user') {
        const response = await axios.get(`${API_BASE}/api/users`);
        setUsers(response.data);
      } else if (type === 'position') {
        const response = await axios.get(`${API_BASE}/api/positions`);
        setPositions(response.data);
      }
      
      alert(`${type.charAt(0).toUpperCase() + type.slice(1)} restored successfully!`);
    } catch (error) {
      console.error('Restore error:', error);
      let errorMessage = 'Unknown error';
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      }
      alert(`Failed to restore ${type}: ` + errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTimeEntry = async (entryOrId) => {
    if (window.confirm('Are you sure you want to delete this time entry? This action cannot be undone.')) {
      try {
        setLoading(true);
        // Handle both entry object and entry ID
        const entryId = typeof entryOrId === 'string' ? entryOrId : entryOrId.id;
        await axios.delete(`${API_BASE}/api/time-entries/${entryId}`);
        
        // Refresh time entries
        const response = await axios.get(`${API_BASE}/api/time-entries`);
        setTimeEntries(response.data);
        
        alert('Time entry deleted successfully!');
      } catch (error) {
        console.error('Time entry delete error:', error);
        let errorMessage = 'Unknown error';
        if (error.response?.data?.detail) {
          errorMessage = error.response.data.detail;
        }
        alert('Failed to delete time entry: ' + errorMessage);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleUpdateClient = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      
      const cleanedClientData = {
        ...editingClient,
        hourly_rate: editingClient.hourly_rate ? parseFloat(editingClient.hourly_rate) : null,
        fixed_rate: editingClient.fixed_rate ? parseFloat(editingClient.fixed_rate) : null,
        cap_amount: editingClient.cap_amount ? parseFloat(editingClient.cap_amount) : null,
        cap_period: editingClient.cap_period || null
      };
      
      await axios.put(`${API_BASE}/api/clients/${editingClient.id}`, cleanedClientData);
      
      // Refresh clients
      const response = await axios.get(`${API_BASE}/api/clients`);
      setClients(response.data);
      
      setIsEditModalOpen(false);
      setEditingClient(null);
      alert('Client updated successfully!');
    } catch (error) {
      console.error('Client update error:', error);
      let errorMessage = 'Unknown error';
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      }
      alert('Failed to update client: ' + errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await axios.put(`${API_BASE}/api/users/${editingUser.id}`, editingUser);
      
      // Refresh users
      const response = await axios.get(`${API_BASE}/api/users`);
      setUsers(response.data);
      
      setIsEditModalOpen(false);
      setEditingUser(null);
      alert('User updated successfully!');
    } catch (error) {
      console.error('User update error:', error);
      let errorMessage = 'Unknown error';
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      }
      alert('Failed to update user: ' + errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePosition = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await axios.put(`${API_BASE}/api/positions/${editingPosition.id}`, editingPosition);
      
      // Refresh positions
      const response = await axios.get(`${API_BASE}/api/positions`);
      setPositions(response.data);
      
      setIsEditModalOpen(false);
      setEditingPosition(null);
      alert('Position updated successfully!');
    } catch (error) {
      console.error('Position update error:', error);
      let errorMessage = 'Unknown error';
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      }
      alert('Failed to update position: ' + errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateTimeEntry = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await axios.put(`${API_BASE}/api/time-entries/${editingTimeEntry.id}`, editingTimeEntry);
      
      // Refresh time entries
      const response = await axios.get(`${API_BASE}/api/time-entries`);
      setTimeEntries(response.data);
      
      setIsEditModalOpen(false);
      setEditingTimeEntry(null);
      alert('Time entry updated successfully!');
    } catch (error) {
      console.error('Time entry update error:', error);
      let errorMessage = 'Unknown error';
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      }
      alert('Failed to update time entry: ' + errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleChangeCredentials = async (e) => {
    e.preventDefault();
    try {
      if (credentialsForm.new_password && credentialsForm.new_password !== credentialsForm.confirm_password) {
        alert('New passwords do not match');
        return;
      }

      setLoading(true);
      const updateData = {
        current_password: credentialsForm.current_password
      };
      
      if (credentialsForm.new_email) updateData.new_email = credentialsForm.new_email;
      if (credentialsForm.new_password) updateData.new_password = credentialsForm.new_password;
      
      const response = await axios.put(`${API_BASE}/api/auth/change-credentials`, updateData);
      setUser(response.data);
      
      setCredentialsForm({
        current_password: '',
        new_email: '',
        new_password: '',
        confirm_password: ''
      });
      setIsCredentialsModalOpen(false);
      alert('Credentials updated successfully!');
    } catch (error) {
      console.error('Credentials update error:', error);
      let errorMessage = 'Unknown error';
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      }
      alert('Failed to update credentials: ' + errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-xl">
          <CardHeader className="text-center">
            <CardTitle className="text-3xl font-bold text-slate-800">LegalWork</CardTitle>
            <CardDescription>Law Firm Time Management System</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={loginForm.email}
                  onChange={(e) => setLoginForm({...loginForm, email: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={loginForm.password}
                  onChange={(e) => setLoginForm({...loginForm, password: e.target.value})}
                  required
                />
              </div>
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>
            <div className="mt-4 text-center text-sm text-slate-600">
              Default admin: admin@legalwork.com / admin123
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <h1 className="text-2xl font-bold text-slate-800">LegalWork</h1>
              <Badge variant="outline">{getRoleDisplayName(user?.role)}</Badge>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-slate-600">{user?.name} {user?.surname}</span>
              {user?.role !== 'lawyer' && (
                <Button variant="ghost" size="sm" onClick={() => setIsCredentialsModalOpen(true)}>
                  <Edit className="h-4 w-4 mr-2" />
                  Change Credentials
                </Button>
              )}
              <Button variant="ghost" size="sm" onClick={handleLogout}>
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-4 lg:grid-cols-8 gap-1">
            <TabsTrigger value="dashboard" className="flex items-center space-x-2">
              <Building2 className="h-4 w-4" />
              <span>Dashboard</span>
            </TabsTrigger>
            <TabsTrigger value="timesheet" className="flex items-center space-x-2">
              <Clock className="h-4 w-4" />
              <span>Timesheet</span>
            </TabsTrigger>
            {user?.role === 'lawyer' ? (
              <>
                <TabsTrigger value="tasks" className="flex items-center space-x-2">
                  <FileText className="h-4 w-4" />
                  <span>Tasks</span>
                </TabsTrigger>
                <TabsTrigger value="user-settings" className="flex items-center space-x-2">
                  <Users className="h-4 w-4" />
                  <span>Settings</span>
                </TabsTrigger>
              </>
            ) : (
              <>
                <TabsTrigger value="external-costs" className="flex items-center space-x-2">
                  <FileText className="h-4 w-4" />
                  <span>External Cost</span>
                </TabsTrigger>
                <TabsTrigger value="invoices" className="flex items-center space-x-2 relative after:content-[''] after:absolute after:-right-2 after:top-1/2 after:-translate-y-1/2 after:h-6 after:w-px after:bg-border">
                  <Receipt className="h-4 w-4" />
                  <span>Invoices</span>
                </TabsTrigger>
                <TabsTrigger value="clients" className="flex items-center space-x-2">
                  <Users className="h-4 w-4" />
                  <span>Clients</span>
                </TabsTrigger>
                <TabsTrigger value="users" className="flex items-center space-x-2">
                  <Users className="h-4 w-4" />
                  <span>Users</span>
                </TabsTrigger>
                <TabsTrigger value="positions" className="flex items-center space-x-2 relative after:content-[''] after:absolute after:-right-2 after:top-1/2 after:-translate-y-1/2 after:h-6 after:w-px after:bg-border">
                  <FileText className="h-4 w-4" />
                  <span>Positions</span>
                </TabsTrigger>
                <TabsTrigger value="settings" className="flex items-center space-x-2">
                  <Building2 className="h-4 w-4" />
                  <span>Settings</span>
                </TabsTrigger>
              </>
            )}
          </TabsList>

          {/* Dashboard */}
          <TabsContent value="dashboard" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Clients</CardTitle>
                  <Users className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{clients.length}</div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Active Projects</CardTitle>
                  <FileText className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{projects.length}</div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Time Entries</CardTitle>
                  <Clock className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{timeEntries.length}</div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Hours</CardTitle>
                  <Clock className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{getTotalHours(timeEntries)}h</div>
                </CardContent>
              </Card>
            </div>

            {/* Filters - Available for all users */}
            <Card>
              <CardHeader>
                <CardTitle>Filter Time Entries</CardTitle>
                <CardDescription>
                  {user?.role === 'lawyer' 
                    ? 'Filter your time entries by client, project, or date range'
                    : 'Filter time entries by client, user, or date range'
                  }
                </CardDescription>
              </CardHeader>
              <CardContent>
                {user?.role === 'lawyer' ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                    <div>
                      <Label>Client</Label>
                      <Select
                        value={filters.client_id}
                        onValueChange={(value) => handleFilterChange('client_id', value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="All clients" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Clients</SelectItem>
                          {clientOptions.map((client) => (
                            <SelectItem key={`client-${client.id}`} value={client.id}>
                              {client.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label>Project</Label>
                      <Select
                        value={filters.project_id}
                        onValueChange={(value) => handleFilterChange('project_id', value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="All projects" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Projects</SelectItem>
                          {filteredProjectOptions.map((project) => (
                            <SelectItem key={project.id} value={project.id}>
                              {project.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label>Chargeable</Label>
                      <Select
                        value={filters.chargeable}
                        onValueChange={(value) => handleFilterChange('chargeable', value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="All entries" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Entries</SelectItem>
                          <SelectItem value="true">Chargeable Only</SelectItem>
                          <SelectItem value="false">Non-Chargeable Only</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label>Start Date</Label>
                      <Input
                        type="date"
                        value={filters.start_date}
                        onChange={(e) => handleFilterChange('start_date', e.target.value)}
                      />
                    </div>

                    <div>
                      <Label>End Date</Label>
                      <Input
                        type="date"
                        value={filters.end_date}
                        onChange={(e) => handleFilterChange('end_date', e.target.value)}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
                    <div>
                      <Label>Client</Label>
                      <Select
                        value={filters.client_id}
                        onValueChange={(value) => handleFilterChange('client_id', value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="All clients" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Clients</SelectItem>
                          {clientOptions.map((client) => (
                            <SelectItem key={`client-${client.id}`} value={client.id}>
                              {client.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label>User</Label>
                      <Select
                        value={filters.user_id}
                        onValueChange={(value) => handleFilterChange('user_id', value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="All users" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Users</SelectItem>
                          {users.map((user) => (
                            <SelectItem key={user.id} value={user.id}>
                              {user.name} {user.surname}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label>Project</Label>
                      <Select
                        value={filters.project_id}
                        onValueChange={(value) => handleFilterChange('project_id', value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="All projects" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Projects</SelectItem>
                          {filteredProjectOptions.map((project) => (
                            <SelectItem key={project.id} value={project.id}>
                              {project.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label>Chargeable</Label>
                      <Select
                        value={filters.chargeable}
                        onValueChange={(value) => handleFilterChange('chargeable', value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="All entries" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Entries</SelectItem>
                          <SelectItem value="true">Chargeable Only</SelectItem>
                          <SelectItem value="false">Non-Chargeable Only</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label>Invoiced</Label>
                      <Select
                        value={filters.invoiced}
                        onValueChange={(value) => handleFilterChange('invoiced', value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="All entries" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Entries</SelectItem>
                          <SelectItem value="true">Invoiced Only</SelectItem>
                          <SelectItem value="false">Not Invoiced Only</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label>Start Date</Label>
                      <Input
                        type="date"
                        value={filters.start_date}
                        onChange={(e) => handleFilterChange('start_date', e.target.value)}
                      />
                    </div>

                    <div>
                      <Label>End Date</Label>
                      <Input
                        type="date"
                        value={filters.end_date}
                        onChange={(e) => handleFilterChange('end_date', e.target.value)}
                      />
                    </div>
                  </div>
                )}
                
                <div className="flex space-x-2 mt-4">
                  <Button onClick={() => {
                    fetchFilteredTimeEntries();
                    if (user?.role !== 'lawyer') {
                      fetchFilteredExternalCosts();
                    }
                  }} variant="outline">
                    Apply Filters
                  </Button>
                  <Button onClick={clearFilters} variant="ghost">
                    Clear Filters
                  </Button>
                  <Button onClick={() => handleGenerateReport('dashboard')} className="bg-green-600 hover:bg-green-700">
                    <Download className="h-4 w-4 mr-2" />
                    Generate Report
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Enhanced Time Entries Table */}
            <Card>
              <CardHeader>
                <CardTitle>
                  {user?.role === 'lawyer' ? 'My Recent Time Entries' : 'All Time Entries'}
                </CardTitle>
                <CardDescription>
                  {user?.role === 'lawyer' ? 'Your recent time entries' : 'Time entries from all users'}
                </CardDescription>
              </CardHeader>
              {user?.role !== 'lawyer' && (
                <div className="px-6 pb-4">
                  <Button 
                    onClick={handleMarkAsInvoiced}
                    disabled={selectedEntries.size === 0}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <Receipt className="h-4 w-4 mr-2" />
                    Mark as Invoiced ({selectedEntries.size})
                  </Button>
                </div>
              )}
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      {user?.role !== 'lawyer' && (
                        <TableHead className="w-12">
                          <Checkbox
                            checked={selectedEntries.size > 0 && 
                              getSortedTimeEntries(timeEntries, dashboardSortConfig).slice(0, 20)
                                .filter(entry => !entry.invoice_number)
                                .every(entry => selectedEntries.has(entry.id))
                            }
                            onCheckedChange={(checked) => 
                              handleSelectAll(
                                getSortedTimeEntries(timeEntries, dashboardSortConfig).slice(0, 20)
                                  .filter(entry => !entry.invoice_number), 
                                checked
                              )
                            }
                          />
                        </TableHead>
                      )}
                      <TableHead 
                        className="cursor-pointer hover:bg-slate-100"
                        onClick={() => handleDashboardSort('date')}
                      >
                        Date {dashboardSortConfig.key === 'date' && (dashboardSortConfig.direction === 'asc' ? '↑' : '↓')}
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-slate-100"
                        onClick={() => handleDashboardSort('client')}
                      >
                        Client {dashboardSortConfig.key === 'client' && (dashboardSortConfig.direction === 'asc' ? '↑' : '↓')}
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-slate-100"
                        onClick={() => handleDashboardSort('project')}
                      >
                        Project {dashboardSortConfig.key === 'project' && (dashboardSortConfig.direction === 'asc' ? '↑' : '↓')}
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-slate-100"
                        onClick={() => handleDashboardSort('description')}
                      >
                        Description {dashboardSortConfig.key === 'description' && (dashboardSortConfig.direction === 'asc' ? '↑' : '↓')}
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-slate-100"
                        onClick={() => handleDashboardSort('time')}
                      >
                        Time {dashboardSortConfig.key === 'time' && (dashboardSortConfig.direction === 'asc' ? '↑' : '↓')}
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-slate-100"
                        onClick={() => handleDashboardSort('chargeable')}
                      >
                        Chargeable {dashboardSortConfig.key === 'chargeable' && (dashboardSortConfig.direction === 'asc' ? '↑' : '↓')}
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-slate-100"
                        onClick={() => handleDashboardSort('cost')}
                      >
                        Cost {dashboardSortConfig.key === 'cost' && (dashboardSortConfig.direction === 'asc' ? '↑' : '↓')}
                      </TableHead>
                      {user?.role !== 'lawyer' && (
                        <TableHead>User</TableHead>
                      )}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {getSortedTimeEntries(timeEntries, dashboardSortConfig).slice(0, 20).map((entry) => {
                      const client = clients.find(c => c.id === entry.client_id);
                      const project = projects.find(p => p.id === entry.project_id);
                      const entryUser = users.find(u => u.id === entry.user_id);
                      const cost = calculateEntryCost(entry);
                      
                      return (
                        <TableRow key={entry.id}>
                          {user?.role !== 'lawyer' && (
                            <TableCell>
                              {!entry.invoice_number && (
                                <Checkbox
                                  checked={selectedEntries.has(entry.id)}
                                  onCheckedChange={(checked) => handleEntrySelection(entry.id, checked)}
                                />
                              )}
                            </TableCell>
                          )}
                          <TableCell>{format(new Date(entry.date), 'MMM dd, yyyy')}</TableCell>
                          <TableCell>{client?.business_name || 'Unknown Client'}</TableCell>
                          <TableCell>{project?.name || 'No Project'}</TableCell>
                          <TableCell className="max-w-xs truncate">{entry.description}</TableCell>
                          <TableCell>{formatTime(entry.hours, entry.minutes)}</TableCell>
                          <TableCell>
                            <Badge variant={entry.is_chargeable ? "default" : "secondary"}>
                              {entry.is_chargeable ? 'Yes' : 'No'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {cost > 0 ? (
                              <span className="font-medium">
                                {project?.hourly_rate_currency || project?.fixed_rate_currency || client?.hourly_rate_currency || 'USD'} {cost.toFixed(2)}
                              </span>
                            ) : (
                              <span className="text-slate-400">-</span>
                            )}
                          </TableCell>
                          {user?.role !== 'lawyer' && (
                            <TableCell>
                              {entryUser ? `${entryUser.name} ${entryUser.surname}` : 'Unknown User'}
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Timesheet */}
          <TabsContent value="timesheet" className="space-y-6">
            {/* Filters - Admin Only */}
            {user?.role !== 'lawyer' && (
              <Card>
                <CardHeader>
                  <CardTitle>Filter Time Entries</CardTitle>
                  <CardDescription>Filter time entries by client, user, or date range</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
                    <div>
                      <Label>Client</Label>
                      <Select
                        value={filters.client_id}
                        onValueChange={(value) => handleFilterChange('client_id', value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="All clients" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Clients</SelectItem>
                          {clientOptions.map((client) => (
                            <SelectItem key={`client-${client.id}`} value={client.id}>
                              {client.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label>User</Label>
                      <Select
                        value={filters.user_id}
                        onValueChange={(value) => handleFilterChange('user_id', value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="All users" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Users</SelectItem>
                          {users.map((user) => (
                            <SelectItem key={user.id} value={user.id}>
                              {user.name} {user.surname}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label>Project</Label>
                      <Select
                        value={filters.project_id}
                        onValueChange={(value) => handleFilterChange('project_id', value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="All projects" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Projects</SelectItem>
                          {filteredProjectOptions.map((project) => (
                            <SelectItem key={project.id} value={project.id}>
                              {project.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label>Chargeable</Label>
                      <Select
                        value={filters.chargeable}
                        onValueChange={(value) => handleFilterChange('chargeable', value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="All entries" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Entries</SelectItem>
                          <SelectItem value="true">Chargeable Only</SelectItem>
                          <SelectItem value="false">Non-Chargeable Only</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label>Invoiced</Label>
                      <Select
                        value={filters.invoiced}
                        onValueChange={(value) => handleFilterChange('invoiced', value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="All entries" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Entries</SelectItem>
                          <SelectItem value="true">Invoiced Only</SelectItem>
                          <SelectItem value="false">Not Invoiced Only</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label>Start Date</Label>
                      <Input
                        type="date"
                        value={filters.start_date}
                        onChange={(e) => handleFilterChange('start_date', e.target.value)}
                      />
                    </div>

                    <div>
                      <Label>End Date</Label>
                      <Input
                        type="date"
                        value={filters.end_date}
                        onChange={(e) => handleFilterChange('end_date', e.target.value)}
                      />
                    </div>
                  </div>
                  
                  <div className="flex space-x-2 mt-4">
                    <Button onClick={() => {
                      fetchFilteredTimeEntries();
                      if (user?.role !== 'lawyer') {
                        fetchFilteredExternalCosts();
                      }
                    }} variant="outline">
                      Apply Filters
                    </Button>
                    <Button onClick={clearFilters} variant="ghost">
                      Clear Filters
                    </Button>
                    <Button onClick={() => handleGenerateReport('timesheet')} className="bg-green-600 hover:bg-green-700">
                      <Download className="h-4 w-4 mr-2" />
                      Generate Report
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Add Time Entry Form - Horizontal Layout Below Filters */}
            <Card>
              <CardHeader>
                <CardTitle>
                  {user?.role === 'lawyer' ? 'Add Time Entry' : 'Add Time Entry for User'}
                </CardTitle>
                <CardDescription>
                  {user?.role === 'lawyer' 
                    ? 'Record time spent on client work' 
                    : 'Create time entries for team members'
                  }
                </CardDescription>
              </CardHeader>
              <CardContent>
                {user?.role === 'lawyer' ? (
                  // Lawyer Time Entry Form - Horizontal Layout
                  <form onSubmit={handleTimeEntrySubmit} className="grid grid-cols-1 md:grid-cols-6 gap-4">
                    <div>
                      <Label>Client</Label>
                      <Select
                        value={timeEntryForm.client_id}
                        onValueChange={(value) => setTimeEntryForm({...timeEntryForm, client_id: value, project_id: ''})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select client" />
                        </SelectTrigger>
                        <SelectContent>
                          {clients.map((client) => (
                            <SelectItem key={client.id} value={client.id}>
                              {client.business_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label>Project</Label>
                      <Select
                        value={timeEntryForm.project_id}
                        onValueChange={(value) => setTimeEntryForm({...timeEntryForm, project_id: value})}
                        disabled={!timeEntryForm.client_id}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select project" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No Project</SelectItem>
                          {getClientProjects(timeEntryForm.client_id).map((project) => (
                            <SelectItem key={project.id} value={project.id}>
                              {project.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label>Date</Label>
                      <Input
                        type="date"
                        value={format(timeEntryForm.date, 'yyyy-MM-dd')}
                        onChange={(e) => setTimeEntryForm({...timeEntryForm, date: new Date(e.target.value)})}
                      />
                    </div>

                    <div>
                      <Label>Description</Label>
                      <Input
                        value={timeEntryForm.description}
                        onChange={(e) => setTimeEntryForm({...timeEntryForm, description: e.target.value})}
                        placeholder="Work description..."
                        required
                      />
                    </div>

                    <div>
                      <Label>Time</Label>
                      <div className="flex space-x-1">
                        <Input
                          type="number"
                          min="0"
                          max="23"
                          value={timeEntryForm.hours}
                          onChange={(e) => setTimeEntryForm({...timeEntryForm, hours: parseInt(e.target.value) || 0})}
                          placeholder="Hours"
                          className="w-16"
                        />
                        <Input
                          type="number"
                          min="0"
                          max="59"
                          value={timeEntryForm.minutes}
                          onChange={(e) => setTimeEntryForm({...timeEntryForm, minutes: parseInt(e.target.value) || 0})}
                          placeholder="Min"
                          className="w-16"
                        />
                      </div>
                    </div>

                    <div className="flex flex-col justify-end">
                      <div className="flex items-center space-x-2 mb-2">
                        <Checkbox
                          id="chargeable"
                          checked={timeEntryForm.is_chargeable}
                          onCheckedChange={(checked) => setTimeEntryForm({...timeEntryForm, is_chargeable: checked})}
                        />
                        <Label htmlFor="chargeable" className="text-sm">Chargeable</Label>
                      </div>
                      <Button type="submit" disabled={loading}>
                        {loading ? 'Adding...' : 'Add Entry'}
                      </Button>
                    </div>
                  </form>
                ) : (
                  // Admin Time Entry Form - Horizontal Layout
                  <form onSubmit={handleAdminTimeEntrySubmit} className="grid grid-cols-1 md:grid-cols-7 gap-4">
                    <div>
                      <Label>User</Label>
                      <Select
                        value={adminTimeEntryForm.user_id}
                        onValueChange={(value) => setAdminTimeEntryForm({...adminTimeEntryForm, user_id: value})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select user" />
                        </SelectTrigger>
                        <SelectContent>
                          {users.filter(u => u.role === 'lawyer').map((user) => (
                            <SelectItem key={user.id} value={user.id}>
                              {user.name} {user.surname}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label>Client</Label>
                      <Select
                        value={adminTimeEntryForm.client_id}
                        onValueChange={(value) => setAdminTimeEntryForm({...adminTimeEntryForm, client_id: value, project_id: ''})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select client" />
                        </SelectTrigger>
                        <SelectContent>
                          {clients.map((client) => (
                            <SelectItem key={client.id} value={client.id}>
                              {client.business_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label>Project</Label>
                      <Select
                        value={adminTimeEntryForm.project_id}
                        onValueChange={(value) => setAdminTimeEntryForm({...adminTimeEntryForm, project_id: value})}
                        disabled={!adminTimeEntryForm.client_id}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select project" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No Project</SelectItem>
                          {getClientProjects(adminTimeEntryForm.client_id).map((project) => (
                            <SelectItem key={project.id} value={project.id}>
                              {project.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label>Date</Label>
                      <Input
                        type="date"
                        value={format(adminTimeEntryForm.date, 'yyyy-MM-dd')}
                        onChange={(e) => setAdminTimeEntryForm({...adminTimeEntryForm, date: new Date(e.target.value)})}
                      />
                    </div>

                    <div>
                      <Label>Description</Label>
                      <Input
                        value={adminTimeEntryForm.description}
                        onChange={(e) => setAdminTimeEntryForm({...adminTimeEntryForm, description: e.target.value})}
                        placeholder="Work description..."
                        required
                      />
                    </div>

                    <div>
                      <Label>Time</Label>
                      <div className="flex space-x-1">
                        <Input
                          type="number"
                          min="0"
                          max="23"
                          value={adminTimeEntryForm.hours}
                          onChange={(e) => setAdminTimeEntryForm({...adminTimeEntryForm, hours: parseInt(e.target.value) || 0})}
                          placeholder="Hours"
                          className="w-16"
                        />
                        <Input
                          type="number"
                          min="0"
                          max="59"
                          value={adminTimeEntryForm.minutes}
                          onChange={(e) => setAdminTimeEntryForm({...adminTimeEntryForm, minutes: parseInt(e.target.value) || 0})}
                          placeholder="Min"
                          className="w-16"
                        />
                      </div>
                    </div>

                    <div className="flex flex-col justify-end">
                      <div className="flex items-center space-x-2 mb-2">
                        <Checkbox
                          id="admin-chargeable"
                          checked={adminTimeEntryForm.is_chargeable}
                          onCheckedChange={(checked) => setAdminTimeEntryForm({...adminTimeEntryForm, is_chargeable: checked})}
                        />
                        <Label htmlFor="admin-chargeable" className="text-sm">Chargeable</Label>
                      </div>
                      <Button type="submit" disabled={loading}>
                        {loading ? 'Adding...' : 'Add Entry'}
                      </Button>
                    </div>
                  </form>
                )}
              </CardContent>
            </Card>

            {/* Time Entries Table */}
            <Card>
              <CardHeader>
                <CardTitle>
                  {user?.role === 'lawyer' ? 'My Time Entries' : 'All Time Entries'}
                </CardTitle>
                <CardDescription>
                  {user?.role === 'lawyer' ? 'Your time entries' : 'Time entries from all users'}
                </CardDescription>
              </CardHeader>
              {user?.role !== 'lawyer' && (
                <div className="px-6 pb-4">
                  <Button 
                    onClick={handleMarkAsInvoiced}
                    disabled={selectedEntries.size === 0}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <Receipt className="h-4 w-4 mr-2" />
                    Mark as Invoiced ({selectedEntries.size})
                  </Button>
                </div>
              )}
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      {user?.role !== 'lawyer' && (
                        <TableHead className="w-12">
                          <Checkbox
                            checked={selectedEntries.size > 0 && 
                              getSortedTimeEntries(timeEntries, sortConfig)
                                .filter(entry => !entry.invoice_number)
                                .every(entry => selectedEntries.has(entry.id))
                            }
                            onCheckedChange={(checked) => 
                              handleSelectAll(
                                getSortedTimeEntries(timeEntries, sortConfig)
                                  .filter(entry => !entry.invoice_number), 
                                checked
                              )
                            }
                          />
                        </TableHead>
                      )}
                      <TableHead 
                        className="cursor-pointer hover:bg-slate-100"
                        onClick={() => handleSort('date')}
                      >
                        Date {sortConfig.key === 'date' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-slate-100"
                        onClick={() => handleSort('client')}
                      >
                        Client {sortConfig.key === 'client' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-slate-100"
                        onClick={() => handleSort('project')}
                      >
                        Project {sortConfig.key === 'project' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-slate-100"
                        onClick={() => handleSort('description')}
                      >
                        Description {sortConfig.key === 'description' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-slate-100"
                        onClick={() => handleSort('time')}
                      >
                        Time {sortConfig.key === 'time' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-slate-100"
                        onClick={() => handleSort('chargeable')}
                      >
                        Chargeable {sortConfig.key === 'chargeable' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-slate-100"
                        onClick={() => handleSort('cost')}
                      >
                        Cost {sortConfig.key === 'cost' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                      </TableHead>
                      {user?.role !== 'lawyer' && (
                        <TableHead>User</TableHead>
                      )}
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {getSortedTimeEntries(timeEntries, sortConfig).map((entry) => {
                      const client = clients.find(c => c.id === entry.client_id);
                      const project = projects.find(p => p.id === entry.project_id);
                      const entryUser = users.find(u => u.id === entry.user_id);
                      const isOwnEntry = entry.user_id === user.id;
                      const cost = calculateEntryCost(entry);
                      
                      return (
                        <TableRow key={entry.id} className={entry.invoice_number ? 'bg-slate-50' : ''}>
                          {user?.role !== 'lawyer' && (
                            <TableCell>
                              {!entry.invoice_number && (
                                <Checkbox
                                  checked={selectedEntries.has(entry.id)}
                                  onCheckedChange={(checked) => handleEntrySelection(entry.id, checked)}
                                />
                              )}
                            </TableCell>
                          )}
                          <TableCell>{format(new Date(entry.date), 'MMM dd, yyyy')}</TableCell>
                          <TableCell>{client?.business_name || 'Unknown Client'}</TableCell>
                          <TableCell>{project?.name || 'No Project'}</TableCell>
                          <TableCell className="max-w-xs truncate">{entry.description}</TableCell>
                          <TableCell>{formatTime(entry.hours, entry.minutes)}</TableCell>
                          <TableCell>
                            <Badge variant={entry.is_chargeable ? "default" : "secondary"}>
                              {entry.is_chargeable ? 'Yes' : 'No'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {cost > 0 ? (
                              <span className="font-medium">
                                {project?.hourly_rate_currency || project?.fixed_rate_currency || client?.hourly_rate_currency || 'USD'} {cost.toFixed(2)}
                              </span>
                            ) : (
                              <span className="text-slate-400">-</span>
                            )}
                          </TableCell>
                          {user?.role !== 'lawyer' && (
                            <TableCell>
                              {entryUser ? `${entryUser.name} ${entryUser.surname}` : 'Unknown User'}
                            </TableCell>
                          )}
                          <TableCell>
                            <div className="flex items-center space-x-2">
                              {(user?.role !== 'lawyer' || isOwnEntry) && (
                                <>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleEditTimeEntry(entry)}
                                  >
                                    <Edit className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDeleteTimeEntry(entry.id)}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Clients Tab - Admin Only */}
          {user?.role !== 'lawyer' && (
            <TabsContent value="clients" className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Add Client Form */}
                <Card>
                  <CardHeader>
                    <CardTitle>Add New Client</CardTitle>
                    <CardDescription>Create a new client profile</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleClientSubmit} className="space-y-4">
                      <div>
                        <Label>Business Name</Label>
                        <Input
                          value={clientForm.business_name}
                          onChange={(e) => setClientForm({...clientForm, business_name: e.target.value})}
                          required
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Street</Label>
                          <Input
                            value={clientForm.address.street}
                            onChange={(e) => setClientForm({
                              ...clientForm,
                              address: {...clientForm.address, street: e.target.value}
                            })}
                          />
                        </div>
                        <div>
                          <Label>Building</Label>
                          <Input
                            value={clientForm.address.building}
                            onChange={(e) => setClientForm({
                              ...clientForm,
                              address: {...clientForm.address, building: e.target.value}
                            })}
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <Label>Apartment</Label>
                          <Input
                            value={clientForm.address.apartment}
                            onChange={(e) => setClientForm({
                              ...clientForm,
                              address: {...clientForm.address, apartment: e.target.value}
                            })}
                          />
                        </div>
                        <div>
                          <Label>Postal Code</Label>
                          <Input
                            value={clientForm.address.postal_code}
                            onChange={(e) => setClientForm({
                              ...clientForm,
                              address: {...clientForm.address, postal_code: e.target.value}
                            })}
                          />
                        </div>
                        <div>
                          <Label>City</Label>
                          <Input
                            value={clientForm.address.city}
                            onChange={(e) => setClientForm({
                              ...clientForm,
                              address: {...clientForm.address, city: e.target.value}
                            })}
                          />
                        </div>
                      </div>

                      <div>
                        <Label>Country</Label>
                        <Input
                          value={clientForm.address.country}
                          onChange={(e) => setClientForm({
                            ...clientForm,
                            address: {...clientForm.address, country: e.target.value}
                          })}
                        />
                      </div>

                      <div>
                        <Label>VAT Number</Label>
                        <Input
                          value={clientForm.vat_number}
                          onChange={(e) => setClientForm({...clientForm, vat_number: e.target.value})}
                        />
                      </div>

                      <div>
                        <Label>Cooperation Type</Label>
                        <Select
                          value={clientForm.cooperation_type}
                          onValueChange={(value) => setClientForm({...clientForm, cooperation_type: value})}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="permanent">Permanent</SelectItem>
                            <SelectItem value="project">Project</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label>Settlement Method</Label>
                        <Select
                          value={clientForm.settlement_method}
                          onValueChange={(value) => setClientForm({...clientForm, settlement_method: value})}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="fixed_rate">Fixed Rate</SelectItem>
                            <SelectItem value="hourly_rate">Hourly Rate</SelectItem>
                            <SelectItem value="hourly_with_cap">Hourly with Cap</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {clientForm.settlement_method === 'hourly_rate' && (
                        <div>
                          <Label>Hourly Rate</Label>
                          <div className="flex space-x-2">
                            <Input
                              type="number"
                              step="0.01"
                              value={clientForm.hourly_rate}
                              onChange={(e) => setClientForm({...clientForm, hourly_rate: e.target.value})}
                              className="flex-1"
                            />
                            <Select
                              value={clientForm.hourly_rate_currency}
                              onValueChange={(value) => setClientForm({...clientForm, hourly_rate_currency: value})}
                            >
                              <SelectTrigger className="w-24">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="USD">USD</SelectItem>
                                <SelectItem value="EUR">EUR</SelectItem>
                                <SelectItem value="PLN">PLN</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      )}

                      {clientForm.settlement_method === 'fixed_rate' && (
                        <div>
                          <Label>Fixed Rate</Label>
                          <div className="flex space-x-2">
                            <Input
                              type="number"
                              step="0.01"
                              value={clientForm.fixed_rate}
                              onChange={(e) => setClientForm({...clientForm, fixed_rate: e.target.value})}
                              className="flex-1"
                            />
                            <Select
                              value={clientForm.fixed_rate_currency || clientForm.hourly_rate_currency}
                              onValueChange={(value) => setClientForm({...clientForm, fixed_rate_currency: value})}
                            >
                              <SelectTrigger className="w-24">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="USD">USD</SelectItem>
                                <SelectItem value="EUR">EUR</SelectItem>
                                <SelectItem value="PLN">PLN</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      )}

                      {clientForm.settlement_method === 'hourly_with_cap' && (
                        <>
                          <div>
                            <Label>Hourly Rate</Label>
                            <div className="flex space-x-2">
                              <Input
                                type="number"
                                step="0.01"
                                value={clientForm.hourly_rate}
                                onChange={(e) => setClientForm({...clientForm, hourly_rate: e.target.value})}
                                className="flex-1"
                              />
                              <Select
                                value={clientForm.hourly_rate_currency}
                                onValueChange={(value) => setClientForm({...clientForm, hourly_rate_currency: value})}
                              >
                                <SelectTrigger className="w-24">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="USD">USD</SelectItem>
                                  <SelectItem value="EUR">EUR</SelectItem>
                                  <SelectItem value="PLN">PLN</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          <div>
                            <Label>Cap Amount</Label>
                            <Input
                              type="number"
                              step="0.01"
                              value={clientForm.cap_amount}
                              onChange={(e) => setClientForm({...clientForm, cap_amount: e.target.value})}
                            />
                          </div>
                          <div>
                            <Label>Cap Period</Label>
                            <Select
                              value={clientForm.cap_period}
                              onValueChange={(value) => setClientForm({...clientForm, cap_period: value})}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select period" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="monthly">Monthly</SelectItem>
                                <SelectItem value="quarterly">Quarterly</SelectItem>
                                <SelectItem value="yearly">Yearly</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </>
                      )}

                      <Button type="submit" className="w-full" disabled={loading}>
                        <Plus className="h-4 w-4 mr-2" />
                        Add Client
                      </Button>
                    </form>
                  </CardContent>
                </Card>

                {/* Clients List */}
                <Card>
                  <CardHeader>
                    <CardTitle>Clients</CardTitle>
                    <CardDescription>Manage your clients</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {clients.map((client) => (
                        <div key={client.id} className="p-4 border rounded-lg">
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="font-medium">{client.business_name}</div>
                              <div className="text-sm text-slate-600">{client.vat_number}</div>
                              <div className="text-sm text-slate-500">
                                {client.cooperation_type} • {client.settlement_method}
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Badge variant={client.is_active !== false ? "default" : "secondary"}>
                                {client.is_active !== false ? 'Active' : 'Suspended'}
                              </Badge>
                              {client.hourly_rate && client.hourly_rate_currency && (
                                <Badge variant="outline">
                                  {client.hourly_rate_currency} {client.hourly_rate}/hr
                                </Badge>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleAddProjectToClient(client)}
                              >
                                <Plus className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleViewClient(client)}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEditClient(client)}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              {client.is_active !== false ? (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteItem(client, 'client')}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              ) : (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleRestoreItem(client, 'client')}
                                >
                                  <RotateCcw className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </div>
                          <div className="mt-2 text-sm text-slate-600">
                            {client.address.street} {client.address.building}, {client.address.city}
                          </div>
                          
                          {/* Projects Section */}
                          {getClientProjects(client.id).length > 0 && (
                            <div className="mt-3 pt-3 border-t border-slate-200">
                              <div className="text-sm font-medium text-slate-700 mb-2">Projects ({getClientProjects(client.id).length})</div>
                              <div className="space-y-1">
                                {getClientProjects(client.id).map((project) => (
                                  <div key={project.id} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                                    <div className="flex-1">
                                      <div className="text-sm font-medium">{project.name}</div>
                                      <div className="text-xs text-slate-500">{project.settlement_method}</div>
                                    </div>
                                    <div className="flex items-center space-x-1">
                                      {project.hourly_rate && project.hourly_rate_currency && (
                                        <Badge variant="outline" className="text-xs">
                                          {project.hourly_rate_currency} {project.hourly_rate}/hr
                                        </Badge>
                                      )}
                                      {project.fixed_rate && project.fixed_rate_currency && (
                                        <Badge variant="outline" className="text-xs">
                                          {project.fixed_rate_currency} {project.fixed_rate}
                                        </Badge>
                                      )}
                                      <Badge variant={project.is_active !== false ? "default" : "secondary"} className="text-xs">
                                        {project.is_active !== false ? 'Active' : 'Inactive'}
                                      </Badge>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleViewProject(project)}
                                        className="h-6 w-6 p-0"
                                      >
                                        <Eye className="h-3 w-3" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleEditProject(project)}
                                        className="h-6 w-6 p-0"
                                      >
                                        <Edit className="h-3 w-3" />
                                      </Button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          )}

          {/* External Costs Tab - Admin Only */}
          {user?.role !== 'lawyer' && (
            <TabsContent value="external-costs" className="space-y-6">
              {/* Filters */}
              <Card>
                <CardHeader>
                  <CardTitle>Filter External Costs</CardTitle>
                  <CardDescription>Filter external costs by client, project, or invoice status</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
                    <div>
                      <Label>Client</Label>
                      <Select
                        value={filters.client_id}
                        onValueChange={(value) => handleFilterChange('client_id', value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="All clients" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Clients</SelectItem>
                          {clientOptions.map((client) => (
                            <SelectItem key={`filter-client-${client.id}`} value={client.id}>
                              {client.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label>Project</Label>
                      <Select
                        value={filters.project_id}
                        onValueChange={(value) => handleFilterChange('project_id', value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="All projects" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Projects</SelectItem>
                          {filteredProjectOptions.map((project) => (
                            <SelectItem key={`filter-project-${project.id}`} value={project.id}>
                              {project.name} {project.clientName ? `(${project.clientName})` : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label>Invoiced Status</Label>
                      <Select
                        value={filters.invoiced}
                        onValueChange={(value) => handleFilterChange('invoiced', value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="All costs" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Costs</SelectItem>
                          <SelectItem value="invoiced">Invoiced Only</SelectItem>
                          <SelectItem value="not_invoiced">Not Invoiced</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div></div>
                    <div></div>
                    <div></div>
                  </div>
                  
                  <div className="flex space-x-2 mt-4">
                    <Button onClick={() => {
                      fetchFilteredTimeEntries();
                      if (user?.role !== 'lawyer') {
                        fetchFilteredExternalCosts();
                      }
                    }} variant="outline">
                      Apply Filters
                    </Button>
                    <Button onClick={clearFilters} variant="ghost">
                      Clear Filters
                    </Button>
                    <Button onClick={() => handleGenerateReport('external-costs')} className="bg-green-600 hover:bg-green-700">
                      <Download className="h-4 w-4 mr-2" />
                      Generate Report
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Add External Cost Form - Horizontal Layout */}
              <Card>
                <CardHeader>
                  <CardTitle>
                    {editingExternalCost ? 'Edit External Cost' : 'Add External Cost'}
                  </CardTitle>
                  <CardDescription>
                    {editingExternalCost ? 'Update external cost information' : 'Record external costs incurred for clients'}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleExternalCostSubmit} className="grid grid-cols-1 md:grid-cols-5 gap-4">
                    <div>
                      <Label>Client *</Label>
                      <Select
                        value={externalCostForm.client_id}
                        onValueChange={(value) => debouncedSetExternalCostForm({...externalCostForm, client_id: value, project_id: 'none'})}
                        required
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select a client" />
                        </SelectTrigger>
                        <SelectContent>
                          {clients.map((client) => (
                            <SelectItem key={client.id} value={client.id}>
                              {client.business_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label>Project (Optional)</Label>
                      <Select
                        value={externalCostForm.project_id}
                        onValueChange={(value) => debouncedSetExternalCostForm({...externalCostForm, project_id: value === 'none' ? '' : value})}
                        disabled={!externalCostForm.client_id}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select a project (optional)" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No Project</SelectItem>
                          {projectOptionsForClient(externalCostForm.client_id).map((project) => (
                            <SelectItem key={`form-project-${project.id}`} value={project.id}>
                              {project.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label>Amount *</Label>
                      <div className="flex space-x-1">
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={externalCostForm.amount}
                          onChange={(e) => setExternalCostForm({...externalCostForm, amount: e.target.value})}
                          placeholder="0.00"
                          required
                          className="flex-1"
                        />
                        <Select
                          value={externalCostForm.currency}
                          onValueChange={(value) => setExternalCostForm({...externalCostForm, currency: value})}
                        >
                          <SelectTrigger className="w-20">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {currencies.map((currency) => (
                              <SelectItem key={currency} value={currency}>
                                {currency}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div>
                      <Label>Description *</Label>
                      <Input
                        value={externalCostForm.description}
                        onChange={(e) => setExternalCostForm({...externalCostForm, description: e.target.value})}
                        placeholder="Cost description..."
                        required
                      />
                    </div>

                    <div className="flex flex-col justify-end">
                      <div className="flex space-x-2">
                        <Button type="submit" disabled={loading} className="flex-1">
                          {editingExternalCost ? 'Update Cost' : 'Add Cost'}
                        </Button>
                        {editingExternalCost && (
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => {
                              setEditingExternalCost(null);
                              setExternalCostForm({
                                client_id: '',
                                project_id: 'none',
                                description: '',
                                amount: '',
                                currency: 'USD'
                              });
                            }}
                          >
                            Cancel
                          </Button>
                        )}
                      </div>
                    </div>
                  </form>
                </CardContent>
              </Card>

              {/* External Costs Table */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>External Costs</CardTitle>
                      <CardDescription>
                        {externalCosts.length} external cost{externalCosts.length !== 1 ? 's' : ''} found
                      </CardDescription>
                    </div>
                    <div className="flex space-x-2">
                      {selectedExternalCosts.size > 0 && (
                        <Button
                          onClick={() => setIsExternalCostModalOpen(true)}
                          size="sm"
                        >
                          Mark as Invoiced ({selectedExternalCosts.size})
                        </Button>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="border rounded-md">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-12">
                            <Checkbox
                              checked={selectedExternalCosts.size === externalCosts.filter(cost => !cost.invoice_number).length && externalCosts.filter(cost => !cost.invoice_number).length > 0}
                              onCheckedChange={(checked) => {
                                const unInvoicedCosts = externalCosts.filter(cost => !cost.invoice_number);
                                if (checked) {
                                  setSelectedExternalCosts(new Set(unInvoicedCosts.map(cost => cost.id)));
                                } else {
                                  setSelectedExternalCosts(new Set());
                                }
                              }}
                            />
                          </TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead 
                            className="cursor-pointer select-none hover:bg-gray-50"
                            onClick={() => handleExternalCostsSort('client')}
                          >
                            <div className="flex items-center space-x-1">
                              <span>Client</span>
                              {externalCostsSortConfig.key === 'client' && (
                                externalCostsSortConfig.direction === 'asc' ? 
                                  <ChevronUp className="h-4 w-4" /> : 
                                  <ChevronDown className="h-4 w-4" />
                              )}
                            </div>
                          </TableHead>
                          <TableHead 
                            className="cursor-pointer select-none hover:bg-gray-50"
                            onClick={() => handleExternalCostsSort('project')}
                          >
                            <div className="flex items-center space-x-1">
                              <span>Project</span>
                              {externalCostsSortConfig.key === 'project' && (
                                externalCostsSortConfig.direction === 'asc' ? 
                                  <ChevronUp className="h-4 w-4" /> : 
                                  <ChevronDown className="h-4 w-4" />
                              )}
                            </div>
                          </TableHead>
                          <TableHead 
                            className="cursor-pointer select-none hover:bg-gray-50"
                            onClick={() => handleExternalCostsSort('description')}
                          >
                            <div className="flex items-center space-x-1">
                              <span>Description</span>
                              {externalCostsSortConfig.key === 'description' && (
                                externalCostsSortConfig.direction === 'asc' ? 
                                  <ChevronUp className="h-4 w-4" /> : 
                                  <ChevronDown className="h-4 w-4" />
                              )}
                            </div>
                          </TableHead>
                          <TableHead 
                            className="cursor-pointer select-none hover:bg-gray-50"
                            onClick={() => handleExternalCostsSort('amount')}
                          >
                            <div className="flex items-center space-x-1">
                              <span>Amount</span>
                              {externalCostsSortConfig.key === 'amount' && (
                                externalCostsSortConfig.direction === 'asc' ? 
                                  <ChevronUp className="h-4 w-4" /> : 
                                  <ChevronDown className="h-4 w-4" />
                              )}
                            </div>
                          </TableHead>
                          <TableHead 
                            className="cursor-pointer select-none hover:bg-gray-50"
                            onClick={() => handleExternalCostsSort('status')}
                          >
                            <div className="flex items-center space-x-1">
                              <span>Status</span>
                              {externalCostsSortConfig.key === 'status' && (
                                externalCostsSortConfig.direction === 'asc' ? 
                                  <ChevronUp className="h-4 w-4" /> : 
                                  <ChevronDown className="h-4 w-4" />
                              )}
                            </div>
                          </TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {getSortedExternalCosts(externalCosts, externalCostsSortConfig).map((cost) => {
                          const client = clients.find(c => c.id === cost.client_id);
                          const project = cost.project_id ? projects.find(p => p.id === cost.project_id) : null;
                          const isInvoiced = cost.invoice_number;
                          
                          return (
                            <TableRow key={cost.id} className={isInvoiced ? "opacity-60 bg-gray-50" : ""}>
                              <TableCell>
                                {!isInvoiced && (
                                  <Checkbox
                                    checked={selectedExternalCosts.has(cost.id)}
                                    onCheckedChange={(checked) => handleSelectExternalCost(cost.id, checked)}
                                  />
                                )}
                              </TableCell>
                              <TableCell>{format(new Date(cost.created_at), 'MMM dd, yyyy')}</TableCell>
                              <TableCell className="font-medium">
                                {client?.business_name || 'Unknown Client'}
                              </TableCell>
                              <TableCell>
                                {project?.name || 'No Project'}
                              </TableCell>
                              <TableCell className="max-w-xs truncate">
                                {cost.description}
                              </TableCell>
                              <TableCell className="font-medium">
                                {cost.amount.toFixed(2)} {cost.currency}
                              </TableCell>
                              <TableCell>
                                {isInvoiced ? (
                                  <Badge variant="secondary">Invoiced</Badge>
                                ) : (
                                  <Badge variant="outline">Not Invoiced</Badge>
                                )}
                              </TableCell>
                              <TableCell>
                                <div className="flex space-x-2">
                                  {!isInvoiced && (
                                    <>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handleEditExternalCost(cost)}
                                      >
                                        <Edit className="h-4 w-4" />
                                      </Button>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handleDeleteExternalCost(cost.id)}
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    </>
                                  )}
                                  {isInvoiced && (
                                    <Badge variant="secondary" className="text-xs">
                                      {cost.invoice_number}
                                    </Badge>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>

                  {externalCosts.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      No external costs found. Add your first external cost above.
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Users Tab - Admin Only */}
          {user?.role !== 'lawyer' && (
            <TabsContent value="users" className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Add User Form */}
                <Card>
                  <CardHeader>
                    <CardTitle>Add New User</CardTitle>
                    <CardDescription>Create a new user account</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleUserSubmit} className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>First Name</Label>
                          <Input
                            value={userForm.name}
                            onChange={(e) => setUserForm({...userForm, name: e.target.value})}
                            required
                          />
                        </div>
                        <div>
                          <Label>Last Name</Label>
                          <Input
                            value={userForm.surname}
                            onChange={(e) => setUserForm({...userForm, surname: e.target.value})}
                            required
                          />
                        </div>
                      </div>

                      <div>
                        <Label>Email</Label>
                        <Input
                          type="email"
                          value={userForm.email}
                          onChange={(e) => setUserForm({...userForm, email: e.target.value})}
                          required
                        />
                      </div>

                      <div>
                        <Label>Password</Label>
                        <Input
                          type="password"
                          value={userForm.password}
                          onChange={(e) => setUserForm({...userForm, password: e.target.value})}
                          required
                        />
                      </div>

                      <div>
                        <Label>Role</Label>
                        <Select
                          value={userForm.role}
                          onValueChange={(value) => setUserForm({...userForm, role: value})}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="lawyer">Lawyer</SelectItem>
                            <SelectItem value="admin_passive">Passive Administrator</SelectItem>
                            <SelectItem value="admin_modify">Modify Administrator</SelectItem>
                            <SelectItem value="admin_full">Full Administrator</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label>Position</Label>
                        <Select
                          value={userForm.position_id}
                          onValueChange={(value) => setUserForm({...userForm, position_id: value})}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select position" />
                          </SelectTrigger>
                          <SelectContent>
                            {positions.map((position) => (
                              <SelectItem key={position.id} value={position.id}>
                                {position.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <Button type="submit" className="w-full" disabled={loading}>
                        <Plus className="h-4 w-4 mr-2" />
                        Add User
                      </Button>
                    </form>
                  </CardContent>
                </Card>

                {/* Users List */}
                <Card>
                  <CardHeader>
                    <CardTitle>Users</CardTitle>
                    <CardDescription>Manage system users</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {users.map((user) => {
                        const position = positions.find(p => p.id === user.position_id);
                        return (
                          <div key={user.id} className="p-4 border rounded-lg">
                            <div className="flex items-center justify-between">
                              <div>
                                <div className="font-medium">{user.name} {user.surname}</div>
                                <div className="text-sm text-slate-600">{user.email}</div>
                                <div className="text-sm text-slate-500">
                                  {getRoleDisplayName(user.role)}
                                  {position && ` • ${position.name}`}
                                </div>
                              </div>
                              <div className="flex items-center space-x-2">
                                <Badge variant={user.is_active !== false ? "default" : "secondary"}>
                                  {user.is_active !== false ? 'Active' : 'Suspended'}
                                </Badge>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleViewUser(user)}
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleEditUser(user)}
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                {user.is_active !== false ? (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDeleteItem(user, 'user')}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                ) : (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleRestoreItem(user, 'user')}
                                  >
                                    <RotateCcw className="h-4 w-4" />
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          )}

          {/* Positions Tab - Admin Only */}
          {user?.role !== 'lawyer' && (
            <TabsContent value="positions" className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Add Position Form */}
                <Card>
                  <CardHeader>
                    <CardTitle>Add New Position</CardTitle>
                    <CardDescription>Create a new position type</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handlePositionSubmit} className="space-y-4">
                      <div>
                        <Label>Position Name</Label>
                        <Input
                          value={positionForm.name}
                          onChange={(e) => setPositionForm({...positionForm, name: e.target.value})}
                          required
                        />
                      </div>

                      <div>
                        <Label>Description</Label>
                        <Textarea
                          value={positionForm.description}
                          onChange={(e) => setPositionForm({...positionForm, description: e.target.value})}
                          placeholder="Position description..."
                        />
                      </div>

                      <div>
                        <Label>Default Hourly Rate</Label>
                        <div className="flex space-x-2">
                          <Input
                            type="number"
                            step="0.01"
                            value={positionForm.hourly_rate}
                            onChange={(e) => setPositionForm({...positionForm, hourly_rate: e.target.value})}
                            placeholder="0.00"
                            className="flex-1"
                          />
                          <Select
                            value={positionForm.currency}
                            onValueChange={(value) => setPositionForm({...positionForm, currency: value})}
                          >
                            <SelectTrigger className="w-24">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="USD">USD</SelectItem>
                              <SelectItem value="EUR">EUR</SelectItem>
                              <SelectItem value="PLN">PLN</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <Button type="submit" className="w-full" disabled={loading}>
                        <Plus className="h-4 w-4 mr-2" />
                        Add Position
                      </Button>
                    </form>
                  </CardContent>
                </Card>

                {/* Positions List */}
                <Card>
                  <CardHeader>
                    <CardTitle>Positions</CardTitle>
                    <CardDescription>Available positions in the firm</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {positions.map((position) => (
                        <div key={position.id} className="p-4 border rounded-lg">
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="font-medium">{position.name}</div>
                              {position.description && (
                                <div className="text-sm text-slate-600">{position.description}</div>
                              )}
                            </div>
                            <div className="flex items-center space-x-2">
                              <Badge variant={position.is_active !== false ? "default" : "secondary"}>
                                {position.is_active !== false ? 'Active' : 'Suspended'}
                              </Badge>
                              {position.hourly_rate && (
                                <Badge variant="outline">
                                  {position.currency || 'USD'} {position.hourly_rate}/hr
                                </Badge>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleViewPosition(position)}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEditPosition(position)}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              {position.is_active !== false ? (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteItem(position, 'position')}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              ) : (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleRestoreItem(position, 'position')}
                                >
                                  <RotateCcw className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          )}

          {/* Invoices Tab Content */}
          <TabsContent value="invoices" className="space-y-6">
            {user?.role !== 'lawyer' ? (
              <Card>
                <CardHeader>
                  <CardTitle>Invoice Management</CardTitle>
                  <CardDescription>
                    View and manage all invoices with their associated time entries
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {invoices.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <Receipt className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>No invoices found</p>
                      <p className="text-sm">Time entries marked as invoiced will appear here</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead 
                              className="cursor-pointer select-none hover:bg-gray-50"
                              onClick={() => handleInvoicesSort('invoice_number')}
                            >
                              <div className="flex items-center space-x-1">
                                <span>Invoice Number</span>
                                {invoicesSortConfig.key === 'invoice_number' && (
                                  invoicesSortConfig.direction === 'asc' ? 
                                    <ChevronUp className="h-4 w-4" /> : 
                                    <ChevronDown className="h-4 w-4" />
                                )}
                              </div>
                            </TableHead>
                            <TableHead 
                              className="cursor-pointer select-none hover:bg-gray-50"
                              onClick={() => handleInvoicesSort('created_at')}
                            >
                              <div className="flex items-center space-x-1">
                                <span>Created Date</span>
                                {invoicesSortConfig.key === 'created_at' && (
                                  invoicesSortConfig.direction === 'asc' ? 
                                    <ChevronUp className="h-4 w-4" /> : 
                                    <ChevronDown className="h-4 w-4" />
                                )}
                              </div>
                            </TableHead>
                            <TableHead 
                              className="cursor-pointer select-none hover:bg-gray-50"
                              onClick={() => handleInvoicesSort('total_entries')}
                            >
                              <div className="flex items-center space-x-1">
                                <span>Time Entries</span>
                                {invoicesSortConfig.key === 'total_entries' && (
                                  invoicesSortConfig.direction === 'asc' ? 
                                    <ChevronUp className="h-4 w-4" /> : 
                                    <ChevronDown className="h-4 w-4" />
                                )}
                              </div>
                            </TableHead>
                            <TableHead 
                              className="cursor-pointer select-none hover:bg-gray-50"
                              onClick={() => handleInvoicesSort('total_hours')}
                            >
                              <div className="flex items-center space-x-1">
                                <span>Total Hours</span>
                                {invoicesSortConfig.key === 'total_hours' && (
                                  invoicesSortConfig.direction === 'asc' ? 
                                    <ChevronUp className="h-4 w-4" /> : 
                                    <ChevronDown className="h-4 w-4" />
                                )}
                              </div>
                            </TableHead>
                            <TableHead 
                              className="cursor-pointer select-none hover:bg-gray-50"
                              onClick={() => handleInvoicesSort('total_costs')}
                            >
                              <div className="flex items-center space-x-1">
                                <span>External Costs</span>
                                {invoicesSortConfig.key === 'total_costs' && (
                                  invoicesSortConfig.direction === 'asc' ? 
                                    <ChevronUp className="h-4 w-4" /> : 
                                    <ChevronDown className="h-4 w-4" />
                                )}
                              </div>
                            </TableHead>
                            <TableHead 
                              className="cursor-pointer select-none hover:bg-gray-50"
                              onClick={() => handleInvoicesSort('total_amount')}
                            >
                              <div className="flex items-center space-x-1">
                                <span>Total Amount</span>
                                {invoicesSortConfig.key === 'total_amount' && (
                                  invoicesSortConfig.direction === 'asc' ? 
                                    <ChevronUp className="h-4 w-4" /> : 
                                    <ChevronDown className="h-4 w-4" />
                                )}
                              </div>
                            </TableHead>
                            <TableHead>Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {getSortedInvoices(invoices, invoicesSortConfig).map((invoice) => (
                            <TableRow key={invoice.invoice_number}>
                              <TableCell className="font-medium">
                                {invoice.invoice_number}
                              </TableCell>
                              <TableCell>
                                {format(new Date(invoice.created_at), 'MMM dd, yyyy HH:mm')}
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center">
                                  <Clock className="h-4 w-4 mr-1" />
                                  {invoice.total_entries}
                                </div>
                              </TableCell>
                              <TableCell>{invoice.total_hours}h</TableCell>
                              <TableCell>
                                <div className="flex items-center">
                                  <Receipt className="h-4 w-4 mr-1" />
                                  {invoice.total_costs || 0}
                                </div>
                              </TableCell>
                              <TableCell>
                                {invoice.total_amount ? invoice.total_amount.toFixed(2) : '0.00'}
                              </TableCell>
                              <TableCell>
                                <div className="flex space-x-2">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleInvoiceDetails(invoice.invoice_number)}
                                  >
                                    <Eye className="h-4 w-4 mr-1" />
                                    View Details
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleGenerateInvoiceReport(invoice.invoice_number)}
                                  >
                                    <Download className="h-4 w-4 mr-1" />
                                    Generate Report
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleRedoInvoice(invoice.invoice_number)}
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  >
                                    <RotateCcw className="h-4 w-4 mr-1" />
                                    Redo Invoice
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="py-8">
                  <div className="text-center text-slate-500">
                    <Receipt className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>Access denied</p>
                    <p className="text-sm">Only administrators can view invoices</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Settings Tab - Admin Only */}
          {user?.role !== 'lawyer' && (
            <TabsContent value="settings" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Law Firm Settings</CardTitle>
                  <CardDescription>Configure your law firm information</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleLawFirmSettingsSubmit} className="space-y-4">
                    <div>
                      <Label>Business Name</Label>
                      <Input
                        value={lawFirmForm.business_name}
                        onChange={(e) => setLawFirmForm({...lawFirmForm, business_name: e.target.value})}
                        placeholder="Law Firm Name"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Street</Label>
                        <Input
                          value={lawFirmForm.address.street}
                          onChange={(e) => setLawFirmForm({
                            ...lawFirmForm,
                            address: {...lawFirmForm.address, street: e.target.value}
                          })}
                        />
                      </div>
                      <div>
                        <Label>Building</Label>
                        <Input
                          value={lawFirmForm.address.building}
                          onChange={(e) => setLawFirmForm({
                            ...lawFirmForm,
                            address: {...lawFirmForm.address, building: e.target.value}
                          })}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label>Apartment</Label>
                        <Input
                          value={lawFirmForm.address.apartment}
                          onChange={(e) => setLawFirmForm({
                            ...lawFirmForm,
                            address: {...lawFirmForm.address, apartment: e.target.value}
                          })}
                        />
                      </div>
                      <div>
                        <Label>Postal Code</Label>
                        <Input
                          value={lawFirmForm.address.postal_code}
                          onChange={(e) => setLawFirmForm({
                            ...lawFirmForm,
                            address: {...lawFirmForm.address, postal_code: e.target.value}
                          })}
                        />
                      </div>
                      <div>
                        <Label>City</Label>
                        <Input
                          value={lawFirmForm.address.city}
                          onChange={(e) => setLawFirmForm({
                            ...lawFirmForm,
                            address: {...lawFirmForm.address, city: e.target.value}
                          })}
                        />
                      </div>
                    </div>

                    <div>
                      <Label>Country</Label>
                      <Input
                        value={lawFirmForm.address.country}
                        onChange={(e) => setLawFirmForm({
                          ...lawFirmForm,
                          address: {...lawFirmForm.address, country: e.target.value}
                        })}
                      />
                    </div>

                    <div>
                      <Label>VAT Number</Label>
                      <Input
                        value={lawFirmForm.vat_number}
                        onChange={(e) => setLawFirmForm({...lawFirmForm, vat_number: e.target.value})}
                      />
                    </div>

                    <Button type="submit" className="w-full" disabled={loading}>
                      Save Settings
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* User Settings Tab - Lawyer Only */}
          {user?.role === 'lawyer' && (
            <TabsContent value="user-settings" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Account Settings</CardTitle>
                  <CardDescription>Update your account credentials</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleCredentialsSubmit} className="space-y-4">
                    <div>
                      <Label>Current Password</Label>
                      <Input
                        type="password"
                        value={credentialsForm.current_password}
                        onChange={(e) => setCredentialsForm({...credentialsForm, current_password: e.target.value})}
                        placeholder="Enter current password"
                        required
                      />
                    </div>

                    <div>
                      <Label>New Email (optional)</Label>
                      <Input
                        type="email"
                        value={credentialsForm.new_email}
                        onChange={(e) => setCredentialsForm({...credentialsForm, new_email: e.target.value})}
                        placeholder="Enter new email address"
                      />
                    </div>

                    <div>
                      <Label>New Password (optional)</Label>
                      <Input
                        type="password"
                        value={credentialsForm.new_password}
                        onChange={(e) => setCredentialsForm({...credentialsForm, new_password: e.target.value})}
                        placeholder="Enter new password"
                      />
                    </div>

                    <div>
                      <Label>Confirm New Password</Label>
                      <Input
                        type="password"
                        value={credentialsForm.confirm_password}
                        onChange={(e) => setCredentialsForm({...credentialsForm, confirm_password: e.target.value})}
                        placeholder="Confirm new password"
                      />
                    </div>

                    <Button type="submit" className="w-full" disabled={loading}>
                      Update Credentials
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Tasks Tab - Lawyer Only */}
          {user?.role === 'lawyer' && (
            <TabsContent value="tasks" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>Task Management</span>
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowCompletedTasks(!showCompletedTasks)}
                      >
                        {showCompletedTasks ? 'Hide Completed' : 'Show Completed'}
                      </Button>
                      <Button
                        onClick={() => setIsTaskModalOpen(true)}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add Task
                      </Button>
                    </div>
                  </CardTitle>
                  <CardDescription>Manage your tasks and priorities</CardDescription>
                </CardHeader>
                <CardContent>
                  {/* Task Filters */}
                  <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-7 gap-4 mb-6">
                    <div>
                      <Label>Client</Label>
                      <Select
                        value={taskFilters.client_id}
                        onValueChange={(value) => setTaskFilters({...taskFilters, client_id: value, project_id: 'all'})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="All clients" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Clients</SelectItem>
                          {clientOptions.map((client) => (
                            <SelectItem key={client.id} value={client.id}>
                              {client.name}
                            </SelectItem>
                          ))}
                          {clientOptions.length === 0 && (
                            <SelectItem value="no-clients" disabled>No clients available</SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label>Project</Label>
                      <Select
                        value={taskFilters.project_id}
                        onValueChange={(value) => setTaskFilters({...taskFilters, project_id: value})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="All projects" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Projects</SelectItem>
                          {(() => {
                            let availableProjects = [];
                            
                            if (taskFilters.client_id && taskFilters.client_id !== 'all') {
                              // Show projects assigned to selected client
                              availableProjects = projects.filter(p => 
                                p.client_id === taskFilters.client_id && p.is_active !== false
                              );
                            } else {
                              // Show projects that are actually used in tasks
                              const projectsUsedInTasks = [...new Set(tasks.filter(t => t.project_id).map(t => t.project_id))];
                              availableProjects = projects.filter(p => 
                                projectsUsedInTasks.includes(p.id) && p.is_active !== false
                              );
                            }
                            
                            return availableProjects.map((project) => (
                              <SelectItem key={project.id} value={project.id}>
                                {project.name}
                              </SelectItem>
                            ));
                          })()}
                          {(() => {
                            let availableProjectsCount = 0;
                            
                            if (taskFilters.client_id && taskFilters.client_id !== 'all') {
                              availableProjectsCount = projects.filter(p => 
                                p.client_id === taskFilters.client_id && p.is_active !== false
                              ).length;
                            } else {
                              const projectsUsedInTasks = [...new Set(tasks.filter(t => t.project_id).map(t => t.project_id))];
                              availableProjectsCount = projects.filter(p => 
                                projectsUsedInTasks.includes(p.id) && p.is_active !== false
                              ).length;
                            }
                            
                            return availableProjectsCount === 0 && (
                              <SelectItem value="no-projects" disabled>
                                {taskFilters.client_id && taskFilters.client_id !== 'all' 
                                  ? "No projects for selected client" 
                                  : "No projects used in tasks"}
                              </SelectItem>
                            );
                          })()}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label>Status</Label>
                      <Select
                        value={taskFilters.is_completed}
                        onValueChange={(value) => setTaskFilters({...taskFilters, is_completed: value})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="All tasks" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Tasks</SelectItem>
                          <SelectItem value="false">Incomplete</SelectItem>
                          <SelectItem value="true">Completed</SelectItem>
                          <SelectItem value="suspended">Suspended</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label>Sort By</Label>
                      <Select
                        value={taskFilters.sort_by}
                        onValueChange={(value) => setTaskFilters({...taskFilters, sort_by: value})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Sort by" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="priority">Priority</SelectItem>
                          <SelectItem value="deadline">Due Date</SelectItem>
                          <SelectItem value="client">Client</SelectItem>
                          <SelectItem value="project">Project</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label>Start Date</Label>
                      <Input
                        type="date"
                        value={taskFilters.start_date}
                        onChange={(e) => setTaskFilters({...taskFilters, start_date: e.target.value})}
                        placeholder="Start date"
                      />
                    </div>

                    <div>
                      <Label>End Date</Label>
                      <Input
                        type="date"
                        value={taskFilters.end_date}
                        onChange={(e) => setTaskFilters({...taskFilters, end_date: e.target.value})}
                        placeholder="End date"
                      />
                    </div>

                    <div className="flex items-end">
                      <Button onClick={fetchTasks} variant="outline" className="w-full">
                        Apply Filters
                      </Button>
                    </div>
                  </div>

                  <div className="flex justify-between items-center mb-4">
                    <div className="flex space-x-2">
                      <Button onClick={() => {
                        setTaskFilters({
                          client_id: 'all',
                          project_id: 'all',
                          deadline: 'all',
                          is_completed: 'all',
                          start_date: '',
                          end_date: '',
                          sort_by: 'priority',
                          sort_order: 'desc'
                        });
                        fetchTasks();
                      }} variant="ghost" size="sm">
                        Clear Filters
                      </Button>
                      <Button 
                        onClick={generateTasksReport} 
                        variant="outline"
                        size="sm"
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Export XLS
                      </Button>
                    </div>
                  </div>

                  {/* Task List */}
                  <div className="space-y-3">
                    {getFilteredTasks().length === 0 ? (
                      <div className="text-center py-8 text-slate-500">
                        <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                        <p>No tasks found</p>
                        <p className="text-sm">Create your first task to get started</p>
                      </div>
                    ) : (
                      getFilteredTasks().map((task, index) => (
                        <div
                          key={task.id}
                          className={`border rounded-lg p-4 ${
                            task.is_completed 
                              ? 'bg-green-50 border-green-200' 
                              : task.is_suspended
                              ? 'bg-purple-50 border-purple-200'
                              : 'bg-white border-slate-200 hover:border-slate-300'
                          } transition-colors`}
                          draggable={!task.is_completed}
                          onDragStart={(e) => setDraggedTask(task)}
                          onDragOver={(e) => e.preventDefault()}
                          onDrop={(e) => {
                            e.preventDefault();
                            if (draggedTask && draggedTask.id !== task.id && !task.is_completed) {
                              const currentTasks = getFilteredTasks().filter(t => !t.is_completed);
                              const draggedIndex = currentTasks.findIndex(t => t.id === draggedTask.id);
                              const targetIndex = currentTasks.findIndex(t => t.id === task.id);
                              
                              if (draggedIndex !== targetIndex) {
                                const newOrder = [...currentTasks];
                                const [removed] = newOrder.splice(draggedIndex, 1);
                                newOrder.splice(targetIndex, 0, removed);
                                handleTaskReorder(newOrder, draggedTask, task);
                              }
                            }
                            setDraggedTask(null);
                          }}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex items-start space-x-3 flex-1">
                              {!task.is_completed && (
                                <GripVertical className="h-5 w-5 text-slate-400 mt-1 cursor-move" />
                              )}
                              
                              <div className="flex-1">
                                <div className="flex items-center space-x-2 mb-2">
                                  {getTaskIcon(task)}
                                  <span className={`text-sm font-medium ${
                                    task.is_completed ? 'text-green-700' : task.is_suspended ? 'text-purple-700' : 'text-slate-900'
                                  }`}>
                                    {task.client_name}
                                  </span>
                                  {task.project_name && (
                                    <span className="text-xs px-2 py-1 rounded-full bg-indigo-100 text-indigo-700">
                                      {task.project_name}
                                    </span>
                                  )}
                                  <span className={`text-xs px-2 py-1 rounded-full ${
                                    task.is_completed 
                                      ? 'bg-green-100 text-green-700'
                                      : task.is_suspended
                                      ? 'bg-purple-100 text-purple-700'
                                      : getTaskStatusText(task) === 'Overdue'
                                      ? 'bg-red-100 text-red-700'
                                      : getTaskStatusText(task) === 'Due Today'
                                      ? 'bg-orange-100 text-orange-700'
                                      : getTaskStatusText(task) === 'Due Soon'
                                      ? 'bg-yellow-100 text-yellow-700'
                                      : 'bg-blue-100 text-blue-700'
                                  }`}>
                                    {task.is_suspended ? 'Suspended' : getTaskStatusText(task)}
                                  </span>
                                </div>

                                <p className={`font-medium ${
                                  task.is_completed ? 'text-green-700' : 'text-slate-900'
                                }`}>
                                  {task.description}
                                </p>

                                {task.comments && (
                                  <p className="text-sm text-slate-600 mt-1">
                                    <strong>Comments:</strong> {task.comments}
                                  </p>
                                )}

                                <div className="flex items-center space-x-4 mt-2 text-sm text-slate-500">
                                  <span>
                                    <strong>Deadline:</strong> {formatDateWithDay(task.deadline)}
                                  </span>
                                  {task.completed_at && (
                                    <span>
                                      <strong>Completed:</strong> {format(new Date(task.completed_at), 'MMM dd, yyyy')}
                                    </span>
                                  )}
                                  {task.is_suspended && task.suspended_at && (
                                    <span>
                                      <strong>Suspended:</strong> {format(new Date(task.suspended_at), 'MMM dd, yyyy')}
                                    </span>
                                  )}
                                </div>

                                {task.completion_comments && (
                                  <p className="text-sm text-green-600 mt-1">
                                    <strong>Completion Notes:</strong> {task.completion_comments}
                                  </p>
                                )}

                                {task.is_suspended && task.suspend_comments && (
                                  <p className="text-sm text-purple-600 mt-1">
                                    <strong>Suspend Notes:</strong> {task.suspend_comments}
                                  </p>
                                )}

                                {task.is_suspended && task.suspend_due_date && (
                                  <p className="text-sm text-purple-600 mt-1">
                                    <strong>Resume By:</strong> {formatDateWithDay(task.suspend_due_date)}
                                  </p>
                                )}
                              </div>
                            </div>

                            <div className="flex items-center space-x-2">
                              {task.is_completed ? (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleUncompleteTask(task.id)}
                                  className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                                >
                                  <RotateCcw className="h-4 w-4" />
                                </Button>
                              ) : task.is_suspended ? (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleResumeTask(task.id)}
                                  className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                >
                                  <Play className="h-4 w-4" />
                                </Button>
                              ) : (
                                <>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleCompleteTask(task)}
                                    className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                  >
                                    <Check className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleSuspendTask(task)}
                                    className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                                  >
                                    <Pause className="h-4 w-4" />
                                  </Button>
                                </>
                              )}
                              
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleEditTask(task)}
                                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDeleteTask(task.id)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>
      </div>

      {/* Edit Modals */}
      
      {/* Edit Client Modal */}
      <Dialog open={isEditModalOpen && editingClient} onOpenChange={() => {
        setIsEditModalOpen(false);
        setEditingClient(null);
      }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto bg-white">
          <DialogHeader>
            <DialogTitle>Edit Client</DialogTitle>
            <DialogDescription>Update client information</DialogDescription>
          </DialogHeader>
          {editingClient && (
            <form onSubmit={handleUpdateClient} className="space-y-4">
              <div>
                <Label>Business Name</Label>
                <Input
                  value={editingClient.business_name}
                  onChange={(e) => setEditingClient({...editingClient, business_name: e.target.value})}
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Street</Label>
                  <Input
                    value={editingClient.address?.street || ''}
                    onChange={(e) => setEditingClient({
                      ...editingClient,
                      address: {...editingClient.address, street: e.target.value}
                    })}
                  />
                </div>
                <div>
                  <Label>Building</Label>
                  <Input
                    value={editingClient.address?.building || ''}
                    onChange={(e) => setEditingClient({
                      ...editingClient,
                      address: {...editingClient.address, building: e.target.value}
                    })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Apartment</Label>
                  <Input
                    value={editingClient.address?.apartment || ''}
                    onChange={(e) => setEditingClient({
                      ...editingClient,
                      address: {...editingClient.address, apartment: e.target.value}
                    })}
                  />
                </div>
                <div>
                  <Label>Postal Code</Label>
                  <Input
                    value={editingClient.address?.postal_code || ''}
                    onChange={(e) => setEditingClient({
                      ...editingClient,
                      address: {...editingClient.address, postal_code: e.target.value}
                    })}
                  />
                </div>
                <div>
                  <Label>City</Label>
                  <Input
                    value={editingClient.address?.city || ''}
                    onChange={(e) => setEditingClient({
                      ...editingClient,
                      address: {...editingClient.address, city: e.target.value}
                    })}
                  />
                </div>
              </div>

              <div>
                <Label>Country</Label>
                <Input
                  value={editingClient.address?.country || ''}
                  onChange={(e) => setEditingClient({
                    ...editingClient,
                    address: {...editingClient.address, country: e.target.value}
                  })}
                />
              </div>

              <div>
                <Label>VAT Number</Label>
                <Input
                  value={editingClient.vat_number}
                  onChange={(e) => setEditingClient({...editingClient, vat_number: e.target.value})}
                />
              </div>

              <div>
                <Label>Cooperation Type</Label>
                <Select
                  value={editingClient.cooperation_type}
                  onValueChange={(value) => setEditingClient({...editingClient, cooperation_type: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="permanent">Permanent</SelectItem>
                    <SelectItem value="project">Project</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Settlement Method</Label>
                <Select
                  value={editingClient.settlement_method}
                  onValueChange={(value) => setEditingClient({...editingClient, settlement_method: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fixed_rate">Fixed Rate</SelectItem>
                    <SelectItem value="hourly_rate">Hourly Rate</SelectItem>
                    <SelectItem value="hourly_with_cap">Hourly with Cap</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {editingClient.settlement_method === 'hourly_rate' && (
                <div>
                  <Label>Hourly Rate</Label>
                  <div className="flex space-x-2">
                    <Input
                      type="number"
                      step="0.01"
                      value={editingClient.hourly_rate}
                      onChange={(e) => setEditingClient({...editingClient, hourly_rate: e.target.value})}
                      className="flex-1"
                    />
                    <Select
                      value={editingClient.hourly_rate_currency}
                      onValueChange={(value) => setEditingClient({...editingClient, hourly_rate_currency: value})}
                    >
                      <SelectTrigger className="w-24">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="USD">USD</SelectItem>
                        <SelectItem value="EUR">EUR</SelectItem>
                        <SelectItem value="PLN">PLN</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {editingClient.settlement_method === 'fixed_rate' && (
                <div>
                  <Label>Fixed Rate</Label>
                  <div className="flex space-x-2">
                    <Input
                      type="number"
                      step="0.01"
                      value={editingClient.fixed_rate}
                      onChange={(e) => setEditingClient({...editingClient, fixed_rate: e.target.value})}
                      className="flex-1"
                    />
                    <Select
                      value={editingClient.fixed_rate_currency || editingClient.hourly_rate_currency}
                      onValueChange={(value) => setEditingClient({...editingClient, fixed_rate_currency: value})}
                    >
                      <SelectTrigger className="w-24">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="USD">USD</SelectItem>
                        <SelectItem value="EUR">EUR</SelectItem>
                        <SelectItem value="PLN">PLN</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {editingClient.settlement_method === 'hourly_with_cap' && (
                <>
                  <div>
                    <Label>Hourly Rate</Label>
                    <div className="flex space-x-2">
                      <Input
                        type="number"
                        step="0.01"
                        value={editingClient.hourly_rate}
                        onChange={(e) => setEditingClient({...editingClient, hourly_rate: e.target.value})}
                        className="flex-1"
                      />
                      <Select
                        value={editingClient.hourly_rate_currency}
                        onValueChange={(value) => setEditingClient({...editingClient, hourly_rate_currency: value})}
                      >
                        <SelectTrigger className="w-24">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="USD">USD</SelectItem>
                          <SelectItem value="EUR">EUR</SelectItem>
                          <SelectItem value="PLN">PLN</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label>Cap Amount</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={editingClient.cap_amount}
                      onChange={(e) => setEditingClient({...editingClient, cap_amount: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Cap Period</Label>
                    <Select
                      value={editingClient.cap_period}
                      onValueChange={(value) => setEditingClient({...editingClient, cap_period: value})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select period" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="quarterly">Quarterly</SelectItem>
                        <SelectItem value="yearly">Yearly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}

              <div className="flex space-x-2">
                <Button type="submit" disabled={loading}>
                  {loading ? 'Updating...' : 'Update Client'}
                </Button>
                <Button type="button" variant="outline" onClick={() => {
                  setIsEditModalOpen(false);
                  setEditingClient(null);
                }}>
                  Cancel
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit User Modal */}
      <Dialog open={isEditModalOpen && editingUser} onOpenChange={() => {
        setIsEditModalOpen(false);
        setEditingUser(null);
      }}>
        <DialogContent className="max-w-lg bg-white">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>Update user information</DialogDescription>
          </DialogHeader>
          {editingUser && (
            <form onSubmit={handleUpdateUser} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>First Name</Label>
                  <Input
                    value={editingUser.name}
                    onChange={(e) => setEditingUser({...editingUser, name: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label>Last Name</Label>
                  <Input
                    value={editingUser.surname}
                    onChange={(e) => setEditingUser({...editingUser, surname: e.target.value})}
                    required
                  />
                </div>
              </div>

              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={editingUser.email}
                  onChange={(e) => setEditingUser({...editingUser, email: e.target.value})}
                  required
                />
              </div>

              <div>
                <Label>Password (leave empty to keep current)</Label>
                <Input
                  type="password"
                  value={editingUser.password}
                  onChange={(e) => setEditingUser({...editingUser, password: e.target.value})}
                  placeholder="Leave empty to keep current password"
                />
              </div>

              <div>
                <Label>Role</Label>
                <Select
                  value={editingUser.role}
                  onValueChange={(value) => setEditingUser({...editingUser, role: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="lawyer">Lawyer</SelectItem>
                    <SelectItem value="admin_passive">Passive Administrator</SelectItem>
                    <SelectItem value="admin_modify">Modify Administrator</SelectItem>
                    <SelectItem value="admin_full">Full Administrator</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Position</Label>
                <Select
                  value={editingUser.position_id || "none"}
                  onValueChange={(value) => setEditingUser({...editingUser, position_id: value === "none" ? "" : value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select position" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Position</SelectItem>
                    {positions.map((position) => (
                      <SelectItem key={position.id} value={position.id}>
                        {position.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex space-x-2">
                <Button type="submit" disabled={loading}>
                  {loading ? 'Updating...' : 'Update User'}
                </Button>
                <Button type="button" variant="outline" onClick={() => {
                  setIsEditModalOpen(false);
                  setEditingUser(null);
                }}>
                  Cancel
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Position Modal */}
      <Dialog open={isEditModalOpen && editingPosition} onOpenChange={() => {
        setIsEditModalOpen(false);
        setEditingPosition(null);
      }}>
        <DialogContent className="max-w-lg bg-white">
          <DialogHeader>
            <DialogTitle>Edit Position</DialogTitle>
            <DialogDescription>Update position information</DialogDescription>
          </DialogHeader>
          {editingPosition && (
            <form onSubmit={handleUpdatePosition} className="space-y-4">
              <div>
                <Label>Position Name</Label>
                <Input
                  value={editingPosition.name}
                  onChange={(e) => setEditingPosition({...editingPosition, name: e.target.value})}
                  required
                />
              </div>

              <div>
                <Label>Description</Label>
                <Textarea
                  value={editingPosition.description}
                  onChange={(e) => setEditingPosition({...editingPosition, description: e.target.value})}
                  placeholder="Position description..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Default Hourly Rate</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editingPosition.hourly_rate}
                    onChange={(e) => setEditingPosition({...editingPosition, hourly_rate: e.target.value})}
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <Label>Currency</Label>
                  <Select
                    value={editingPosition.currency}
                    onValueChange={(value) => setEditingPosition({...editingPosition, currency: value})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {currencies.map((currency) => (
                        <SelectItem key={currency} value={currency}>
                          {currency}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex space-x-2">
                <Button type="submit" disabled={loading}>
                  {loading ? 'Updating...' : 'Update Position'}
                </Button>
                <Button type="button" variant="outline" onClick={() => {
                  setIsEditModalOpen(false);
                  setEditingPosition(null);
                }}>
                  Cancel
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Time Entry Modal */}
      <Dialog open={isEditModalOpen && editingTimeEntry} onOpenChange={() => {
        setIsEditModalOpen(false);
        setEditingTimeEntry(null);
      }}>
        <DialogContent className="max-w-lg bg-white">
          <DialogHeader>
            <DialogTitle>Edit Time Entry</DialogTitle>
            <DialogDescription>Update time entry information</DialogDescription>
          </DialogHeader>
          {editingTimeEntry && (
            <form onSubmit={handleUpdateTimeEntry} className="space-y-4">
              <div>
                <Label>Client</Label>
                <Select
                  value={editingTimeEntry.client_id}
                  onValueChange={(value) => setEditingTimeEntry({...editingTimeEntry, client_id: value, project_id: ''})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select client" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((client) => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.business_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {editingTimeEntry.client_id && (
                <div>
                  <Label>Project (Optional)</Label>
                  <Select
                    value={editingTimeEntry.project_id}
                    onValueChange={(value) => setEditingTimeEntry({...editingTimeEntry, project_id: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select project" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Project</SelectItem>
                      {getClientProjects(editingTimeEntry.client_id).map((project) => (
                        <SelectItem key={project.id} value={project.id}>
                          {project.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div>
                <Label>Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {format(editingTimeEntry.date, 'PPP')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={editingTimeEntry.date}
                      onSelect={(date) => setEditingTimeEntry({...editingTimeEntry, date: date || new Date()})}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div>
                <Label>Description</Label>
                <Textarea
                  value={editingTimeEntry.description}
                  onChange={(e) => setEditingTimeEntry({...editingTimeEntry, description: e.target.value})}
                  placeholder="Describe the work performed..."
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Hours</Label>
                  <Input
                    type="number"
                    min="0"
                    max="23"
                    value={editingTimeEntry.hours}
                    onChange={(e) => setEditingTimeEntry({...editingTimeEntry, hours: parseInt(e.target.value) || 0})}
                  />
                </div>
                <div>
                  <Label>Minutes</Label>
                  <Input
                    type="number"
                    min="0"
                    max="59"
                    value={editingTimeEntry.minutes}
                    onChange={(e) => setEditingTimeEntry({...editingTimeEntry, minutes: parseInt(e.target.value) || 0})}
                  />
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="chargeable-edit"
                  checked={editingTimeEntry.is_chargeable}
                  onCheckedChange={(checked) => setEditingTimeEntry({...editingTimeEntry, is_chargeable: checked})}
                />
                <Label htmlFor="chargeable-edit">Chargeable Time</Label>
              </div>

              <div className="flex space-x-2">
                <Button type="submit" disabled={loading}>
                  {loading ? 'Updating...' : 'Update Time Entry'}
                </Button>
                <Button type="button" variant="outline" onClick={() => {
                  setIsEditModalOpen(false);
                  setEditingTimeEntry(null);
                }}>
                  Cancel
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Change Credentials Modal */}
      <Dialog open={isCredentialsModalOpen} onOpenChange={setIsCredentialsModalOpen}>
        <DialogContent className="max-w-lg bg-white">
          <DialogHeader>
            <DialogTitle>Change Credentials</DialogTitle>
            <DialogDescription>Update your email and/or password</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleChangeCredentials} className="space-y-4">
            <div>
              <Label>Current Password</Label>
              <Input
                type="password"
                value={credentialsForm.current_password}
                onChange={(e) => setCredentialsForm({...credentialsForm, current_password: e.target.value})}
                required
              />
            </div>

            <div>
              <Label>New Email (leave empty to keep current)</Label>
              <Input
                type="email"
                value={credentialsForm.new_email}
                onChange={(e) => setCredentialsForm({...credentialsForm, new_email: e.target.value})}
                placeholder="Enter new email or leave empty"
              />
            </div>

            <div>
              <Label>New Password (leave empty to keep current)</Label>
              <Input
                type="password"
                value={credentialsForm.new_password}
                onChange={(e) => setCredentialsForm({...credentialsForm, new_password: e.target.value})}
                placeholder="Enter new password or leave empty"
              />
            </div>

            <div>
              <Label>Confirm New Password</Label>
              <Input
                type="password"
                value={credentialsForm.confirm_password}
                onChange={(e) => setCredentialsForm({...credentialsForm, confirm_password: e.target.value})}
                placeholder="Confirm new password"
                disabled={!credentialsForm.new_password}
              />
            </div>

            <div className="flex space-x-2">
              <Button type="submit" disabled={loading}>
                {loading ? 'Updating...' : 'Update Credentials'}
              </Button>
              <Button type="button" variant="outline" onClick={() => {
                setIsCredentialsModalOpen(false);
                setCredentialsForm({
                  current_password: '',
                  new_email: '',
                  new_password: '',
                  confirm_password: ''
                });
              }}>
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Modals */}
      <Dialog open={isViewModalOpen} onOpenChange={setIsViewModalOpen}>
        <DialogContent className="max-w-2xl bg-white">
          <DialogHeader>
            <DialogTitle>
              {viewingClient && 'Client Details'}
              {viewingUser && 'User Details'}
              {viewingPosition && 'Position Details'}
            </DialogTitle>
            <DialogDescription>View detailed information</DialogDescription>
          </DialogHeader>
          
          {viewingClient && (
            <div className="space-y-4">
              <div>
                <Label className="font-semibold">Business Name</Label>
                <p className="text-slate-700">{viewingClient.business_name}</p>
              </div>
              <div>
                <Label className="font-semibold">VAT Number</Label>
                <p className="text-slate-700">{viewingClient.vat_number}</p>
              </div>
              <div>
                <Label className="font-semibold">Address</Label>
                <p className="text-slate-700">
                  {viewingClient.address.street} {viewingClient.address.building}
                  {viewingClient.address.apartment && `, Apt ${viewingClient.address.apartment}`}
                  <br />
                  {viewingClient.address.postal_code} {viewingClient.address.city}
                  <br />
                  {viewingClient.address.country}
                </p>
              </div>
              <div>
                <Label className="font-semibold">Cooperation Type</Label>
                <p className="text-slate-700 capitalize">{viewingClient.cooperation_type}</p>
              </div>
              <div>
                <Label className="font-semibold">Settlement Method</Label>
                <p className="text-slate-700 capitalize">{viewingClient.settlement_method.replace('_', ' ')}</p>
              </div>
              <div>
                <Label className="font-semibold">Status</Label>
                <Badge variant={viewingClient.is_active ? "default" : "secondary"}>
                  {viewingClient.is_active ? 'Active' : 'Suspended'}
                </Badge>
              </div>
            </div>
          )}

          {viewingUser && (
            <div className="space-y-4">
              <div>
                <Label className="font-semibold">Name</Label>
                <p className="text-slate-700">{viewingUser.name} {viewingUser.surname}</p>
              </div>
              <div>
                <Label className="font-semibold">Email</Label>
                <p className="text-slate-700">{viewingUser.email}</p>
              </div>
              <div>
                <Label className="font-semibold">Role</Label>
                <p className="text-slate-700">{getRoleDisplayName(viewingUser.role)}</p>
              </div>
              {viewingUser.position_id && (
                <div>
                  <Label className="font-semibold">Position</Label>
                  <p className="text-slate-700">{positions.find(p => p.id === viewingUser.position_id)?.name || 'Unknown'}</p>
                </div>
              )}
              <div>
                <Label className="font-semibold">Status</Label>
                <Badge variant={viewingUser.is_active ? "default" : "secondary"}>
                  {viewingUser.is_active ? 'Active' : 'Suspended'}
                </Badge>
              </div>
              <div>
                <Label className="font-semibold">Created</Label>
                <p className="text-slate-700">{format(new Date(viewingUser.created_at), 'PPP')}</p>
              </div>
            </div>
          )}

          {viewingPosition && (
            <div className="space-y-4">
              <div>
                <Label className="font-semibold">Position Name</Label>
                <p className="text-slate-700">{viewingPosition.name}</p>
              </div>
              {viewingPosition.description && (
                <div>
                  <Label className="font-semibold">Description</Label>
                  <p className="text-slate-700">{viewingPosition.description}</p>
                </div>
              )}
              <div>
                <Label className="font-semibold">Status</Label>
                <Badge variant={viewingPosition.is_active ? "default" : "secondary"}>
                  {viewingPosition.is_active ? 'Active' : 'Suspended'}
                </Badge>
              </div>
              <div>
                <Label className="font-semibold">Created</Label>
                <p className="text-slate-700">{format(new Date(viewingPosition.created_at), 'PPP')}</p>
              </div>
            </div>
          )}

          <div className="flex justify-end">
            <Button variant="outline" onClick={() => setIsViewModalOpen(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle>Confirm Delete</DialogTitle>
            <DialogDescription>
              What would you like to do with this {deletingItem?.type}?
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              <strong>Suspend:</strong> Hide from lists but keep all data and relationships intact. Can be restored later.
            </p>
            <p className="text-sm text-slate-600">
              <strong>Permanently Delete:</strong> Remove completely from database. This action cannot be undone and may fail if there are related records.
            </p>
          </div>

          <div className="flex space-x-2">
            <Button 
              variant="outline" 
              onClick={() => handleConfirmDelete(false)}
              disabled={loading}
            >
              Suspend
            </Button>
            <Button 
              variant="destructive" 
              onClick={() => handleConfirmDelete(true)}
              disabled={loading}
            >
              {loading ? 'Deleting...' : 'Permanently Delete'}
            </Button>
            <Button 
              variant="ghost" 
              onClick={() => {
                setIsDeleteModalOpen(false);
                setDeletingItem(null);
              }}
            >
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Project Modal */}
      <Dialog open={showProjectForm} onOpenChange={setShowProjectForm}>
        <DialogContent className="max-w-lg bg-white">
          <DialogHeader>
            <DialogTitle>Add New Project</DialogTitle>
            <DialogDescription>
              Create a project for {selectedClientForProjects?.business_name}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleProjectSubmit} className="space-y-4">
            <div>
              <Label>Project Name</Label>
              <Input
                value={projectForm.name}
                onChange={(e) => setProjectForm({...projectForm, name: e.target.value})}
                required
              />
            </div>

            <div>
              <Label>Settlement Method</Label>
              <Select
                value={projectForm.settlement_method}
                onValueChange={(value) => setProjectForm({...projectForm, settlement_method: value})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="fixed_rate">Fixed Rate</SelectItem>
                  <SelectItem value="hourly_rate">Hourly Rate</SelectItem>
                  <SelectItem value="hourly_with_cap">Hourly with Cap</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {projectForm.settlement_method === 'hourly_rate' && (
              <div>
                <Label>Hourly Rate</Label>
                <div className="flex space-x-2">
                  <Input
                    type="number"
                    step="0.01"
                    value={projectForm.hourly_rate}
                    onChange={(e) => setProjectForm({...projectForm, hourly_rate: e.target.value})}
                    className="flex-1"
                  />
                  <Select
                    value={projectForm.hourly_rate_currency}
                    onValueChange={(value) => setProjectForm({...projectForm, hourly_rate_currency: value})}
                  >
                    <SelectTrigger className="w-24">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="USD">USD</SelectItem>
                      <SelectItem value="EUR">EUR</SelectItem>
                      <SelectItem value="PLN">PLN</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {projectForm.settlement_method === 'fixed_rate' && (
              <div>
                <Label>Fixed Rate</Label>
                <div className="flex space-x-2">
                  <Input
                    type="number"
                    step="0.01"
                    value={projectForm.fixed_rate}
                    onChange={(e) => setProjectForm({...projectForm, fixed_rate: e.target.value})}
                    className="flex-1"
                  />
                  <Select
                    value={projectForm.fixed_rate_currency}
                    onValueChange={(value) => setProjectForm({...projectForm, fixed_rate_currency: value})}
                  >
                    <SelectTrigger className="w-24">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="USD">USD</SelectItem>
                      <SelectItem value="EUR">EUR</SelectItem>
                      <SelectItem value="PLN">PLN</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {projectForm.settlement_method === 'hourly_with_cap' && (
              <>
                <div>
                  <Label>Hourly Rate</Label>
                  <div className="flex space-x-2">
                    <Input
                      type="number"
                      step="0.01"
                      value={projectForm.hourly_rate}
                      onChange={(e) => setProjectForm({...projectForm, hourly_rate: e.target.value})}
                      className="flex-1"
                    />
                    <Select
                      value={projectForm.hourly_rate_currency}
                      onValueChange={(value) => setProjectForm({...projectForm, hourly_rate_currency: value})}
                    >
                      <SelectTrigger className="w-24">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="USD">USD</SelectItem>
                        <SelectItem value="EUR">EUR</SelectItem>
                        <SelectItem value="PLN">PLN</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Cap Amount</Label>
                  <div className="flex space-x-2">
                    <Input
                      type="number"
                      step="0.01"
                      value={projectForm.cap_amount}
                      onChange={(e) => setProjectForm({...projectForm, cap_amount: e.target.value})}
                      className="flex-1"
                    />
                    <Select
                      value={projectForm.cap_currency}
                      onValueChange={(value) => setProjectForm({...projectForm, cap_currency: value})}
                    >
                      <SelectTrigger className="w-24">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="USD">USD</SelectItem>
                        <SelectItem value="EUR">EUR</SelectItem>
                        <SelectItem value="PLN">PLN</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Cap Period</Label>
                  <Select
                    value={projectForm.cap_period}
                    onValueChange={(value) => setProjectForm({...projectForm, cap_period: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select period" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="quarterly">Quarterly</SelectItem>
                      <SelectItem value="yearly">Yearly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            <div className="flex space-x-2">
              <Button type="submit" disabled={loading}>
                {loading ? 'Creating...' : 'Create Project'}
              </Button>
              <Button type="button" variant="outline" onClick={() => setShowProjectForm(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Project Modal */}
      <Dialog open={isEditModalOpen && editingProject} onOpenChange={() => {
        setIsEditModalOpen(false);
        setEditingProject(null);
      }}>
        <DialogContent className="max-w-lg bg-white">
          <DialogHeader>
            <DialogTitle>Edit Project</DialogTitle>
            <DialogDescription>Update project information</DialogDescription>
          </DialogHeader>
          {editingProject && (
            <form onSubmit={handleUpdateProject} className="space-y-4">
              <div>
                <Label>Project Name</Label>
                <Input
                  value={editingProject.name}
                  onChange={(e) => setEditingProject({...editingProject, name: e.target.value})}
                  required
                />
              </div>

              <div>
                <Label>Settlement Method</Label>
                <Select
                  value={editingProject.settlement_method}
                  onValueChange={(value) => setEditingProject({...editingProject, settlement_method: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fixed_rate">Fixed Rate</SelectItem>
                    <SelectItem value="hourly_rate">Hourly Rate</SelectItem>
                    <SelectItem value="hourly_with_cap">Hourly with Cap</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {editingProject.settlement_method === 'hourly_rate' && (
                <div>
                  <Label>Hourly Rate</Label>
                  <div className="flex space-x-2">
                    <Input
                      type="number"
                      step="0.01"
                      value={editingProject.hourly_rate}
                      onChange={(e) => setEditingProject({...editingProject, hourly_rate: e.target.value})}
                      className="flex-1"
                    />
                    <Select
                      value={editingProject.hourly_rate_currency}
                      onValueChange={(value) => setEditingProject({...editingProject, hourly_rate_currency: value})}
                    >
                      <SelectTrigger className="w-24">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="USD">USD</SelectItem>
                        <SelectItem value="EUR">EUR</SelectItem>
                        <SelectItem value="PLN">PLN</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {editingProject.settlement_method === 'fixed_rate' && (
                <div>
                  <Label>Fixed Rate</Label>
                  <div className="flex space-x-2">
                    <Input
                      type="number"
                      step="0.01"
                      value={editingProject.fixed_rate}
                      onChange={(e) => setEditingProject({...editingProject, fixed_rate: e.target.value})}
                      className="flex-1"
                    />
                    <Select
                      value={editingProject.fixed_rate_currency}
                      onValueChange={(value) => setEditingProject({...editingProject, fixed_rate_currency: value})}
                    >
                      <SelectTrigger className="w-24">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="USD">USD</SelectItem>
                        <SelectItem value="EUR">EUR</SelectItem>
                        <SelectItem value="PLN">PLN</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {editingProject.settlement_method === 'hourly_with_cap' && (
                <>
                  <div>
                    <Label>Hourly Rate</Label>
                    <div className="flex space-x-2">
                      <Input
                        type="number"
                        step="0.01"
                        value={editingProject.hourly_rate}
                        onChange={(e) => setEditingProject({...editingProject, hourly_rate: e.target.value})}
                        className="flex-1"
                      />
                      <Select
                        value={editingProject.hourly_rate_currency}
                        onValueChange={(value) => setEditingProject({...editingProject, hourly_rate_currency: value})}
                      >
                        <SelectTrigger className="w-24">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="USD">USD</SelectItem>
                          <SelectItem value="EUR">EUR</SelectItem>
                          <SelectItem value="PLN">PLN</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label>Cap Amount</Label>
                    <div className="flex space-x-2">
                      <Input
                        type="number"
                        step="0.01"
                        value={editingProject.cap_amount}
                        onChange={(e) => setEditingProject({...editingProject, cap_amount: e.target.value})}
                        className="flex-1"
                      />
                      <Select
                        value={editingProject.cap_currency}
                        onValueChange={(value) => setEditingProject({...editingProject, cap_currency: value})}
                      >
                        <SelectTrigger className="w-24">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="USD">USD</SelectItem>
                          <SelectItem value="EUR">EUR</SelectItem>
                          <SelectItem value="PLN">PLN</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label>Cap Period</Label>
                    <Select
                      value={editingProject.cap_period}
                      onValueChange={(value) => setEditingProject({...editingProject, cap_period: value})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select period" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="quarterly">Quarterly</SelectItem>
                        <SelectItem value="yearly">Yearly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}

              <div className="flex space-x-2">
                <Button type="submit" disabled={loading}>
                  {loading ? 'Updating...' : 'Update Project'}
                </Button>
                <Button type="button" variant="outline" onClick={() => {
                  setIsEditModalOpen(false);
                  setEditingProject(null);
                }}>
                  Cancel
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* View Project Modal */}
      <Dialog open={isViewModalOpen && viewingProject} onOpenChange={() => {
        setIsViewModalOpen(false);
        setViewingProject(null);
      }}>
        <DialogContent className="max-w-lg bg-white">
          <DialogHeader>
            <DialogTitle>Project Details</DialogTitle>
            <DialogDescription>View project information</DialogDescription>
          </DialogHeader>
          
          {viewingProject && (
            <div className="space-y-4">
              <div>
                <Label className="font-semibold">Project Name</Label>
                <p className="text-slate-700">{viewingProject.name}</p>
              </div>
              
              <div>
                <Label className="font-semibold">Settlement Method</Label>
                <p className="text-slate-700">{viewingProject.settlement_method}</p>
              </div>

              {viewingProject.hourly_rate && (
                <div>
                  <Label className="font-semibold">Hourly Rate</Label>
                  <p className="text-slate-700">{viewingProject.hourly_rate_currency} {viewingProject.hourly_rate}/hr</p>
                </div>
              )}

              {viewingProject.fixed_rate && (
                <div>
                  <Label className="font-semibold">Fixed Rate</Label>
                  <p className="text-slate-700">{viewingProject.fixed_rate_currency} {viewingProject.fixed_rate}</p>
                </div>
              )}

              {viewingProject.cap_amount && (
                <div>
                  <Label className="font-semibold">Cap Amount</Label>
                  <p className="text-slate-700">{viewingProject.cap_currency} {viewingProject.cap_amount} ({viewingProject.cap_period})</p>
                </div>
              )}
              
              <div>
                <Label className="font-semibold">Status</Label>
                <Badge variant={viewingProject.is_active ? "default" : "secondary"}>
                  {viewingProject.is_active ? 'Active' : 'Inactive'}
                </Badge>
              </div>
              
              <div>
                <Label className="font-semibold">Created</Label>
                <p className="text-slate-700">{format(new Date(viewingProject.created_at), 'PPP')}</p>
              </div>
            </div>
          )}

          <div className="flex justify-end">
            <Button variant="outline" onClick={() => {
              setIsViewModalOpen(false);
              setViewingProject(null);
            }}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Report Generation Modal */}
      <Dialog open={isReportModalOpen} onOpenChange={setIsReportModalOpen}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle>Generuj Raport / Generate Report</DialogTitle>
            <DialogDescription>
              {reportSource.startsWith('invoice-') 
                ? `Eksportuj dane z faktury ${reportSource.replace('invoice-', '')} jako Excel lub PDF`
                : `Eksportuj dane z ${reportSource === 'dashboard' ? 'panelu głównego' : 'karty czasu'} jako Excel lub PDF`
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="text-sm text-slate-600">
              <p><strong>Kancelaria / Law Firm:</strong> {getLawFirmInfo().business_name}</p>
              <p><strong>Źródło danych / Data Source:</strong> 
                {reportSource.startsWith('invoice-') 
                  ? `Faktura / Invoice: ${reportSource.replace('invoice-', '')}`
                  : reportSource === 'dashboard' 
                    ? 'Panel główny / Dashboard' 
                    : 'Karta czasu / Timesheet'
                }
              </p>
              <p><strong>Wpisy / Entries:</strong> 
                {reportSource.startsWith('invoice-') && selectedInvoiceDetails 
                  ? `${selectedInvoiceDetails.entries.length} wpisów czasowych`
                  : `${timeEntries.length} wpisów czasowych`
                }
              </p>
              {filters.client_id !== 'all' && !reportSource.startsWith('invoice-') && (
                <p><strong>Klient / Client:</strong> {clients.find(c => c.id === filters.client_id)?.business_name || 'Nieznany/Unknown'}</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Button 
                onClick={generateExcelReport}
                className="bg-green-600 hover:bg-green-700"
              >
                <FileText className="h-4 w-4 mr-2" />
                Eksport do Excel
              </Button>
              <Button 
                onClick={generatePDFReport}
                className="bg-red-600 hover:bg-red-700"
              >
                <FileText className="h-4 w-4 mr-2" />
                Eksport do PDF
              </Button>
            </div>

            <div className="text-xs text-slate-500 border-t pt-3">
              <p><strong>Funkcje raportu / Report Features:</strong></p>
              <ul className="list-disc list-inside space-y-1">
                <li>Sortowanie według daty / Sorted by date</li>
                <li>Osobne tabele dla projektów / Separate project tables</li>
                <li>Stałe opłaty za projekty z limitami / Project fixed fees with caps</li>
                <li>Dane kancelarii i klienta / Law firm and client information</li>
                <li>Podsumowanie razem / Summary totals</li>
                <li>Stawki godzinowe / Hourly rates displayed</li>
                <li>Wyrównane waluty / Aligned currencies</li>
              </ul>
            </div>
          </div>

          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={() => setIsReportModalOpen(false)}>
              Anuluj / Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Invoice Modal */}
      <Dialog open={isInvoiceModalOpen} onOpenChange={setIsInvoiceModalOpen}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>Mark as Invoiced</DialogTitle>
            <DialogDescription>
              Enter invoice number for selected entries ({selectedEntries.size} entries)
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="invoice-number">Invoice Number</Label>
              <Input
                id="invoice-number"
                value={invoiceNumber}
                onChange={(e) => setInvoiceNumber(e.target.value)}
                placeholder="Enter invoice number (e.g., INV-001, 1/ETR/2025, ABC.123)..."
                required
              />
              <p className="text-xs text-muted-foreground mt-1">
                Supports letters, numbers, slashes, dashes, dots, and other symbols
              </p>
            </div>
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setIsInvoiceModalOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleInvoiceSubmit} className="bg-green-600 hover:bg-green-700">
                Mark as Invoiced
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Invoice Details Modal */}
      <Dialog open={isInvoiceDetailsModalOpen} onOpenChange={setIsInvoiceDetailsModalOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto bg-white">
          <DialogHeader>
            <DialogTitle>Invoice Details - {selectedInvoiceDetails?.invoice_number}</DialogTitle>
            <DialogDescription>
              Created on {selectedInvoiceDetails?.created_at ? format(new Date(selectedInvoiceDetails.created_at), 'MMM dd, yyyy HH:mm') : 'N/A'}
            </DialogDescription>
          </DialogHeader>
          {selectedInvoiceDetails && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-sm font-medium">Time Entries:</p>
                  <p className="text-lg">{selectedInvoiceDetails.total_entries || 0}</p>
                </div>
                <div>
                  <p className="text-sm font-medium">Total Hours:</p>
                  <p className="text-lg">{selectedInvoiceDetails.total_hours || 0}h</p>
                </div>
                <div>
                  <p className="text-sm font-medium">External Costs:</p>
                  <p className="text-lg">{selectedInvoiceDetails.total_costs || 0}</p>
                </div>
                <div>
                  <p className="text-sm font-medium">Total Amount:</p>
                  <p className="text-lg">{selectedInvoiceDetails.total_amount ? selectedInvoiceDetails.total_amount.toFixed(2) : '0.00'}</p>
                </div>
              </div>
              
              {/* Time Entries Section */}
              {selectedInvoiceDetails.entries && selectedInvoiceDetails.entries.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2 flex items-center">
                    <Clock className="h-4 w-4 mr-2" />
                    Time Entries ({selectedInvoiceDetails.entries.length})
                  </h4>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Client</TableHead>
                        <TableHead>Project</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>Time</TableHead>
                        <TableHead>Chargeable</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedInvoiceDetails.entries.map((entry) => {
                        const client = clients.find(c => c.id === entry.client_id);
                        const project = entry.project_id ? projects.find(p => p.id === entry.project_id) : null;
                        
                        return (
                          <TableRow key={entry.id}>
                            <TableCell>{entry.date ? format(new Date(entry.date), 'MMM dd, yyyy') : 'N/A'}</TableCell>
                            <TableCell>{client?.business_name || 'Unknown Client'}</TableCell>
                            <TableCell>{project?.name || 'No Project'}</TableCell>
                            <TableCell className="max-w-xs truncate">{entry.description || 'N/A'}</TableCell>
                            <TableCell>{formatTime(entry.hours || 0, entry.minutes || 0)}</TableCell>
                            <TableCell>
                              <Badge variant={entry.is_chargeable ? "default" : "outline"}>
                                {entry.is_chargeable ? 'Yes' : 'No'}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}

              {/* External Costs Section */}
              {selectedInvoiceDetails.external_costs && selectedInvoiceDetails.external_costs.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2 flex items-center">
                    <Receipt className="h-4 w-4 mr-2" />
                    External Costs ({selectedInvoiceDetails.external_costs.length})
                  </h4>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Client</TableHead>
                        <TableHead>Project</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead>Currency</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedInvoiceDetails.external_costs.map((cost) => {
                        const client = clients.find(c => c.id === cost.client_id);
                        const project = cost.project_id ? projects.find(p => p.id === cost.project_id) : null;
                        
                        return (
                          <TableRow key={cost.id} className="bg-orange-50">
                            <TableCell>{cost.created_at ? format(new Date(cost.created_at), 'MMM dd, yyyy') : 'N/A'}</TableCell>
                            <TableCell>{client?.business_name || 'Unknown Client'}</TableCell>
                            <TableCell>{project?.name || 'No Project'}</TableCell>
                            <TableCell className="max-w-xs truncate">{cost.description || 'N/A'}</TableCell>
                            <TableCell className="font-medium">{cost.amount ? cost.amount.toFixed(2) : '0.00'}</TableCell>
                            <TableCell>
                              <Badge variant="secondary">{cost.currency || 'USD'}</Badge>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
              
              <div className="flex justify-end">
                <Button onClick={() => setIsInvoiceDetailsModalOpen(false)}>
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* External Cost Invoice Modal */}
      <Dialog open={isExternalCostModalOpen} onOpenChange={setIsExternalCostModalOpen}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>Mark External Costs as Invoiced</DialogTitle>
            <DialogDescription>
              Enter invoice number for selected external costs ({selectedExternalCosts.size} costs)
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="external-cost-invoice-number">Invoice Number</Label>
              <Input
                id="external-cost-invoice-number"
                value={invoiceNumber}
                onChange={(e) => setInvoiceNumber(e.target.value)}
                placeholder="Enter invoice number (e.g., INV-001, 1/ETR/2025, ABC.123)..."
                required
              />
              <p className="text-xs text-muted-foreground mt-1">
                Supports letters, numbers, slashes, dashes, dots, and other symbols
              </p>
            </div>
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setIsExternalCostModalOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={() => {
                  handleMarkExternalCostsInvoiced(invoiceNumber);
                  setIsExternalCostModalOpen(false);
                  setInvoiceNumber('');
                }}
                className="bg-green-600 hover:bg-green-700"
              >
                Mark as Invoiced
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Task Creation/Editing Modal */}
      <Dialog open={isTaskModalOpen} onOpenChange={setIsTaskModalOpen}>
        <DialogContent className="max-w-2xl bg-white">
          <DialogHeader>
            <DialogTitle>{editingTask ? 'Edit Task' : 'Create New Task'}</DialogTitle>
            <DialogDescription>
              {editingTask ? 'Update the task details below' : 'Fill in the task details below'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleTaskSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Client Selection</Label>
                <Select
                  value={taskForm.client_id}
                  onValueChange={(value) => {
                    if (value === "no-client") {
                      setTaskForm({
                        ...taskForm,
                        client_id: 'no-client',
                        client_name: '',
                        project_id: 'no-project',
                        project_name: ''
                      });
                    } else {
                      const selectedClient = clients.find(c => c.id === value);
                      setTaskForm({
                        ...taskForm,
                        client_id: value,
                        client_name: selectedClient ? selectedClient.business_name : taskForm.client_name,
                        project_id: 'no-project',
                        project_name: ''
                      });
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select existing client (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="no-client">No existing client</SelectItem>
                    {clientOptions.length > 0 ? (
                      clientOptions.map((client) => (
                        <SelectItem key={client.id} value={client.id}>
                          {client.name}
                        </SelectItem>
                      ))
                    ) : (
                      <SelectItem value="no-clients-available" disabled>No clients available</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Client Name *</Label>
                <Input
                  value={taskForm.client_name}
                  onChange={(e) => setTaskForm({...taskForm, client_name: e.target.value})}
                  placeholder="Enter client name (manual entry or from selection)"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Project Selection</Label>
                <Select
                  value={taskForm.project_id}
                  onValueChange={(value) => {
                    if (value === "no-project") {
                      setTaskForm({
                        ...taskForm,
                        project_id: 'no-project',
                        project_name: ''
                      });
                    } else {
                      const selectedProject = projects.find(p => p.id === value);
                      setTaskForm({
                        ...taskForm,
                        project_id: value,
                        project_name: selectedProject ? selectedProject.name : taskForm.project_name
                      });
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select existing project (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="no-project">No existing project</SelectItem>
                    {taskForm.client_id && taskForm.client_id !== 'no-client' ? (
                      projects
                        .filter(p => p.client_id === taskForm.client_id && p.is_active !== false)
                        .map((project) => (
                          <SelectItem key={project.id} value={project.id}>
                            {project.name}
                          </SelectItem>
                        ))
                    ) : (
                      projects
                        .filter(p => p.is_active !== false)
                        .map((project) => (
                          <SelectItem key={project.id} value={project.id}>
                            {project.name}
                          </SelectItem>
                        ))
                    )}
                    {projects.length === 0 && (
                      <SelectItem value="no-projects-available" disabled>No projects available</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Project Name</Label>
                <Input
                  value={taskForm.project_name}
                  onChange={(e) => setTaskForm({...taskForm, project_name: e.target.value})}
                  placeholder="Enter project name (manual entry or from selection)"
                />
              </div>
            </div>

            <div>
              <Label>Deadline *</Label>
              <Input
                type="date"
                value={taskForm.deadline}
                onChange={(e) => setTaskForm({...taskForm, deadline: e.target.value})}
                required
              />
            </div>

            <div>
              <Label>Description *</Label>
              <Textarea
                value={taskForm.description}
                onChange={(e) => setTaskForm({...taskForm, description: e.target.value})}
                placeholder="Describe the task details..."
                rows={3}
                required
              />
            </div>

            <div>
              <Label>Comments</Label>
              <Textarea
                value={taskForm.comments}
                onChange={(e) => setTaskForm({...taskForm, comments: e.target.value})}
                placeholder="Additional comments or notes..."
                rows={2}
              />
            </div>

            <div className="flex justify-end space-x-2">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => {
                  setIsTaskModalOpen(false);
                  setEditingTask(null);
                  setTaskForm({
                    client_id: 'no-client',
                    client_name: '',
                    deadline: '',
                    description: '',
                    comments: ''
                  });
                }}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={loading} className="bg-green-600 hover:bg-green-700">
                {editingTask ? 'Update Task' : 'Create Task'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Task Completion Modal */}
      <Dialog open={isCompletionModalOpen} onOpenChange={setIsCompletionModalOpen}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle>Complete Task</DialogTitle>
            <DialogDescription>
              Mark this task as completed and add any completion notes.
            </DialogDescription>
          </DialogHeader>
          {completingTask && (
            <div className="space-y-4">
              <div className="bg-slate-50 p-3 rounded-lg">
                <p className="font-medium">{completingTask.client_name}</p>
                <p className="text-sm text-slate-600">{completingTask.description}</p>
                <p className="text-xs text-slate-500">
                  Deadline: {format(new Date(completingTask.deadline), 'MMM dd, yyyy')}
                </p>
              </div>

              <div>
                <Label>Completion Comments</Label>
                <Textarea
                  value={completionComments}
                  onChange={(e) => setCompletionComments(e.target.value)}
                  placeholder="Add any notes about task completion..."
                  rows={3}
                />
              </div>

              <div className="flex justify-end space-x-2">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setIsCompletionModalOpen(false);
                    setCompletingTask(null);
                    setCompletionComments('');
                  }}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleTaskCompletion}
                  disabled={loading}
                  className="bg-green-600 hover:bg-green-700"
                >
                  Mark as Complete
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Suspended Task Reminders Modal */}
      <Dialog open={showReminders} onOpenChange={setShowReminders}>
        <DialogContent className="max-w-2xl bg-white">
          <DialogHeader>
            <DialogTitle>Suspended Task Reminders</DialogTitle>
            <DialogDescription>
              You have suspended tasks that are due for review.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {suspendedReminders.map((task) => (
              <div key={task.id} className="border rounded-lg p-4 bg-purple-50">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <p className="font-medium text-purple-900">{task.description}</p>
                    <p className="text-sm text-purple-700">Client: {task.client_name}</p>
                    {task.project_name && (
                      <p className="text-sm text-purple-700">Project: {task.project_name}</p>
                    )}
                    <p className="text-sm text-purple-600 mt-1">
                      <strong>Resume by:</strong> {formatDateWithDay(task.suspend_due_date)}
                    </p>
                    <p className="text-sm text-purple-600">
                      <strong>Suspend notes:</strong> {task.suspend_comments}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => {
                      handleResumeTask(task.id);
                      setShowReminders(false);
                    }}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    Resume Now
                  </Button>
                </div>
              </div>
            ))}
            
            <div className="flex justify-end space-x-2">
              <Button 
                variant="outline" 
                onClick={remindLater}
              >
                Remind Later
              </Button>
              <Button 
                onClick={dismissReminders}
                className="bg-purple-600 hover:bg-purple-700"
              >
                OK
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Task Suspend Modal */}
      <Dialog open={isSuspendModalOpen} onOpenChange={setIsSuspendModalOpen}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle>Suspend Task</DialogTitle>
            <DialogDescription>
              Suspend this task and add information about what needs to be done to resume.
            </DialogDescription>
          </DialogHeader>
          {suspendingTask && (
            <div className="space-y-4">
              <div>
                <p className="font-medium">{suspendingTask.description}</p>
                <p className="text-sm text-slate-600">for {suspendingTask.client_name}</p>
              </div>

              <div>
                <Label>Suspend Notes *</Label>
                <Textarea
                  value={suspendComments}
                  onChange={(e) => setSuspendComments(e.target.value)}
                  placeholder="Explain what needs to be done to resume this task..."
                  rows={3}
                  required
                />
              </div>

              <div>
                <Label>Resume Due Date (Optional)</Label>
                <Input
                  type="date"
                  value={suspendDueDate}
                  onChange={(e) => setSuspendDueDate(e.target.value)}
                  placeholder="When should this task be resumed?"
                />
              </div>

              <div className="flex justify-end space-x-2">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setIsSuspendModalOpen(false);
                    setSuspendingTask(null);
                    setSuspendComments('');
                    setSuspendDueDate('');
                  }}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleSuspendSubmit}
                  disabled={loading || !suspendComments.trim()}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  Suspend Task
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default App;